import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import * as fabric from 'fabric';
import { CanvasArea } from '../components/CanvasArea';
import { EditorToolbar } from '../components/EditorToolbar';
import { EditorTabBar } from '../components/EditorTabBar';
import { ToolPalette } from '../components/ToolPalette';
import { PropertiesPanel } from '../components/PropertiesPanel';
import { RETOUCH_VARIANT_ORDER } from '../components/RetouchPanel';
import { AIToolPickerDialog } from '../components/AIToolPickerDialog';
import { StatusBar } from '../components/StatusBar';
import { ColorRangeDialog } from '../components/ColorRangeDialog';
import { RefineEdgeWorkspace } from '../components/RefineEdgeWorkspace';
import { LiquifyWorkspace } from '../components/LiquifyWorkspace';
import { ExportDialog } from '../components/ExportDialog';
import {
  AdjustmentsModal,
  adjustmentDisplayName,
  type AdjustmentKind,
} from '../components/adjustments';
import type { AdjustmentConfig } from '../types';
import { ConfirmCloseDialog, type SaveDestination } from '../components/ConfirmCloseDialog';
import { RasterizeBeforeWarpDialog } from '../components/RasterizeBeforeWarpDialog';
import type { AIStatus, SmartCreateInsertModel } from '../components/PropertiesPanel';
import { IconLegendDialog } from '../components/IconLegendDialog';
import { runSmartDelete } from '../lib/smart-delete';
import { pathToSvgData } from '../lib/vector-path';
import api from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { FiasImagePickerDialog } from '../components/FiasImagePickerDialog';
import { LayersPanel } from '../components/LayersPanel';
import { useImageEditorState } from '../hooks/useImageEditorState';
import { useFabricCanvas } from '../hooks/useFabricCanvas';
import { useCanvasHistory, SERIALIZED_PROPS } from '../hooks/useCanvasHistory';
import { useImageExport } from '../hooks/useImageExport';
import { useIsLandscape } from '../hooks/useIsLandscape';
import { useDocumentTabs } from '../hooks/useDocumentTabs';
import {
  loadSession,
  saveSession,
  clearSession,
  type PersistedSession,
} from '../lib/session-store';
import {
  SMART_CREATE_MAX_INPUT_EDGE_PX,
  STABILITY_MIN_INPUT_EDGE_PX,
  RECRAFT_MIN_INPUT_EDGE_PX,
  OPENAI_MIN_INPUT_EDGE_PX,
  FEATHER_RADIUS_RATIO_DEFAULT,
  computeFeatherRadius,
  featherMask,
  fitCanvasToMaxDim,
  flattenForExport,
  loadImageToCanvas,
  toGrayscaleMask,
} from '../lib/inpaint-helpers';
import { downloadSvgLocal, uploadSvgToFias } from '../lib/save-svg';
import {
  hasUnimplementedSlowPath,
  renderDocument,
  requiresCustomComposite,
} from '../lib/render-pipeline';
import { hasRasterizableTarget } from '../lib/layer-tree';
import {
  generatePolygonPath,
  generateStarPath,
  generateArrowPath,
  shouldRegenerateVectorPrimitive,
} from '../lib/shape-generators';
import { pathBoolean, type BooleanOp } from '../lib/path-ops';
import {
  VectorizeSaveDialog,
  type VectorizeSaveDestination,
} from '../components/VectorizeSaveDialog';
import type {
  EditorTool,
  ExportSettings,
  ImageToImageEngine,
  EditorOutputMode,
  InpaintEngine,
  RemoveBackgroundEngine,
  ReplaceBackgroundEngine,
  ReplaceBackgroundLightDirection,
  ReplaceBackgroundRecraftStyle,
  UpscaleMode,
  UpscaleEngine,
  UpscaleFactor,
  ControlMode,
  Layer,
  EditorDocument,
  LayerType,
  BlendMode,
  LayerEffects,
  LayerMask,
  ShapeSettings,
  VectorPath,
} from '../types';

/** Whether the Async Clipboard API can read images out of the OS
 *  clipboard. Available in secure contexts on modern browsers. Used
 *  to enable the toolbar Paste button even when the Fabric internal
 *  clipboard is empty — so a user with a screenshot in their OS
 *  clipboard can paste via the button (mobile especially, where
 *  Ctrl+V isn't an option). */
const SUPPORTS_OS_CLIPBOARD_READ =
  typeof navigator !== 'undefined' && typeof navigator.clipboard?.read === 'function';

/** Default at-creation radius / half-extent for vector primitives.
 *  Used when the shape tool drops a fresh polygon / star / arrow
 *  onto the canvas; the slider-driven regenerate flow overrides
 *  this with the layer's current scene-space half-extent so a
 *  scaled primitive doesn't snap back to default size when its
 *  parameters change. */
const DEFAULT_VECTOR_PRIMITIVE_RADIUS = 60;

/** Build a `VectorPath` for one of the Tier 2 Group A vector
 *  primitives (polygon / star / arrow) centered at `center`. Used
 *  by both the shape-tool creation flow and the slider-driven
 *  regenerate flow — keeps the per-primitive defaults + stroke/
 *  fill wiring in one place so the two paths can't drift.
 *
 *  `halfExtent` is the radius (polygon / star) or half-length
 *  (arrow) the generator targets. At creation time the caller
 *  omits it and the default is used; on slider-driven regenerate
 *  the caller passes the layer's current scene-space half-extent
 *  so the visual size stays put across slider ticks (e.g. a
 *  polygon scaled to 200×200 stays 200×200 when Sides changes).
 *
 *  Caveat: the regenerate path normalizes back to uniform
 *  proportions — a non-uniform user scale (squished polygon)
 *  becomes circular again on regen. Intentional v1 trade-off
 *  documented in the audit (S5). */
function generateVectorShapePath(
  settings: ShapeSettings,
  center: { x: number; y: number },
  halfExtent: number = DEFAULT_VECTOR_PRIMITIVE_RADIUS,
): VectorPath {
  let path: VectorPath;
  if (settings.type === 'polygon') {
    path = generatePolygonPath(center, {
      sides: settings.polygonSides ?? 6,
      radius: halfExtent,
      cornerRadius: settings.cornerRadius,
    });
  } else if (settings.type === 'star') {
    path = generateStarPath(center, {
      points: settings.starPoints ?? 5,
      outerRadius: halfExtent,
      innerRadiusRatio: settings.starInnerRadiusRatio ?? 0.5,
    });
  } else {
    // 'arrow' — direction along +x by default; the user can
    // rotate via the Move tool's bounding-box rotation handle.
    path = generateArrowPath({
      from: { x: center.x - halfExtent, y: center.y },
      to: { x: center.x + halfExtent, y: center.y },
      headSize: settings.arrowHeadSize ?? 24,
      headShape: settings.arrowHeadShape ?? 'triangle',
      lineWidth: settings.strokeWidth || 4,
    });
  }
  path.stroke = { color: settings.stroke, width: settings.strokeWidth };
  path.fill = settings.fill;
  return path;
}

/** Per-mode running-status label shown in the AI banner during a
 *  Control / Guided call. The four endpoints have visibly different
 *  runtimes, so the label tells the user which operation is taking
 *  the time. */
const CONTROL_RUNNING_LABEL: Record<ControlMode, string> = {
  'from-sketch': 'Generating from sketch',
  'match-structure': 'Matching structure',
  'match-style': 'Matching style',
  'style-transfer': 'Transferring style',
};

/** Soft cap on adjustment-layer count per document. Each adjustment
 *  owns a full-canvas backing canvas (4K ≈ 64MB), so unbounded
 *  stacking exhausts browser memory without warning. The cap
 *  matches a typical pro-photo retouch envelope; users hitting it
 *  should flatten or split. Surfaced via toast at the creation
 *  site, not enforced in the reducer (the reducer trusts its
 *  caller, matching the rest of the layer-mutation API). */
const MAX_ADJUSTMENT_LAYERS = 16;

/** New-layer name when Control runs in 'new-layer' output mode. The
 *  per-mode names make the layers panel readable when a user runs
 *  several variations on the same source. */
const CONTROL_LAYER_LABEL: Record<ControlMode, string> = {
  'from-sketch': 'From Sketch',
  'match-structure': 'Match Structure',
  'match-style': 'Match Style',
  'style-transfer': 'Style Transfer',
};

export function EditorPage() {
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isLandscape = useIsLandscape();
  const [hasImage, setHasImage] = useState(false);
  const layerIdSeqRef = useRef(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [fiasPickerOpen, setFiasPickerOpen] = useState(false);
  const [gridVisible, setGridVisible] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  // Adjustments modal — `activeAdjustmentKind` drives which kind's
  // panel is mounted inside `AdjustmentsModal`; `null` keeps the
  // dialog closed. The modal itself is generic across kinds and
  // looks each one up in the spec registry. `adjustmentSessionId`
  // bumps on every open so the modal can remount its inner body
  // even when the user re-opens the same kind back-to-back —
  // forces `useState` initializers to re-run with fresh state.
  // The body captures the source ImageData itself in a post-mount
  // effect so the (potentially multi-MB) `getImageData` doesn't
  // block the modal from appearing.
  //
  // `adjustmentTargets` holds an entry per layer the adjustment
  // writes into — single-element for a non-group active layer,
  // multi-element when the active layer is a group (one entry per
  // rasterizable descendant). Each entry: the live paint canvas
  // (what the spec writes into) + the wrapping Fabric object (what
  // we mark `dirty` so the next render re-reads the modified
  // pixels). Resolved at open-time via `getActiveAdjustmentTargets`.
  const [activeAdjustmentKind, setActiveAdjustmentKind] = useState<AdjustmentKind | null>(null);
  const [adjustmentSessionId, setAdjustmentSessionId] = useState(0);
  const [adjustmentTargets, setAdjustmentTargets] = useState<
    Array<{ canvas: HTMLCanvasElement; fabricObj: fabric.FabricObject }>
  >([]);
  // Non-destructive edit mode: tracks the adjustment-layer node
  // currently open in the modal (id + its config at open-time, used
  // for Cancel-reverts). Null when the modal is opened in
  // destructive mode (or closed entirely). Set / cleared together
  // with `activeAdjustmentKind` so the two stay aligned.
  const [editingAdjustment, setEditingAdjustment] = useState<{
    layerId: string;
    original: AdjustmentConfig;
  } | null>(null);
  // AI tool state — banner + per-capability prompts. Each capability
  // gets its own prompt field so switching between Smart Create and
  // Inpaint doesn't clobber what the user has typed.
  const [aiStatus, setAiStatus] = useState<AIStatus>({ kind: 'idle' });

  // Tier 1 Group D — Selection refinement modals. Color Range and
  // Refine Edge are both modal-shaped (one a small dialog, the
  // other a full-screen workspace), opened from the StatusBar
  // chip popover and from the MarqueePanel.
  const [colorRangeOpen, setColorRangeOpen] = useState(false);
  const [refineEdgeOpen, setRefineEdgeOpen] = useState(false);
  // Liquify — Tier 1 Group E final piece. Same shape as Refine
  // Edge: the workspace owns its own working state, we just snap
  // the source at open time and route the apply-result back into
  // the active layer.
  const [liquifyOpen, setLiquifyOpen] = useState(false);
  const [liquifySource, setLiquifySource] = useState<HTMLCanvasElement | null>(null);
  // Rasterize-before-warp prompt — Distort / Perspective / Warp
  // need a FabricImage target; clicking one of those chips while a
  // vector / shape / text layer is active opens this dialog. The
  // pending mode is what we'll switch to after the user accepts
  // the rasterize. Null = dialog closed.
  const [pendingWarpMode, setPendingWarpMode] = useState<'distort' | 'perspective' | 'warp' | null>(
    null,
  );
  /** Source canvas snapshot for the Color Range modal and Refine
   *  Edge workspace, taken at open time so the preview is stable
   *  while the user works. Stored as a canvas (NOT ImageData) —
   *  see ColorRangeDialog's comment for the perf rationale (4K
   *  ImageData is ~32MB; passing canvases lets consumers drawImage
   *  cheaply and getImageData lazily only where needed). */
  const [sourceCanvas, setSourceCanvas] = useState<HTMLCanvasElement | null>(null);
  const [smartCreatePrompt, setSmartCreatePrompt] = useState('');
  const [smartCreateInsertModel, setSmartCreateInsertModel] =
    useState<SmartCreateInsertModel>('flux-2-pro');
  // Inpaint (mask + prompt). Used to be Smart Create's "Blend" mode;
  // now its own picker entry per the AI tool restructuring. The feather
  // ratio drives the post-process edge blend — 0 = hard edges, 0.2 =
  // very soft (FEATHER_RADIUS_RATIO_DEFAULT = 5%).
  //
  // `engine` picks the provider — Stability (cheapest, ~4 credits) /
  // Recraft (purpose-built for replacement, ~5 credits) / OpenAI
  // gpt-image-1 (best text rendering, ~5 credits). `negativePrompt`
  // is forwarded to engines that support it (Stability); ignored by
  // others. `growMaskPx` dilates the mask outward server-side.
  const [inpaintPrompt, setInpaintPrompt] = useState('');
  const [inpaintNegativePrompt, setInpaintNegativePrompt] = useState('');
  const [inpaintEngine, setInpaintEngine] = useState<InpaintEngine>('stability');
  const [inpaintGrowMaskPx, setInpaintGrowMaskPx] = useState<number>(0);
  const [inpaintFeatherRatio, setInpaintFeatherRatio] = useState<number>(
    FEATHER_RADIUS_RATIO_DEFAULT,
  );
  // Engine-conditional style preset. Empty string = engine default.
  // Reset on engine change because the valid value sets are disjoint
  // (Stability's 11 presets vs. Recraft's 4 modes); a stale value from
  // the other engine would either get rejected upstream or silently
  // drop. The reset wrapper lives below.
  const [inpaintStylePreset, setInpaintStylePreset] = useState<string>('');
  const handleInpaintEngineChange = useCallback((engine: InpaintEngine) => {
    setInpaintEngine(engine);
    setInpaintStylePreset('');
  }, []);
  // Smart Delete (paid Stability erase, escalation from the free
  // MI-GAN-based Free Delete). `growMask` dilates the mask outward
  // server-side before Stability's erase model runs — useful when the
  // marquee just barely catches the object. `feather` softens the
  // boundary on composite (same post-process as Inpaint).
  const [eraseGrowMaskPx, setEraseGrowMaskPx] = useState<number>(0);
  const [eraseFeatherRatio, setEraseFeatherRatio] = useState<number>(FEATHER_RADIUS_RATIO_DEFAULT);
  // Search & Replace (Stability auto-segments via `searchPrompt` and
  // replaces with content matching `searchReplacePrompt`). No marquee
  // required. `growMask` cleans up edge halos when Stability's
  // segmentation just misses an edge.
  const [searchReplaceSearchPrompt, setSearchReplaceSearchPrompt] = useState('');
  const [searchReplacePrompt, setSearchReplacePrompt] = useState('');
  const [searchReplaceNegativePrompt, setSearchReplaceNegativePrompt] = useState('');
  const [searchReplaceGrowMaskPx, setSearchReplaceGrowMaskPx] = useState<number>(0);
  // Optional Stability style preset (empty = engine default) and seed
  // (null = engine random). Seed is `number | null` so a blank input
  // doesn't get sent as `0` and lock the user into a specific seed.
  const [searchReplaceStylePreset, setSearchReplaceStylePreset] = useState<string>('');
  const [searchReplaceSeed, setSearchReplaceSeed] = useState<number | null>(null);
  // Search & Recolor (Stability auto-segments via `selectPrompt` and
  // recolors with the color/effect in `prompt`). Same pattern as Search
  // & Replace, just preserves shape.
  const [searchRecolorSelectPrompt, setSearchRecolorSelectPrompt] = useState('');
  const [searchRecolorPrompt, setSearchRecolorPrompt] = useState('');
  const [searchRecolorNegativePrompt, setSearchRecolorNegativePrompt] = useState('');
  const [searchRecolorGrowMaskPx, setSearchRecolorGrowMaskPx] = useState<number>(0);
  const [searchRecolorStylePreset, setSearchRecolorStylePreset] = useState<string>('');
  const [searchRecolorSeed, setSearchRecolorSeed] = useState<number | null>(null);
  // Outpaint extends the canvas in any of 4 directions. ≥1 must be > 0.
  // Optional prompt guides the new content; creativity 0..1 controls
  // adherence to the source (default 0.5).
  const [outpaintLeftPx, setOutpaintLeftPx] = useState<number>(0);
  const [outpaintRightPx, setOutpaintRightPx] = useState<number>(0);
  const [outpaintUpPx, setOutpaintUpPx] = useState<number>(0);
  const [outpaintDownPx, setOutpaintDownPx] = useState<number>(0);
  const [outpaintPrompt, setOutpaintPrompt] = useState('');
  const [outpaintCreativity, setOutpaintCreativity] = useState<number>(0.5);
  const [outpaintNegativePrompt, setOutpaintNegativePrompt] = useState('');
  const [outpaintStylePreset, setOutpaintStylePreset] = useState<string>('');
  const [outpaintSeed, setOutpaintSeed] = useState<number | null>(null);
  // Image to Image — strength-based reimagining via Stability's
  // generate/* endpoints in image-to-image mode. `strength` follows
  // Stability's convention (0 = identical, 1 = ignore source); 0.35
  // is a moderate redraw default that keeps the source recognizable.
  const [i2iPrompt, setI2iPrompt] = useState('');
  const [i2iEngine, setI2iEngine] = useState<ImageToImageEngine>('core');
  const [i2iStrength, setI2iStrength] = useState<number>(0.35);
  const [i2iNegativePrompt, setI2iNegativePrompt] = useState('');
  const [i2iStylePreset, setI2iStylePreset] = useState<string>('');
  const [i2iSeed, setI2iSeed] = useState<number | null>(null);
  // Default to non-destructive — the new layer is easy to compare with
  // the original or discard by deleting it. Users can switch to
  // 'replace' for iterative refinement on the same target.
  const [i2iMode, setI2iMode] = useState<EditorOutputMode>('new-layer');
  // Replace Background — Recraft default (cheap, prompt-only); Stability
  // upsell for photoreal compositing with relighting and reference-image
  // BGs. Default mode is 'new-layer' for the same A/B comparison reasons
  // as I2I.
  const [replaceBgEngine, setReplaceBgEngine] = useState<ReplaceBackgroundEngine>('recraft');
  const [replaceBgPrompt, setReplaceBgPrompt] = useState('');
  const [replaceBgReferenceBlob, setReplaceBgReferenceBlob] = useState<Blob | null>(null);
  const [replaceBgLightReferenceBlob, setReplaceBgLightReferenceBlob] = useState<Blob | null>(null);
  const [replaceBgLightDirection, setReplaceBgLightDirection] =
    useState<ReplaceBackgroundLightDirection>('none');
  const [replaceBgLightStrength, setReplaceBgLightStrength] = useState<number>(0.7);
  // Stability relights the subject as part of every call — there's no
  // way to skip it server-side. `preserveSubject` near 1.0 dampens the
  // relight effect, so we default to 0.7 (faithful original) instead of
  // 0.5. Users wanting a more dramatic relight can pull the slider down.
  const [replaceBgPreserveSubject, setReplaceBgPreserveSubject] = useState<number>(0.7);
  const [replaceBgPreserveOriginalBg, setReplaceBgPreserveOriginalBg] = useState<number>(0);
  const [replaceBgStyle, setReplaceBgStyle] = useState<ReplaceBackgroundRecraftStyle | ''>('');
  const [replaceBgSeed, setReplaceBgSeed] = useState<number | null>(null);
  const [replaceBgMode, setReplaceBgMode] = useState<EditorOutputMode>('new-layer');
  // Upscale — three modes. Default to fast/Stability/4× because that's
  // the cheapest and most predictable starting point. Engine state
  // persists across mode switches so a user who picked Recraft once
  // doesn't lose their choice when they bounce through HQ (which forces
  // Stability) and back. Output stays non-destructive by default.
  const [upscaleMode, setUpscaleMode] = useState<UpscaleMode>('fast');
  const [upscaleEngine, setUpscaleEngine] = useState<UpscaleEngine>('stability');
  const [upscalePrompt, setUpscalePrompt] = useState<string>('');
  // Default 0.25 — sits inside both engines' valid ranges:
  //   • Stability conservative (HQ): requires 0.2..0.35
  //   • Stability creative (reimagine): accepts 0..0.35
  // Earlier we shipped 0.15 which was below conservative's 0.2 floor
  // and made every HQ call fail server-side.
  const [upscaleCreativity, setUpscaleCreativity] = useState<number>(0.25);
  const [upscaleFactor, setUpscaleFactor] = useState<UpscaleFactor>(4);
  const [upscaleSeed, setUpscaleSeed] = useState<number | null>(null);
  const [upscaleOutputMode, setUpscaleOutputMode] = useState<EditorOutputMode>('new-layer');
  // Vectorize keeps the most recent SVG result in memory so a
  // cancelled save dialog (or a deliberate "Save again" click) can
  // re-open the file picker without paying for the Recraft call
  // twice. `vectorizeStatus` drives the panel's idle / saved /
  // cancelled view; `vectorizeSavedFileName` is shown back to the
  // user as confirmation after a successful save.
  const [vectorizeStatus, setVectorizeStatus] = useState<'idle' | 'saved' | 'cancelled'>('idle');
  // SVG result kept in a ref so re-renders during the save flow
  // don't drop it. State + ref pair: state drives the panel view;
  // ref holds the raw bytes for the dialog and "Save again".
  const vectorizeSvgRef = useRef<string | null>(null);
  const [vectorizeSavedFileName, setVectorizeSavedFileName] = useState<string | null>(null);
  // The in-editor save-as dialog opens after a successful vectorize
  // and again whenever the user clicks "Save again". Local/Fias
  // destination is sticky across both opens — the user's last pick
  // stays selected.
  const [vectorizeSaveDialogOpen, setVectorizeSaveDialogOpen] = useState(false);
  const [vectorizeSaveDialogDestination, setVectorizeSaveDialogDestination] =
    useState<VectorizeSaveDestination>('local');
  // Control / Guided — four Stability v2beta `/control/*` endpoints
  // surfaced as modes. Default to `match-structure` (most general-
  // purpose: works on any image with a clear subject), `new-layer`
  // output for non-destructive A/B. Slider defaults match Stability's
  // documented sweet spots: 0.7 control strength, 0.5 fidelity for
  // match-style, 0.5/0.9/0.5 for the style-transfer trio.
  const [controlMode, setControlMode] = useState<ControlMode>('match-structure');
  const [controlPrompt, setControlPrompt] = useState<string>('');
  const [controlNegativePrompt, setControlNegativePrompt] = useState<string>('');
  const [controlStrength, setControlStrength] = useState<number>(0.7);
  const [controlFidelity, setControlFidelity] = useState<number>(0.5);
  const [controlStyleReferenceBlob, setControlStyleReferenceBlob] = useState<Blob | null>(null);
  const [controlStyleStrength, setControlStyleStrength] = useState<number>(0.5);
  const [controlCompositionFidelity, setControlCompositionFidelity] = useState<number>(0.9);
  const [controlChangeStrength, setControlChangeStrength] = useState<number>(0.5);
  const [controlSeed, setControlSeed] = useState<number | null>(null);
  const [controlOutputMode, setControlOutputMode] = useState<EditorOutputMode>('new-layer');
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [removeBackgroundError, setRemoveBackgroundError] = useState<string | null>(null);
  // Remove-background engine — defaults to the free on-device imgly path.
  // Stability / Recraft route through the paid backend (see
  // smart-remove-background.ts).
  const [removeBackgroundEngine, setRemoveBackgroundEngine] =
    useState<RemoveBackgroundEngine>('imgly');

  // Close-confirmation flow. When a dirty tab's X is clicked, the editor
  // first switches to that tab (so its content is on the canvas) and shows
  // the dialog. The Save / Save-As paths run through ExportDialog so the
  // close happens AFTER the export resolves.
  const [confirmCloseTabId, setConfirmCloseTabId] = useState<string | null>(null);
  const [isQuickSaving, setIsQuickSaving] = useState(false);
  const [exportDialogDestination, setExportDialogDestination] = useState<SaveDestination>('local');
  /** User-facing error message from the most recent save attempt, surfaced
   *  in the close-confirm dialog so the user can react to FSA permission
   *  revocations, network failures, quota exceeded, etc. instead of seeing
   *  the dialog hang. */
  const [saveError, setSaveError] = useState<string | null>(null);
  // When non-null, the currently-mounted ExportDialog should close this tab
  // after a successful export. Captured at the moment Save-As is clicked so
  // it survives the dialog closing on cancel without leaking into the next
  // unrelated export.
  const closeAfterExportTabIdRef = useRef<string | null>(null);

  const {
    state,
    setActiveTool,
    setSelectedAITool,
    setAIPickerOpen,
    setQuickMaskActive,
    setQuickMaskMode,
    beginTransform,
    setTransformMode,
    convertLayerToPaint,
    endTransform,
    updateBrushSettings,
    updateTextSettings,
    updateShapeSettings,
    updateMarqueeSettings,
    updateRetouchSettings,
    updatePenSettings,
    beginPathEdit,
    setPathEditAnchor,
    endPathEdit,
    setVectorPath,
    setZoom,
    setCanvasDimensions,
    setSelection,
    setFileInfo,
    markDirty,
    markClean,
    togglePropertiesPanel,
    toggleLayersPanel,
    addLayer,
    addAdjustment,
    updateAdjustment,
    removeLayer,
    renameLayer,
    toggleLayerVisibility,
    setLayerOpacity,
    setLayerBlendMode,
    setLayerEffects,
    setLayerMask,
    setTextPath,
    setEditTarget,
    groupLayers,
    ungroup,
    moveNodeInTree,
    setActiveLayer,
    reorderLayer,
    resetLayers,
    resetLayerDocument,
  } = useImageEditorState();

  // Ref shadow of `state.document` for callbacks / rAF closures
  // that need the LATEST tree, not the one captured at their
  // declaration time. Two consumers today:
  //   1. `handleEditAdjustmentLayer` (passed to LayersPanel) reads
  //      it so its identity stays stable across renders — keeps the
  //      LayerRow `React.memo` from busting every slider tick.
  //   2. The rAF-coalesced slow-path effect reads it from the
  //      scheduled tick (not from effect-time closure) so rapid
  //      `state.document` changes all collapse into one composite
  //      pass against the freshest state.
  //
  // Synced via `useEffect` rather than a render-phase assignment so
  // the ref update obeys React's commit phase — important under
  // StrictMode's double-render and any future concurrent-rendering
  // codepath, where a render-time write could briefly point the ref
  // at a snapshot that React then discards.
  const documentRef = useRef(state.document);
  useEffect(() => {
    documentRef.current = state.document;
  }, [state.document]);

  // Tier 1: the history extra captures the full `document` so
  // group/mask metadata (parentId, childIds, mask) survives undo.
  // Older Tier 0 snapshots persist via `layers` only — the set
  // callback below tolerates either shape so legacy IDB sessions
  // keep working.
  interface LayersHistoryExtra {
    document?: EditorDocument;
    layers?: Layer[];
    activeLayerId: string | null;
  }
  const history = useCanvasHistory<LayersHistoryExtra>({
    maxHistory: 50,
    // Recurring "background gone after undo" diagnostic — fires at
    // snapshot time, not at undo time, so the offending handler is on
    // the call stack when the bad snapshot is created. The check is
    // simple and conservative: if the canvas has FabricImages but the
    // React-side layers list is empty, the snapshot is doomed (the next
    // undo would `resetLayers([])` and erase every layer entry from the
    // panel even though the canvas still draws the pixels). Returns
    // `null` on the happy path so this is silent in production.
    validateSnapshot: ({ canvasJSON, extra }) => {
      const objects = (canvasJSON as { objects?: Array<{ type?: string }> }).objects ?? [];
      // Fabric v7 serializes FabricImage as `type: "Image"`; tolerate the
      // legacy lower-case form too in case any older snapshot survives.
      const fabricImages = objects.filter((o) => o.type === 'Image' || o.type === 'image');
      const layerCount = extra?.document?.rootIds.length ?? extra?.layers?.length ?? 0;
      if (fabricImages.length > 0 && layerCount === 0) {
        return `canvas has ${fabricImages.length} FabricImage(s) but layers list is empty — undo from this snapshot would wipe the Background layer entry`;
      }
      return null;
    },
  });

  // Refs that always hold the latest layer state so the history hook's
  // get-extra callback can be registered once and read fresh values.
  const layersStateRef = useRef<LayersHistoryExtra>({
    document: state.document,
    activeLayerId: state.activeLayerId,
  });
  layersStateRef.current = { document: state.document, activeLayerId: state.activeLayerId };

  // Tracks "the user just selected this group via the LayersPanel".
  // Set synchronously in `handleSelectLayer` for group rows; the
  // `onLayerSelected` callback (which Fabric fires synchronously
  // when we then multi-select the group's descendants on the canvas)
  // reads this ref to know it should ignore the descendant
  // selections and keep the group as the active layer. Cleared on
  // any non-group selection, when a canvas selection lands outside
  // the tracked group, or when activeLayerId drifts away from the
  // tracked group via any other path (group deleted, programmatic
  // setActiveLayer from elsewhere, etc.) via the effect below.
  const programmaticGroupSelectionRef = useRef<string | null>(null);

  useEffect(() => {
    history.setExtraStateAccess({
      get: () => layersStateRef.current,
      set: (extra) => {
        // Prefer the v2 (document) shape. Tier 0 sessions in IDB
        // stored only `layers`; the legacy branch promotes via
        // resetLayers (which routes through fromFlatLayers).
        if (extra.document) {
          resetLayerDocument(extra.document, extra.activeLayerId);
        } else if (extra.layers) {
          resetLayers(extra.layers, extra.activeLayerId);
        }
      },
    });
    return () => history.setExtraStateAccess(null);
  }, [history, resetLayers, resetLayerDocument]);

  // Set when a user-action handler changes layer state — useEffect below
  // captures the post-commit snapshot so the saved entry reflects the new state.
  const layerSnapshotPendingRef = useRef(false);
  // Sibling ref for slider-style "continuous" mutations (opacity, effect
  // params, filter sliders). Each tick of a slider drag would otherwise
  // produce its own history snapshot, exhausting the 50-step undo cap in
  // a single drag. The snapshot effect debounces this ref's commits by
  // SLIDER_SNAPSHOT_DEBOUNCE_MS so a slider drag becomes one history
  // entry. `markDirty()` still fires immediately so the tab `*` indicator
  // stays responsive. Discrete operations (toggles, delete, reorder)
  // continue to use the regular ref above and snapshot immediately.
  const layerSnapshotContinuousRef = useRef(false);
  // Sibling ref for the post-loadFile snapshot — same commit-deferred
  // capture, but skips `markDirty()` so a freshly-opened file doesn't
  // appear modified. Without this, opening a file would unconditionally
  // flag the new tab as dirty (and prompt to save on close).
  const cleanSnapshotPendingRef = useRef(false);
  // Debounce timer for the continuous-ref path. Cleared on every new
  // continuous tick (keeps the slider drag in one entry), and flushed
  // ahead of any discrete op or undo so history order stays correct.
  const continuousDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Window the continuous debounce coalesces over. ~half a second feels
  // like the natural "I'm done dragging" pause to the user without
  // making undo wait noticeably after a slider release.
  const SLIDER_SNAPSHOT_DEBOUNCE_MS = 500;

  // While Free Transform is active, suppress the per-`object:modified`
  // snapshot save. The transform session is one "atomic" operation
  // from the user's perspective: their drag should end with EITHER
  // a single Apply-saved snapshot OR a clean Cancel that leaves no
  // history trace. Without this gate, each mouse:up during a drag
  // would saveSnapshot, and Cancel would have to roll back through
  // multiple history entries to get back to pre-transform.
  const transformSuppressSnapshotRef = useRef(false);

  const canvas = useFabricCanvas({
    canvasElRef,
    wrapperRef,
    onObjectModified: () => {
      markDirty();
      if (transformSuppressSnapshotRef.current) return;
      history.saveSnapshot();
    },
    onSelectionChanged: setSelection,
    onLayerSelected: (layerId) => {
      // Sync the panel highlight to the canvas selection. We don't clear the
      // active layer when canvas-selection clears (matches Photoshop).
      if (!layerId) return;

      // Group-selection guard: when the user clicks a group row in
      // LayersPanel, `handleSelectLayer` programmatically multi-selects
      // every descendant's Fabric object so drag moves the whole group
      // together. Fabric's selection event then fires this callback
      // synchronously (before React commits the parent's setActiveLayer)
      // with the first descendant's id. We need a synchronous signal —
      // not the stale React state — to know "the user just selected
      // group G; ignore subsequent descendant selections".
      //
      // `programmaticGroupSelectionRef` is set inside `handleSelectLayer`
      // for groups and cleared on any non-group selection path. When set,
      // we ignore selections that are descendants of the tracked group.
      const programmaticGroupId = programmaticGroupSelectionRef.current;
      const doc = layersStateRef.current.document;
      if (programmaticGroupId && doc) {
        let parentId: string | null = doc.layers[layerId]?.parentId ?? null;
        while (parentId) {
          if (parentId === programmaticGroupId) return; // descendant — keep the group
          parentId = doc.layers[parentId]?.parentId ?? null;
        }
        // Not a descendant — the user has navigated away from the
        // group selection, so the guard no longer applies. Fall
        // through and let the regular flow update activeLayer.
        programmaticGroupSelectionRef.current = null;
      }
      setActiveLayer(layerId);
    },
    onShapeToolClick: (point) => {
      // User clicked empty canvas with the Shape tool active.
      // Vector primitives (polygon / star / arrow) create a real
      // vector layer using `lib/shape-generators`; legacy
      // primitives (rect / circle / line / triangle) continue
      // through the Fabric-primitive `addShape` path.
      const s = state.shapeSettings;
      if (s.type === 'polygon' || s.type === 'star' || s.type === 'arrow') {
        const base = makeLayer('vector');
        const path = generateVectorShapePath(s, point);
        const layer = {
          ...base,
          path,
          fabricObjectId: base.id,
          vectorSource: { kind: s.type },
        };
        addLayer(layer, true);
        canvas.addVectorObject(layer.id, path);
        layerSnapshotPendingRef.current = true;
        return;
      }
      const layer = makeLayer('shape');
      addLayer(layer, true);
      canvas.addShape(state.shapeSettings.type, state.shapeSettings, layer.id, point);
      layerSnapshotPendingRef.current = true;
    },
    onTextToolClick: (point) => {
      // Same behavior for the Text tool.
      const layer = makeLayer('text');
      addLayer(layer, true);
      canvas.addText('add text', state.textSettings, layer.id, point);
      layerSnapshotPendingRef.current = true;
    },
    onPenToolFinalize: (path) => {
      // Pen tool fired Enter / double-click / close-on-first-anchor.
      // Create a vector LayerNode carrying the path, dispatch
      // ADD_LAYER, and mount the persistent Fabric.Path tagged with
      // the new layer's id. The hook's preview is already torn down
      // by the time this fires.
      const base = makeLayer('vector');
      const layer = { ...base, path, fabricObjectId: base.id };
      addLayer(layer, true);
      canvas.addVectorObject(layer.id, path);
      layerSnapshotPendingRef.current = true;
    },
    onPathEditSelectionChanged: (anchorIndex) => {
      // Mirror canvas-side selection changes (anchor click) into
      // the reducer + into the type-mirror state so the
      // EditPathPanel re-renders with the new anchor's type
      // pre-selected on the radio.
      setPathEditAnchor(anchorIndex);
      // `canvas` was assigned just above this hook call; reading
      // its `getPathEditSelectedAnchorType` is safe inside this
      // callback because it only fires after the user clicks.
      setPathEditSelectedAnchorTypeState(canvas.getPathEditSelectedAnchorType());
    },
    onCanvasDimensions: setCanvasDimensions,
    onPathCreated: () => {
      markDirty();
      history.saveSnapshot();
    },
    onZoomChanged: setZoom,
    onCloneSourceChanged: (point) => {
      setCloneSourcePoint(point);
      // The canvas auto-clears armed when a source is set via tap;
      // mirror that here so the panel button reverts.
      if (point) setCloneArmed(false);
    },
    onCloneStrokeRejected: () => {
      // The RetouchPanel's "Source: not set" text is the primary
      // signal to the user; no additional UI noise needed here.
    },
  });

  // Connect canvas to history.
  //
  // Critical: we wire `loadFromJSON` to the *wrapper's* version
  // (`canvas.loadFromJSON`), NOT Fabric's bare `canvas.canvasRef.current.loadFromJSON`.
  //
  // Why: Fabric's `loadFromJSON` rebuilds every Fabric object on the
  // canvas with FRESH instances. The wrapper holds a `paintSurfacesRef`
  // map keyed by layer ID that stores `{ surface canvas, fabricImg }`
  // pairs — those entries point at the OLD Fabric objects after a bare
  // `loadFromJSON`, so any subsequent AI tool would write to a surface
  // canvas no longer attached to the live Fabric tree, and the user
  // sees no change.
  //
  // The wrapper's `loadFromJSON` calls Fabric's underneath, then
  // rebuilds `paintSurfacesRef` from the freshly-created objects.
  // Routing the history hook through it keeps the surface map in sync
  // across every undo / redo / restore.
  useEffect(() => {
    const fabricCanvas = canvas.canvasRef.current;
    if (!fabricCanvas) return;
    const wrapperLoadFromJSON = canvas.loadFromJSON;
    history.setCanvas({
      // toJSON / toObject / renderAll forward to the bare Fabric
      // canvas — these don't touch the surface map, so the underlying
      // method is correct.
      toJSON: () => fabricCanvas.toJSON(),
      toObject: (props?: string[]) => fabricCanvas.toObject(props),
      renderAll: () => fabricCanvas.renderAll(),
      // loadFromJSON goes through the wrapper so paintSurfacesRef gets
      // rebuilt against the fresh Fabric objects.
      loadFromJSON: wrapperLoadFromJSON,
    });
  }, [canvas.canvasRef, canvas.loadFromJSON, history]);

  const { isExporting, exportToLocal, exportToFias } = useImageExport({
    exportToDataURL: canvas.exportToDataURL,
    exportToBlob: canvas.exportToBlob,
  });

  // Multi-tab document state. Each tab represents one open file. Active tab
  // = live editor state; inactive tabs hold serialized PersistedSession
  // snapshots that get re-applied to the editor on tab switch.
  const tabs = useDocumentTabs();

  // Snapshot the current editor state into a PersistedSession-shaped blob.
  // Returns null if there's nothing to snapshot (canvas not ready).
  const buildSnapshot = useCallback((): PersistedSession | null => {
    const json = canvas.toJSON(SERIALIZED_PROPS);
    if (!json) return null;
    const { states, index } = history.getHistory();
    return {
      canvasJSON: json,
      fileName: state.fileName,
      fileId: state.fileId,
      viewportTransform: canvas.getViewportTransform() ?? undefined,
      // Tier 1: persist the layer document, not the derived flat
      // list. The reverse promotion (legacy v1 records) happens
      // in `session-store.migrateSession`.
      document: state.document,
      activeLayerId: state.activeLayerId,
      historyStates: states.length > 0 ? states : undefined,
      historyIndex: states.length > 0 ? index : undefined,
      savedAt: Date.now(),
    };
  }, [canvas, history, state.fileName, state.fileId, state.document, state.activeLayerId]);

  // Restore a PersistedSession into the editor — inverse of buildSnapshot.
  // Used both by the IndexedDB restore-on-mount flow and by tab switching.
  // Reset transient Smart Create state. The error banner, in-flight
  // prompt, and mode/model selections are per-edit-session — a tab
  // switch or fresh editor wipe should not carry them over to the
  // next document.
  const resetAiState = useCallback(() => {
    setAiStatus({ kind: 'idle' });
    setSmartCreatePrompt('');
  }, []);

  const applySnapshot = useCallback(
    async (session: PersistedSession) => {
      try {
        resetAiState();
        await canvas.loadFromJSON(session.canvasJSON);
        const restoredZoom =
          session.viewportTransform && session.viewportTransform.length === 6
            ? canvas.setViewportTransform(session.viewportTransform)
            : (canvas.fitToContainer() ?? 1);
        setFileInfo(session.fileName, session.fileId);
        // Old sessions persist a `filters` field (Fabric brightness/
        // contrast/saturation filters from before the Adjustments
        // dialog landed) — silently ignore it. The Adjustments
        // dialog supersedes this surface entirely; no UI to set
        // those values anymore.
        setZoom(restoredZoom);
        // Restore the layer tree. v2 sessions ship `document`
        // directly; legacy v1 sessions are promoted by
        // `migrateSession` before they reach this point, so the
        // `document` branch covers both.
        if (session.document) {
          resetLayerDocument(session.document, session.activeLayerId ?? null);
        } else if (session.layers) {
          resetLayers(session.layers, session.activeLayerId ?? null);
        }
        setHasImage(true);
        markClean();
        if (session.historyStates && session.historyStates.length > 0) {
          await history.restoreHistory(
            session.historyStates as { canvasJSON: object; extra?: LayersHistoryExtra }[],
            session.historyIndex ?? session.historyStates.length - 1,
          );
        } else {
          // Same race as `loadFile` — the resetLayers dispatch above
          // hasn't committed yet, so an inline `history.saveSnapshot()`
          // captures `layers: []` and the first undo wipes the
          // Background layer. Defer to the post-commit useEffect via
          // the clean-pending flag so the snapshot reflects the just-
          // restored layers list. This branch only runs for sessions
          // that lack a serialized history stack (older sessions and
          // freshly-loaded files where IndexedDB saved before the first
          // AI action), but it's the path users hit most often after a
          // page reload.
          history.clearHistory();
          cleanSnapshotPendingRef.current = true;
        }
      } catch {
        // If apply fails (corrupt snapshot), leave the editor in whatever
        // state it managed to reach — better than crashing.
      }
    },
    [
      canvas,
      history,
      setFileInfo,
      setZoom,
      resetLayers,
      resetLayerDocument,
      markClean,
      resetAiState,
    ],
  );

  // Reset the editor to its empty/no-image state. Used when switching to a
  // freshly-created tab that hasn't had a file loaded into it yet, or when
  // the last tab is closed.
  const resetEditorToEmpty = useCallback(() => {
    resetAiState();
    canvas.clearCanvas();
    resetLayers([], null);
    setFileInfo('Untitled', null);
    history.clearHistory();
    setHasImage(false);
    markClean();
  }, [canvas, resetLayers, setFileInfo, history, markClean, resetAiState]);

  const nextLayerId = useCallback(() => {
    layerIdSeqRef.current += 1;
    return `layer_${Date.now()}_${layerIdSeqRef.current}`;
  }, []);

  const makeLayer = useCallback(
    (type: LayerType, customName?: string): Layer => {
      const typeName: Record<LayerType, string> = {
        image: 'Image',
        text: 'Text',
        shape: 'Shape',
        paint: 'Drawing',
        empty: 'Layer',
        group: 'Group',
        adjustment: 'Adjustment',
        vector: 'Path',
      };
      const count = state.layers.filter((l) => l.type === type).length + 1;
      return {
        id: nextLayerId(),
        name: customName ?? `${typeName[type]} ${count}`,
        type,
        visible: true,
        // Tier 1: every layer carries an explicit `parentId`. Tier 0
        // makers always produced root layers, so `null` is the right
        // default; group-aware UI will pass an explicit parent id.
        parentId: null,
      };
    },
    [state.layers, nextLayerId],
  );

  // Handle file loading. Always opens the file in a NEW tab — both the
  // toolbar Open button and the tab bar's + button funnel through here, so
  // the user's existing work is never silently overwritten. When `handle`
  // is provided (browsers with the File System Access API), the tab keeps
  // a reference so a later Save can overwrite the original file in place.
  const loadFile = useCallback(
    (file: File, handle?: FileSystemFileHandle | null) => {
      const displayName = file.name.replace(/\.[^.]+$/, '');
      tabs.addTab({ fileName: displayName, fileId: null, fileHandle: handle ?? null });
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        const fitZoom = await canvas.loadImage(dataUrl);
        const bgLayer: Layer = {
          id: nextLayerId(),
          name: 'Background',
          type: 'image',
          visible: true,
          parentId: null,
        };
        canvas.setBackgroundLayerId(bgLayer.id);
        resetLayers([bgLayer], bgLayer.id);
        setZoom(fitZoom);
        setFileInfo(displayName, null);
        setHasImage(true);
        markClean();
        // Defer the initial snapshot until React commits the new layers
        // list. `saveSnapshot` reads `layersStateRef.current`, which only
        // updates on the next render — calling it inline here captures
        // an empty layers array, and the first undo (after any subsequent
        // change) restores that empty state, wiping the Background layer.
        // The clean-pending flag routes through the layers useEffect
        // after the commit has flushed; using the "clean" variant skips
        // markDirty so the freshly-opened file stays clean.
        history.clearHistory();
        cleanSnapshotPendingRef.current = true;
      };
      reader.readAsDataURL(file);
    },
    [canvas, setFileInfo, markClean, history, nextLayerId, resetLayers, setZoom, tabs],
  );

  // Layer action handlers. Each mutates state/canvas and flags the snapshot
  // useEffect (above) to capture once the React commit has flushed.
  const handleAddLayer = useCallback(() => {
    const layer = makeLayer('empty');
    addLayer(layer, true);
    layerSnapshotPendingRef.current = true;
  }, [addLayer, makeLayer]);

  const handleSelectLayer = useCallback(
    (id: string) => {
      setActiveLayer(id);
      // Group rows have no Fabric objects of their own — clicking
      // a group should select every descendant's Fabric object so
      // a single drag moves the whole group together (Fabric's
      // built-in ActiveSelection drag handles the synchronized
      // movement). Walk the document subtree to collect the leaf
      // ids; non-group rows fall back to the single-layer path.
      const node = state.document.layers[id];
      if (node?.type === 'group') {
        // Tell `onLayerSelected` (fired synchronously by the
        // upcoming `selectLayersObjects` call) to ignore the
        // descendant selections that would otherwise override
        // `activeLayerId` with the first child's id.
        programmaticGroupSelectionRef.current = id;
        const descendantIds: string[] = [];
        const walk = (gid: string) => {
          const g = state.document.layers[gid];
          if (!g) return;
          for (const cid of g.childIds ?? []) {
            const child = state.document.layers[cid];
            if (!child) continue;
            if (child.type === 'group') walk(cid);
            else descendantIds.push(cid);
          }
        };
        walk(id);
        canvas.selectLayersObjects(descendantIds);
      } else {
        programmaticGroupSelectionRef.current = null;
        canvas.selectLayerObjects(id);
      }
      // Selection changes are not snapshotted — matches Photoshop behavior.
    },
    [setActiveLayer, canvas, state.document],
  );

  const handleToggleLayerVisibility = useCallback(
    (id: string) => {
      const layer = state.layers.find((l) => l.id === id);
      if (!layer) return;
      canvas.setLayerVisibility(id, !layer.visible);
      toggleLayerVisibility(id);
      layerSnapshotPendingRef.current = true;
    },
    [canvas, state.layers, toggleLayerVisibility],
  );

  const handleRenameLayer = useCallback(
    (id: string, name: string) => {
      renameLayer(id, name);
      layerSnapshotPendingRef.current = true;
    },
    [renameLayer],
  );

  const handleDeleteLayer = useCallback(
    (id: string) => {
      canvas.removeLayerObjects(id);
      removeLayer(id);
      layerSnapshotPendingRef.current = true;
    },
    [canvas, removeLayer],
  );

  const handleReorderLayer = useCallback(
    (fromIndex: number, toIndex: number) => {
      reorderLayer(fromIndex, toIndex);
      layerSnapshotPendingRef.current = true;
    },
    [reorderLayer],
  );

  const handleSetLayerOpacity = useCallback(
    (id: string, opacity: number) => {
      // Continuous: opacity is a slider; coalesce a drag into a single
      // history entry via the debounced snapshot path.
      canvas.setLayerOpacity(id, opacity);
      setLayerOpacity(id, opacity);
      layerSnapshotContinuousRef.current = true;
    },
    [canvas, setLayerOpacity],
  );

  const handleSetLayerBlendMode = useCallback(
    (id: string, blendMode: BlendMode) => {
      // Discrete: one selection from the dropdown — snapshot immediately.
      canvas.setLayerBlendMode(id, blendMode);
      setLayerBlendMode(id, blendMode);
      layerSnapshotPendingRef.current = true;
    },
    [canvas, setLayerBlendMode],
  );

  const handleSetLayerEffects = useCallback(
    (id: string, effects: LayerEffects) => {
      // Continuous: effect controls are dominated by sliders (shadow
      // blur/X/Y, stroke width). Toggling an effect on/off via its
      // checkbox lands here too and absorbs the same 500ms debounce —
      // a barely-perceptible delay before the entry shows in history,
      // and the right tradeoff to keep the slider drags coalesced.
      canvas.setLayerEffects(id, effects);
      setLayerEffects(id, effects);
      layerSnapshotContinuousRef.current = true;
    },
    [canvas, setLayerEffects],
  );

  // Tier 1 layer-tree affordances surfaced to LayersPanel.

  // Per-row depth derived from the document tree. The flat
  // `state.layers` list is depth-first (toOrderedList), but the
  // depth itself isn't carried — we compute it once per document
  // change. Single-pass: walk the tree and stash each id's depth.
  const layerDepths = useMemo(() => {
    const out: Record<string, number> = {};
    const walk = (id: string, depth: number) => {
      const node = state.document.layers[id];
      if (!node) return;
      out[id] = depth;
      if (node.type === 'group' && node.childIds) {
        for (const cid of node.childIds) walk(cid, depth + 1);
      }
    };
    for (const rid of state.document.rootIds) walk(rid, 0);
    return out;
  }, [state.document]);

  // Local collapse state per group id. Not persisted — UI-only.
  // A future polish chunk can persist these in state.editTargets-
  // style if users complain about losing collapse state across
  // refreshes.
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set());

  // Clone Stamp state (mirrored from canvas via callbacks).
  // `cloneSourcePoint` shows in the RetouchPanel as feedback;
  // `cloneArmed` flips the panel button into "Tap canvas to set
  // source" mode and switches the canvas cursor to a crosshair
  // until the user taps. Both reset on tool change.
  const [cloneSourcePoint, setCloneSourcePoint] = useState<{ x: number; y: number } | null>(null);
  const [cloneArmed, setCloneArmed] = useState(false);
  // Track the Alt key globally so the cursor flips to a crosshair
  // while Alt is held — same effect as the armed state, but
  // transient. Held + retouch + clone variant = source-set mode.
  // (Fired at the window level rather than on the canvas because
  // the user might lift Alt off-canvas; we still want the next
  // mouse:down event to know whether Alt was held when it
  // arrived. Fabric's mouse:down handler reads `opt.e.altKey`
  // directly so the actual source-set behavior doesn't depend on
  // this state — only the cursor flip does.)
  const [altHeld, setAltHeld] = useState(false);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt' || e.altKey) setAltHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt' || !e.altKey) setAltHeld(false);
    };
    // Reset on blur — if the user alt-tabs out of the window,
    // the keyup might not fire and the cursor would stick.
    const onBlur = () => setAltHeld(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
  // Source-set mode: any of Set Source button armed, Alt held, or
  // both — but only meaningful while a stamp-style Retouch
  // variant (Clone or Heal) is active. Both share the same
  // source-pick state machine on the canvas. Pulled out so both
  // the canvas cursor override and the CanvasArea wrapper-level
  // CSS cursor stay in sync.
  const isStampVariant =
    state.retouchSettings.variant === 'clone' || state.retouchSettings.variant === 'heal';
  const cloneSourceSetMode =
    state.activeTool === 'retouch' && isStampVariant && (cloneArmed || altHeld);
  useEffect(() => {
    canvas.setCursorOverride(cloneSourceSetMode ? 'crosshair' : null);
  }, [cloneSourceSetMode, canvas]);
  // Push armed state into the canvas hook so the next mouse:down
  // knows to set the source.
  useEffect(() => {
    canvas.setCloneArmed(cloneArmed);
  }, [cloneArmed, canvas]);
  // Push the latest RetouchSettings so the canvas Clone handler
  // reads variant + per-variant config.
  useEffect(() => {
    canvas.setRetouchSettings(state.retouchSettings);
  }, [state.retouchSettings, canvas]);
  // Mirror the pen panel settings into the canvas (Tier 2 Group A).
  // The pen tool's mouse handlers read variant + stroke + fill via
  // a ref so live panel edits take effect on the next dab.
  useEffect(() => {
    canvas.setPenSettings(state.penSettings);
  }, [state.penSettings, canvas]);
  // Cancel in-progress pen path when the user switches away from
  // the pen tool. Without this, the preview keeps hanging around
  // and the next pen session appends to it.
  useEffect(() => {
    if (state.activeTool !== 'pen') canvas.cancelPenPath();
  }, [state.activeTool, canvas]);
  // Path Edit overlay (anchors + handles) renders at a constant
  // SCREEN size across zoom levels — the build helpers divide
  // their SCENE-pixel sizes by the current zoom. We rebuild on
  // every zoom change while a session is active so the visual
  // size stays put as the user wheels / pinches.
  useEffect(() => {
    if (state.pathEditActive) canvas.refreshPathEditOverlay();
  }, [state.zoom, state.pathEditActive, canvas]);
  // Cancel armed-mode when leaving the Retouch tool, when the
  // user switches away from a stamp-style variant (Clone / Heal),
  // or via Esc.
  useEffect(() => {
    if (state.activeTool !== 'retouch' || !isStampVariant) {
      if (cloneArmed) setCloneArmed(false);
    }
  }, [state.activeTool, isStampVariant, cloneArmed]);
  const handleSetCloneSourceFromPanel = useCallback(() => {
    setCloneArmed(true);
  }, []);
  const handleClearCloneSource = useCallback(() => {
    canvas.setCloneSource(null);
    setCloneArmed(false);
  }, [canvas]);
  const handleToggleCollapse = useCallback((id: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Shift-click range selection — only the explicit extension
  // beyond the active layer. Plain clicks clear it. The same
  // additional-selection set is also used by Ctrl/Cmd+click toggle
  // and by the Multi-Select-mode tap-to-toggle path, so the chip
  // row / future multi-layer ops see one unified "additional ids"
  // regardless of how the user built up the selection.
  const [shiftRangeIds, setShiftRangeIds] = useState<Set<string>>(() => new Set());

  // Multi-select mode (mobile-friendly): when on, taps on layer
  // rows TOGGLE membership in shiftRangeIds rather than activating
  // a different layer. The active layer stays put; the user can
  // build up a selection by tapping additional rows. Toggling the
  // mode off doesn't clear the selection — the chip row stays as
  // long as ≥2 vectors are selected. Off by default; the
  // LayersPanel header surfaces a 'Multi' toggle button.
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const toggleMultiSelectMode = useCallback(() => {
    setMultiSelectMode((v) => !v);
  }, []);

  // Toggle a layer's membership in the additional-selection set.
  // Routes from Ctrl/Cmd+click and from Multi-mode tap. We never
  // toggle the *active* layer (you can't deselect it; that would
  // require picking a new active, which feels surprising on tap).
  // Touch on the active layer in Multi mode is a no-op.
  const handleToggleSelectLayer = useCallback(
    (id: string) => {
      if (id === state.activeLayerId) return;
      setShiftRangeIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [state.activeLayerId],
  );
  useEffect(() => {
    // A new active layer (via plain click or another path) drops
    // the shift extension. Group expansion below is derived
    // separately from this — clearing here doesn't affect group
    // descendants.
    setShiftRangeIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [state.activeLayerId]);

  // Clear the programmatic-group-selection ref whenever the active
  // layer drifts away from the tracked group via any path (group
  // deleted, programmatic setActiveLayer from outside the canvas
  // event flow, etc.). Without this the ref can stay set forever
  // pointing at a group that's no longer the active layer, and the
  // next selection on what would have been a descendant would be
  // silently swallowed.
  useEffect(() => {
    if (
      programmaticGroupSelectionRef.current !== null &&
      state.activeLayerId !== programmaticGroupSelectionRef.current
    ) {
      programmaticGroupSelectionRef.current = null;
    }
  }, [state.activeLayerId]);
  const handleShiftSelectLayer = useCallback(
    (id: string) => {
      const ordered = state.layers;
      const anchor = state.activeLayerId;
      if (!anchor || anchor === id) return;
      const a = ordered.findIndex((l) => l.id === anchor);
      const b = ordered.findIndex((l) => l.id === id);
      if (a === -1 || b === -1) return;
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const range = new Set<string>();
      for (let i = lo; i <= hi; i++) range.add(ordered[i].id);
      range.delete(anchor);
      setShiftRangeIds(range);
    },
    [state.layers, state.activeLayerId],
  );

  // Effective panel selection — what gets the multi-select tint
  // and what the "+ Group" header button operates on. Either:
  //   - the active layer is a group → all descendant leaves
  //     (so the panel shows every Fabric-backed child as
  //     selected and Group nesting is visible at a glance), OR
  //   - the active layer is a leaf → the shift-extended range
  //     (zero entries when no shift-click is in flight).
  const selectedLayerIds = useMemo(() => {
    if (!state.activeLayerId) return shiftRangeIds;
    const node = state.document.layers[state.activeLayerId];
    if (node?.type === 'group') {
      const out = new Set<string>();
      const walk = (gid: string) => {
        const g = state.document.layers[gid];
        if (!g) return;
        for (const cid of g.childIds ?? []) {
          const child = state.document.layers[cid];
          if (!child) continue;
          if (child.type === 'group') walk(cid);
          else out.add(cid);
        }
      };
      walk(state.activeLayerId);
      return out;
    }
    return shiftRangeIds;
  }, [state.activeLayerId, state.document, shiftRangeIds]);

  // Count of distinct vector layers in the current selection
  // (active + shift-extended). Drives the PropertiesPanel boolean-
  // ops chip row: hidden unless ≥2, since a single shape has
  // nothing to combine with. Re-derives whenever selection /
  // doc state changes so the row appears/disappears live as the
  // user shift-clicks.
  const eligibleVectorLayerCount = useMemo(() => {
    const ids = new Set<string>();
    if (state.activeLayerId) ids.add(state.activeLayerId);
    for (const id of selectedLayerIds) ids.add(id);
    let count = 0;
    for (const id of ids) {
      const node = state.document.layers[id];
      if (node?.type === 'vector' && node.path) count++;
    }
    return count;
  }, [state.activeLayerId, selectedLayerIds, state.document]);

  // Text-on-path selection (Tier 2 Group A PR-E). Computed from the
  // UNION {activeLayerId, ...selectedLayerIds} — covers both
  // shift-click (desktop) and Multi-mode tap (mobile; the LayersPanel
  // header's "Multi" toggle routes taps through
  // `handleToggleSelectLayer` into the same `shiftRangeIds` set
  // shift-click writes to). The candidate / bound info don't care
  // which layer is active — only that the selection contains the
  // right shapes.
  const textPathSelectionUnion = useMemo(() => {
    const ids = new Set<string>();
    if (state.activeLayerId) ids.add(state.activeLayerId);
    for (const id of selectedLayerIds) ids.add(id);
    return ids;
  }, [state.activeLayerId, selectedLayerIds]);

  // "Unbind" row — surfaces when the selection contains exactly one
  // text layer that's already bound. Multiple bound texts → no UI
  // (ambiguous; user narrows the selection to disambiguate).
  const textPathBoundInfo = useMemo(() => {
    let boundTextLayerId: string | null = null;
    for (const id of textPathSelectionUnion) {
      const n = state.document.layers[id];
      if (n?.type === 'text' && n.textPath) {
        if (boundTextLayerId) return null;
        boundTextLayerId = id;
      }
    }
    if (!boundTextLayerId) return null;
    const textNode = state.document.layers[boundTextLayerId];
    const bindingId = textNode?.textPath?.pathLayerId;
    if (!bindingId) return null;
    const target = state.document.layers[bindingId];
    return {
      textLayerId: boundTextLayerId,
      pathLayerId: bindingId,
      pathLayerName: target?.name ?? 'Path',
    };
  }, [state.document, textPathSelectionUnion]);

  // "Bind to Path" row — surfaces when the selection contains
  // exactly one (text + vector) pair and no bound text is already
  // there (in which case the Unbind row takes the slot). Unrelated
  // layers in the selection are ignored — the user clearly wants
  // to bind these two, plus a stray paint layer in the multi
  // shouldn't suppress the button.
  const textPathBindCandidate = useMemo(() => {
    if (textPathBoundInfo) return null;
    let textLayerId: string | null = null;
    let vectorLayerId: string | null = null;
    for (const id of textPathSelectionUnion) {
      const n = state.document.layers[id];
      if (!n) continue;
      if (n.type === 'text') {
        if (textLayerId) return null;
        textLayerId = id;
      } else if (n.type === 'vector' && n.path) {
        if (vectorLayerId) return null;
        vectorLayerId = id;
      }
    }
    if (!textLayerId || !vectorLayerId) return null;
    return {
      textLayerId,
      pathLayerId: vectorLayerId,
      pathLayerName: state.document.layers[vectorLayerId]?.name ?? 'Path',
    };
  }, [state.document, textPathSelectionUnion, textPathBoundInfo]);

  // Arch slider gating — operates on the ACTIVE layer's text. If
  // the active layer happens to be a bound text, arch is moot
  // (the bound path overrides it). Otherwise leave arch editable
  // — multi-selection may include a bound text that isn't the
  // active layer, and the user might want to edit arch on a
  // different active text.
  const isActiveTextBoundToPath = useMemo(() => {
    if (!state.activeLayerId) return false;
    const active = state.document.layers[state.activeLayerId];
    return !!(active?.type === 'text' && active.textPath);
  }, [state.activeLayerId, state.document]);

  const handleBindTextToPath = useCallback(() => {
    if (!textPathBindCandidate) return;
    setTextPath(textPathBindCandidate.textLayerId, {
      pathLayerId: textPathBindCandidate.pathLayerId,
    });
    layerSnapshotPendingRef.current = true;
  }, [textPathBindCandidate, setTextPath]);

  const handleUnbindTextFromPath = useCallback(() => {
    if (!textPathBoundInfo) return;
    setTextPath(textPathBoundInfo.textLayerId, null);
    layerSnapshotPendingRef.current = true;
  }, [textPathBoundInfo, setTextPath]);

  // Group / Ungroup. Header "+ Group" wraps the active layer plus
  // any shift-extended selection. groupNodes validates that all
  // ids share a parent; we filter to that subset before dispatch
  // so an out-of-range selection silently drops mismatched ids
  // rather than failing visibly.
  const handleGroupActiveLayer = useCallback(() => {
    if (!state.activeLayerId) return;
    const ids = [state.activeLayerId, ...Array.from(selectedLayerIds)];
    const activeParent = state.document.layers[state.activeLayerId]?.parentId ?? null;
    const sharedIds = ids.filter((id) => state.document.layers[id]?.parentId === activeParent);
    if (sharedIds.length === 0) return;
    const groupId = `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    groupLayers(sharedIds, groupId, 'Group');
    layerSnapshotPendingRef.current = true;
    setShiftRangeIds(new Set());
  }, [state.activeLayerId, selectedLayerIds, state.document, groupLayers]);

  // Boolean ops on 2+ selected vector layers (Tier 2 Group A
  // PR-C). The selected vector layers fold sequentially through
  // the chosen op: ((A op B) op C) op D … so an N-layer Union
  // becomes the multi-shape silhouette of all of them. Source
  // layers are HIDDEN (visible=false) rather than deleted —
  // preserves the user's recovery path without forcing an
  // Affinity-style "Source" group (deferred polish). The result
  // becomes the active layer.
  const handleVectorBooleanOp = useCallback(
    (op: BooleanOp) => {
      const candidateIds = [state.activeLayerId, ...Array.from(selectedLayerIds)].filter(
        (id): id is string => typeof id === 'string',
      );
      // Dedupe preserving order so the active layer ends up first.
      const seen = new Set<string>();
      const orderedIds: string[] = [];
      for (const id of candidateIds) {
        if (!seen.has(id)) {
          seen.add(id);
          orderedIds.push(id);
        }
      }
      const vectorLayers = orderedIds
        .map((id) => state.document.layers[id])
        .filter((n): n is Layer => !!n && n.type === 'vector' && !!n.path);
      if (vectorLayers.length < 2) return;

      // Bake each layer's Fabric transform into its path data so
      // paper.js operates in scene-space. Without this, two layers
      // with the same raw path-data but different translations
      // would look identical to paper, producing nonsense
      // intersect/subtract/exclude results placed at the path-data
      // origin instead of the user's visible center.
      const bakedPaths = vectorLayers.map(
        (layer) => canvas.getBakedVectorPath(layer.id, layer.path!) ?? layer.path!,
      );

      let result: VectorPath = bakedPaths[0];
      for (let i = 1; i < bakedPaths.length; i++) {
        result = pathBoolean(result, bakedPaths[i], op);
        if (result.subPaths.length === 0) {
          // Most commonly: Intersect on disjoint shapes. Tell the
          // user — otherwise the chip click reads as a hang. We
          // exit BEFORE hiding any source so the document state
          // stays consistent (no destructive change on no-result).
          toast({
            title: 'Boolean produced no geometry',
            description: 'The selected shapes have no overlap for this operation.',
          });
          return;
        }
      }
      // Inherit style from the first source layer so the result
      // visually echoes the user's intent.
      const firstPath = vectorLayers[0].path!;
      result = {
        ...result,
        stroke: firstPath.stroke,
        fill: firstPath.fill,
      };

      const base = makeLayer('vector');
      const resultLayer: Layer = {
        ...base,
        path: result,
        fabricObjectId: base.id,
      };
      addLayer(resultLayer, true);
      canvas.addVectorObject(resultLayer.id, result);

      // Hide every source layer so the result reads as the
      // canonical visual. User can re-show via the eye icon to
      // recover; nothing is destroyed. Skip already-hidden layers
      // so the toggle doesn't flip them visible again.
      for (const layer of vectorLayers) {
        if (layer.visible !== false) toggleLayerVisibility(layer.id);
      }
      // Drop the shift-extended selection — after the op the
      // sources are no longer the user's focal target, the result
      // is. Mirrors handleGroupActiveLayer's post-op cleanup.
      setShiftRangeIds(new Set());
      layerSnapshotPendingRef.current = true;
    },
    [
      state.activeLayerId,
      selectedLayerIds,
      state.document,
      makeLayer,
      addLayer,
      canvas,
      toggleLayerVisibility,
    ],
  );
  const handleUngroup = useCallback(
    (id: string) => {
      ungroup(id);
      layerSnapshotPendingRef.current = true;
    },
    [ungroup],
  );
  const handleMoveNodeInTree = useCallback(
    (id: string, parentId: string | null, index: number) => {
      moveNodeInTree(id, parentId, index);
      layerSnapshotPendingRef.current = true;
    },
    [moveNodeInTree],
  );

  // Mask add/delete: routes through the canvas helper (which
  // creates / detaches the Fabric clipPath) and mirrors the
  // resulting LayerMask config into the document via the reducer
  // so persistence + undo work. Use the pending-snapshot pattern
  // so history.saveSnapshot fires AFTER React flushes the
  // setLayerMask dispatch — calling saveSnapshot inline here would
  // capture stale `state.layers` from `layersStateRef.current`
  // (it's only updated in render), and that stale extra would
  // wipe the mask config from the document on any later restore
  // (page reload's history.restoreHistory or undo/redo). Discrete,
  // not continuous — masks are a single user gesture, not a slider
  // drag.
  const handleAddMaskToLayer = useCallback(
    (id: string, opts?: { fromMarquee?: boolean }) => {
      const mask = canvas.addLayerMask(id, opts);
      if (!mask) return;
      setLayerMask(id, mask);
      layerSnapshotPendingRef.current = true;
    },
    [canvas, setLayerMask],
  );
  const handleDeleteMaskFromLayer = useCallback(
    (id: string) => {
      canvas.removeLayerMask(id);
      setLayerMask(id, null);
      layerSnapshotPendingRef.current = true;
    },
    [canvas, setLayerMask],
  );

  // Mask config toggles (Disable/Enable, Invert). The reducer
  // dispatch sets the new full mask config; the mask-sync effect
  // upstream picks up the diff and re-materializes the clipPath
  // effective alpha. Snapshot-pending so history captures the
  // toggle as a discrete entry. No-op when the layer doesn't have
  // a mask (overflow menu wouldn't surface the toggle in that
  // case anyway, but stays defensive against stale closures).
  const handleToggleMaskEnabled = useCallback(
    (id: string) => {
      const node = state.document.layers[id];
      const mask = node?.mask;
      if (!mask) return;
      setLayerMask(id, { ...mask, enabled: !mask.enabled });
      layerSnapshotPendingRef.current = true;
    },
    [state.document, setLayerMask],
  );
  const handleToggleMaskInverted = useCallback(
    (id: string) => {
      const node = state.document.layers[id];
      const mask = node?.mask;
      if (!mask) return;
      setLayerMask(id, { ...mask, inverted: !mask.inverted });
      layerSnapshotPendingRef.current = true;
    },
    [state.document, setLayerMask],
  );

  // Keep the canvas z-order in sync with the layer list. Front-of-list = top of canvas.
  useEffect(() => {
    canvas.applyLayerOrder(state.layers.map((l) => l.id));
  }, [state.layers, canvas]);

  // Cascade visibility through the tree: hiding a group hides
  // every descendant Fabric object. Runs on every document change
  // and is idempotent — only renders when something moved.
  useEffect(() => {
    canvas.applyEffectiveVisibility(state.document);
  }, [state.document, canvas]);

  // Flush any in-flight continuous (slider-debounced) snapshot. Called
  // by anything that needs the slider drag committed as a discrete
  // history entry before the next operation lands — discrete edits,
  // explicit undo / redo, and the snapshot effect itself when both a
  // continuous and discrete tick are pending in the same render. Safe
  // to call repeatedly; no-ops when no debounce is in flight.
  const flushContinuousSnapshot = useCallback(() => {
    if (continuousDebounceRef.current) {
      clearTimeout(continuousDebounceRef.current);
      continuousDebounceRef.current = null;
      history.saveSnapshot();
    }
  }, [history]);

  // Cancel the debounce on unmount so the timer can't fire after the
  // editor is gone (would call history.saveSnapshot against a stale
  // canvas ref). The flush-on-blur side is handled implicitly by the
  // existing IndexedDB session save — even if a continuous snapshot
  // never reaches history, the live canvas state survives a refresh.
  useEffect(() => {
    return () => {
      if (continuousDebounceRef.current) {
        clearTimeout(continuousDebounceRef.current);
        continuousDebounceRef.current = null;
      }
    };
  }, []);

  // Capture history snapshot after a user-action layer change has committed.
  // Three pending refs feed this:
  //   - `layerSnapshotPendingRef` — discrete user actions (toggles,
  //     reorder, delete, paint stroke). Snapshots immediately and
  //     marks dirty. Flushes any pending continuous first so slider-
  //     then-discrete sequences land in history in the right order.
  //   - `layerSnapshotContinuousRef` — continuous slider-style
  //     mutations. Marks dirty immediately for tab `*` responsiveness;
  //     debounces the snapshot itself so a slider drag becomes one
  //     history entry.
  //   - `cleanSnapshotPendingRef` — post-loadFile snapshot. Snapshots
  //     immediately and skips markDirty so freshly-opened files don't
  //     appear modified.
  useEffect(() => {
    if (layerSnapshotPendingRef.current) {
      layerSnapshotPendingRef.current = false;
      flushContinuousSnapshot();
      history.saveSnapshot();
      markDirty();
    } else if (layerSnapshotContinuousRef.current) {
      layerSnapshotContinuousRef.current = false;
      markDirty();
      if (continuousDebounceRef.current) clearTimeout(continuousDebounceRef.current);
      continuousDebounceRef.current = setTimeout(() => {
        history.saveSnapshot();
        continuousDebounceRef.current = null;
      }, SLIDER_SNAPSHOT_DEBOUNCE_MS);
    } else if (cleanSnapshotPendingRef.current) {
      cleanSnapshotPendingRef.current = false;
      history.saveSnapshot();
    }
  }, [state.layers, history, markDirty, flushContinuousSnapshot]);

  const handleApplyCrop = useCallback(() => {
    if (canvas.applyActiveCrop()) {
      markDirty();
      history.saveSnapshot();
    }
  }, [canvas, markDirty, history]);

  // Invert turns any marquee shape into a wand-style mask selection
  // (the inverse of a rect is a frame, not a rect — see the
  // `invertMarquee` impl). Push React's marqueeSettings.shape to
  // match so the marquee panel's shape buttons highlight the right
  // tile (otherwise the user sees "rect" highlighted while looking
  // at a wand-style selection). Declared near the other key-
  // shortcut handlers (Copy/Cut/Paste/ApplyCrop) because the
  // keyboard listener's keyHandlersRef captures it at that point
  // in the render order.
  const handleInvertSelection = useCallback(() => {
    if (state.marqueeSettings.shape !== 'wand') {
      updateMarqueeSettings({ shape: 'wand' });
    }
    canvas.invertMarquee();
  }, [canvas, state.marqueeSettings.shape, updateMarqueeSettings]);

  // Quick Mask toggle (Tier 1 Group D). The boolean lives in the
  // reducer; this effect bridges to the canvas side. Entering installs
  // the working alpha mask + red overlay; exiting converts the mask
  // back into a wand-style marquee. The effect runs whenever the
  // flag flips, regardless of what triggered it (StatusBar chip
  // popover, future command-palette entry, etc.).
  const handleToggleQuickMask = useCallback(() => {
    setQuickMaskActive(!state.quickMaskActive);
  }, [setQuickMaskActive, state.quickMaskActive]);

  // Apply Quick Mask via the chosen output target. Selection is the
  // default Photoshop semantic; Layer Mask attaches the painted
  // alpha to the active layer's clipPath via the same plumbing
  // `addLayerMask` uses.
  const handleApplyQuickMask = useCallback(
    (output: 'selection' | 'mask') => {
      const qmCanvas = canvas.getQuickMaskCanvas();
      if (output === 'mask' && state.activeLayerId && qmCanvas) {
        const maskConfig = canvas.applyLayerMaskFromCanvas(state.activeLayerId, qmCanvas);
        if (maskConfig) {
          setLayerMask(state.activeLayerId, maskConfig);
        }
        // Skip the auto-selection install — we've already applied
        // the mask. The state flip below would otherwise trigger
        // exitQuickMask's default `setSelectionFromMask` path.
        canvas.discardQuickMask();
      }
      // Flip the React flag last so the active-flag effect's
      // exitQuickMask is a no-op (refs cleared by discard in the
      // mask path, or runs default selection-install in the
      // selection path).
      setQuickMaskActive(false);
    },
    [canvas, state.activeLayerId, setLayerMask, setQuickMaskActive],
  );

  const handleCancelQuickMask = useCallback(() => {
    // Cancel = throw away the painted mask AND don't install a
    // selection. The selection path would otherwise auto-install
    // via exitQuickMask's default behavior.
    canvas.discardQuickMask();
    setQuickMaskActive(false);
  }, [canvas, setQuickMaskActive]);

  // Mirror state.activeTool → canvas-side tool state for ANY path
  // that changes the active tool, not just ToolPalette clicks.
  // `handleToolSelect` calls `canvas.setTool` directly for an
  // immediate sync; this effect covers the rest — Quick Mask
  // toggling, future command-palette entries, etc. — so brush
  // handlers in `useFabricCanvas` (which gate on
  // `activeToolRef.current`) see the right tool.
  //
  // The `lastSyncedTool` ref guards against double-firing: when
  // `handleToolSelect` runs `canvas.setTool` directly, this effect
  // will also see the new `state.activeTool` and would otherwise
  // call `setTool` again. Some `setTool` cases (notably 'crop')
  // are non-idempotent — re-entering 'crop' adds a second crop
  // rect — so guarding is necessary.
  const brushSettingsRef = useRef(state.brushSettings);
  const lastSyncedToolRef = useRef<EditorTool>(state.activeTool);
  useEffect(() => {
    brushSettingsRef.current = state.brushSettings;
  }, [state.brushSettings]);
  useEffect(() => {
    if (lastSyncedToolRef.current === state.activeTool) return;
    lastSyncedToolRef.current = state.activeTool;
    canvas.setTool(state.activeTool, brushSettingsRef.current);
  }, [state.activeTool, canvas]);

  useEffect(() => {
    if (state.quickMaskActive) {
      // Active marquee shape lands on 'wand' on exit (the QM mask
      // becomes a wand selection), so prep the panel UI now so the
      // user doesn't see a momentary mismatch.
      if (state.marqueeSettings.shape !== 'wand') {
        updateMarqueeSettings({ shape: 'wand' });
      }
      const ok = canvas.enterQuickMask();
      if (!ok) {
        // Couldn't enter — likely no image loaded. Roll back the
        // state to keep the UI consistent.
        setQuickMaskActive(false);
      }
    } else {
      canvas.exitQuickMask();
    }
    // (Mode mirror sits in a separate effect — see below.)
    // The deps are intentionally `quickMaskActive` and `canvas` only:
    // depending on `state.marqueeSettings.shape` or
    // `updateMarqueeSettings` would re-fire the effect when we set
    // the shape ourselves, calling enter/exit a second time per
    // toggle. `enterQuickMask` is defensively idempotent (sweeps
    // any existing tagged overlays before adding a new one) so a
    // stray re-fire is harmless. The project's ESLint config
    // doesn't register the exhaustive-deps rule.
  }, [state.quickMaskActive, canvas]);

  // Mirror Quick Mask mode (Paint / Erase) into the canvas-side ref
  // read by the brush handler. Separate from the active-flag effect
  // so toggling mode mid-session doesn't re-enter QM.
  useEffect(() => {
    canvas.setQuickMaskMode(state.quickMaskMode);
  }, [state.quickMaskMode, canvas]);

  // Free Transform (Tier 1 Group E Phase 1). Mirrors the state-flag
  // pattern from Quick Mask: BEGIN_TRANSFORM in the reducer flips
  // `state.transformActive`, this effect bridges to the canvas.
  // The control config (which Fabric handles render, skew locks)
  // sits in `applyTransformControls` — fired on entry AND on every
  // mode change so the per-mode handle setup stays in lockstep.
  const handleBeginTransform = useCallback(
    (layerId: string) => {
      // Mute per-stroke snapshots for the duration of the session.
      // Apply will save ONE snapshot at the end; Cancel saves
      // none. Without the mute, dragging fires object:modified on
      // mouse:up, which saves a "post-drag" snapshot that Cancel
      // can't cleanly roll back.
      transformSuppressSnapshotRef.current = true;
      beginTransform(layerId);
    },
    [beginTransform],
  );
  const handleApplyTransform = useCallback(() => {
    canvas.exitTransformMode(true);
    transformSuppressSnapshotRef.current = false;
    // Apply marks the session's net effect as ONE history entry.
    // markDirty + saveSnapshot mirror what onObjectModified would
    // do for a normal user-initiated edit.
    markDirty();
    history.saveSnapshot();
    endTransform(true);
  }, [canvas, endTransform, history, markDirty]);
  // Intercepts transform-mode changes from the FreeTransformPanel
  // chip row. Distort / Perspective / Warp need a `FabricImage`
  // target (the underlying rasterizer in `commitNonAffineDeformation`
  // uses `setElement`, which is FabricImage-only). On a vector /
  // shape / text layer we open the rasterize-before-warp prompt
  // instead of switching mode immediately; the dialog's onConfirm
  // does the rasterize then proceeds. Free / Skew still pass
  // through directly — they only need affine matrix props that
  // every Fabric object supports.
  const handleSetTransformMode = useCallback(
    (mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp') => {
      if (mode !== 'distort' && mode !== 'perspective' && mode !== 'warp') {
        setTransformMode(mode);
        return;
      }
      const layerId = state.transformLayerId ?? state.activeLayerId;
      const node = layerId ? state.document.layers[layerId] : null;
      const needsRasterize =
        node?.type === 'vector' || node?.type === 'shape' || node?.type === 'text';
      if (!needsRasterize) {
        setTransformMode(mode);
        return;
      }
      // Defer the mode switch — the dialog's onConfirm will run
      // the rasterize swap and dispatch SET_TRANSFORM_MODE then.
      setPendingWarpMode(mode);
    },
    [state.transformLayerId, state.activeLayerId, state.document.layers, setTransformMode],
  );
  const handleConfirmRasterizeBeforeWarp = useCallback(() => {
    if (!pendingWarpMode) return;
    const layerId = state.transformLayerId ?? state.activeLayerId;
    if (!layerId) {
      setPendingWarpMode(null);
      return;
    }
    // Order matters: swap the Fabric object FIRST so the mode-mirror
    // effect (which calls applyTransformControls) sees a
    // FabricImage when it next fires. Then mutate the document so
    // type/path stay consistent with the on-canvas reality.
    const ok = canvas.rasterizeLayerToImage(layerId);
    if (!ok) {
      setPendingWarpMode(null);
      return;
    }
    convertLayerToPaint(layerId);
    setTransformMode(pendingWarpMode);
    setPendingWarpMode(null);
  }, [
    pendingWarpMode,
    state.transformLayerId,
    state.activeLayerId,
    canvas,
    convertLayerToPaint,
    setTransformMode,
  ]);
  const handleCancelRasterizeBeforeWarp = useCallback(() => {
    // Drop the dialog. We don't reset to 'free' because we never
    // dispatched a mode change — `state.transformMode` is still
    // whatever the panel showed before the chip click. The chip
    // row's active-state indicator will reflect that.
    setPendingWarpMode(null);
  }, []);
  const handleCancelTransform = useCallback(() => {
    canvas.exitTransformMode(false);
    transformSuppressSnapshotRef.current = false;
    // No saveSnapshot — Cancel leaves no history trace per the
    // "cancel means it never happened" user expectation.
    endTransform(false);
  }, [canvas, endTransform]);

  // Liquify — Tier 1 Group E final piece. Activates the target
  // layer (if it isn't already), pulls its rasterizable surface
  // via the same `getActiveAdjustmentTargets` gate the Adjust
  // button uses, and snapshots a fresh canvas as the workspace's
  // working source. Apply routes through `runOnActiveLayerFullImage`
  // which writes the deformed pixels back over the live surface
  // and fires `object:modified` (markDirty + history.saveSnapshot
  // run through the existing canvas-hook plumbing — no need to
  // mirror them here).
  const handleBeginLiquify = useCallback(
    (layerId: string) => {
      if (state.activeLayerId !== layerId) {
        setActiveLayer(layerId);
        canvas.setActiveLayerId(layerId);
      }
      const targets = canvas.getActiveAdjustmentTargets(state.document);
      if (targets.length === 0) return;
      const liveSurface = targets[0].canvas;
      const snapshot = document.createElement('canvas');
      snapshot.width = liveSurface.width;
      snapshot.height = liveSurface.height;
      // `willReadFrequently: true` on the FIRST getContext call —
      // the rasterizer reads back via getImageData every drag tick,
      // and the flag is locked on first context creation regardless
      // of later options. Without this Chrome logs a perf warning.
      const sctx = snapshot.getContext('2d', { willReadFrequently: true });
      if (!sctx) return;
      sctx.drawImage(liveSurface, 0, 0);
      setLiquifySource(snapshot);
      setLiquifyOpen(true);
    },
    [state.activeLayerId, state.document, canvas, setActiveLayer],
  );
  const handleApplyLiquify = useCallback(
    async (deformedCanvas: HTMLCanvasElement) => {
      try {
        await canvas.runOnActiveLayerFullImage(async () => deformedCanvas);
      } catch (err) {
        // Lazy-register fallback in `runOnActiveLayerFullImage`
        // covers the common "unpainted image layer" case, so a
        // throw here is unexpected — log and close the workspace
        // anyway so the user isn't stuck with a stale full-screen
        // takeover.
        console.error('[image-editor] liquify apply failed:', err);
      } finally {
        setLiquifyOpen(false);
        setLiquifySource(null);
      }
    },
    [canvas],
  );
  const handleCloseLiquify = useCallback(() => {
    setLiquifyOpen(false);
    setLiquifySource(null);
  }, []);

  // Path Edit mode — Tier 2 Group A PR-B. Mirrors the Free
  // Transform pattern: reducer flag + a canvas-side overlay that
  // owns the working state until Apply / Cancel.
  const handleBeginPathEdit = useCallback(
    (layerId: string) => {
      const node = state.document.layers[layerId];
      if (!node || node.type !== 'vector' || !node.path) return;
      if (state.activeLayerId !== layerId) {
        setActiveLayer(layerId);
        canvas.setActiveLayerId(layerId);
      }
      // Snapshot the path BEFORE dispatching BEGIN_PATH_EDIT — the
      // canvas-side overlay treats it as the source-of-truth for
      // the session. Reducer state.document stays at this snapshot
      // throughout; Apply commits via SET_VECTOR_PATH.
      const ok = canvas.enterPathEditMode(layerId, node.path);
      if (!ok) return;
      beginPathEdit(layerId);
    },
    [state.document, state.activeLayerId, canvas, beginPathEdit, setActiveLayer],
  );
  const handleApplyPathEdit = useCallback(() => {
    if (!state.pathEditLayerId) return;
    const finalPath = canvas.exitPathEditMode(true);
    if (finalPath) {
      // Drop the `vectorSource` marker on Apply — the user has
      // committed their custom anchor edits, so the path is no
      // longer a "regular polygon / star / arrow." Without this,
      // a subsequent color / stroke / sides slider change would
      // trip the regenerate flow and overwrite the user's curve
      // edits with a freshly-regenerated primitive.
      setVectorPath(state.pathEditLayerId, finalPath, { clearVectorSource: true });
    }
    endPathEdit(true);
    setPathEditSelectedAnchorTypeState(null);
    // Defer the history snapshot via `layerSnapshotPendingRef` so
    // it fires from the post-render useEffect — the
    // `setVectorPath` dispatch above queues a state change, and an
    // inline `history.saveSnapshot()` runs BEFORE React commits.
    // The pre-commit snapshot captures the canvas in its post-Apply
    // shape (canvas state is mutable + already updated) but the
    // React document in its pre-Apply shape. Reload via
    // `restoreHistory` then mismatches them: the canvas renders the
    // new path but `state.document.vector.path` reverts to the old
    // geometry, breaking anything that derives from the document
    // (text-on-path bindings, future vector mask paths, etc.).
    // markDirty is fired by the pending-snapshot effect alongside
    // saveSnapshot — no need to call it inline here.
    layerSnapshotPendingRef.current = true;
  }, [canvas, state.pathEditLayerId, endPathEdit, setVectorPath]);
  const handleCancelPathEdit = useCallback(() => {
    canvas.exitPathEditMode(false);
    endPathEdit(false);
    setPathEditSelectedAnchorTypeState(null);
  }, [canvas, endPathEdit]);
  // React state mirror of the selected anchor's type. The canvas
  // owns the authoritative working path; this mirror exists so
  // the EditPathPanel's radio reflects type changes immediately
  // (a direct read of `canvas.getPathEditSelectedAnchorType()`
  // per render doesn't re-trigger when `setPathEditAnchorType`
  // mutates the canvas-side state, because mutating a ref
  // doesn't schedule a React update).
  const [pathEditSelectedAnchorType, setPathEditSelectedAnchorTypeState] = useState<
    'corner' | 'smooth' | 'asymmetric' | null
  >(null);
  const handlePathEditAnchorType = useCallback(
    (type: 'corner' | 'smooth' | 'asymmetric') => {
      canvas.setPathEditAnchorType(type);
      setPathEditSelectedAnchorTypeState(type);
    },
    [canvas],
  );
  // The canvas-side selection-change callback (passed in
  // `useFabricCanvas` opts above) calls `setPathEditAnchor` AND
  // refreshes the type mirror so the radio reflects the new
  // anchor's existing type the moment it's selected.

  useEffect(() => {
    if (state.transformActive && state.transformLayerId) {
      const ok = canvas.enterTransformMode(state.transformLayerId);
      if (!ok) {
        // Target layer missing — likely deleted between dispatch
        // and effect fire. Roll back so the panel doesn't get
        // stuck.
        endTransform(false);
      } else {
        canvas.applyTransformControls(state.transformMode);
      }
    }
    // The end-transform handlers (apply / cancel) call
    // `canvas.exitTransformMode` themselves with the right
    // `apply` boolean, so the `else` branch is a deliberate
    // no-op.
    //
    // `state.transformMode` is intentionally omitted — the
    // second effect below mirrors mode changes separately, so
    // re-running this on every mode flip would re-enter the
    // session and clobber in-progress overlay state. The
    // project's ESLint config doesn't register
    // `react-hooks/exhaustive-deps` AND uses
    // `--report-unused-disable-directives`, so adding a disable
    // directive for it is itself a lint error — descriptive
    // comment is the only valid expression of intent.
  }, [state.transformActive, state.transformLayerId, canvas]);

  useEffect(() => {
    if (state.transformActive) {
      canvas.applyTransformControls(state.transformMode);
    }
  }, [state.transformMode, state.transformActive, canvas]);

  // Cycle through Retouch variants on Shift+R. Order is sourced
  // from `RETOUCH_VARIANT_ORDER` in RetouchPanel — derived from
  // the chip row, so cycle and visual order can't drift.
  const handleCycleRetouchVariant = useCallback(() => {
    const cur = state.retouchSettings.variant;
    const idx = RETOUCH_VARIANT_ORDER.indexOf(cur);
    const next = RETOUCH_VARIANT_ORDER[(idx + 1) % RETOUCH_VARIANT_ORDER.length];
    updateRetouchSettings({ variant: next });
  }, [state.retouchSettings.variant, updateRetouchSettings]);

  // Cut / Copy / Paste — toolbar buttons + Ctrl+X/C/V shortcuts. Cut and
  // Copy operate on the marqueed region of the active layer's paint
  // surface; Paste creates a new "Pasted" layer placed at the original
  // bbox so the pixels return to where they were copied from.
  const handleCopy = useCallback(() => {
    canvas.copySelection();
  }, [canvas]);

  const handleCut = useCallback(() => {
    if (canvas.cutSelection()) {
      markDirty();
      history.saveSnapshot();
    }
  }, [canvas, markDirty, history]);

  const handlePaste = useCallback(async () => {
    // 1. Fabric internal clipboard first — for in-editor cut/copy/paste of
    //    objects (text/shape/image already in the document). Lossless,
    //    preserves position so the pasted object lands at its source coords.
    const item = canvas.getClipboard();
    if (item) {
      const blob = await new Promise<Blob | null>((resolve) => {
        item.canvas.toBlob(resolve, 'image/png');
      });
      if (!blob) return;
      const layer = makeLayer('image', 'Pasted');
      addLayer(layer, true);
      const img = await canvas.addImageLayer(blob, { layerId: layer.id });
      if (img) {
        img.set({ left: item.left, top: item.top, originX: 'left', originY: 'top' });
        img.setCoords();
      }
      // Flag the snapshot effect to capture once React commits the new layer
      // state. We don't call markDirty() / history.saveSnapshot() here — the
      // pending-flag effect handles both, so doing it twice was producing
      // duplicate history entries and requiring two Ctrl+Z to undo a paste.
      layerSnapshotPendingRef.current = true;
      return;
    }

    // 2. OS clipboard fallback (e.g. screenshot from outside the editor).
    //    The native `paste` event listener below catches this for Ctrl+V
    //    in some browsers, but the toolbar Paste button — and mobile,
    //    where Ctrl+V isn't an option — depend on this path. Permission
    //    or feature unavailability is silent: failing to paste isn't
    //    surprising enough to warrant an error toast.
    if (!SUPPORTS_OS_CLIPBOARD_READ) return;
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const clipItem of clipboardItems) {
        const imageType = clipItem.types.find((t) => t.startsWith('image/'));
        if (!imageType) continue;
        const blob = await clipItem.getType(imageType);
        const layer = makeLayer('image', 'Pasted');
        addLayer(layer, true);
        await canvas.addImageLayer(blob, { layerId: layer.id });
        layerSnapshotPendingRef.current = true;
        return;
      }
      toast({
        title: 'Nothing to paste',
        description: 'No image found in the clipboard.',
      });
    } catch {
      // Permission denied (most common on first-use without a user
      // gesture) or transient read failure — no-op silently.
    }
  }, [canvas, addLayer, makeLayer]);

  // Tool switching
  const handleToolSelect = useCallback(
    (tool: EditorTool) => {
      setActiveTool(tool);
      canvas.setTool(tool, state.brushSettings);
      // Mark this tool as already-synced so the activeTool effect
      // skips a redundant `canvas.setTool` call after React commits.
      // See the lastSyncedToolRef declaration above for why this
      // matters (idempotency concerns in setTool's switch cases).
      lastSyncedToolRef.current = tool;

      // Text and Shape tools used to auto-create a placeholder
      // object on tool select. That behavior was removed because
      // it generated unwanted layers when the user picked Text /
      // Shape just to *edit* an existing object's properties (e.g.
      // selecting a shape layer, then clicking the Shape tool to
      // tweak its fill or stroke). Object creation now happens on
      // canvas click via `onShapeToolClick` / `onTextToolClick`
      // — one click selects the tool, the next click on empty
      // canvas drops the new object.

      // AI tool: open the capability picker. Always reopens on click so
      // users can switch capabilities without leaving the AI tool — the
      // sub-panel header has a "Change" button that does the same thing.
      if (tool === 'ai') {
        setAIPickerOpen(true);
      }
    },
    [setActiveTool, setAIPickerOpen, canvas, state.brushSettings],
  );

  useEffect(() => {
    canvas.setMarqueeShape(state.marqueeSettings.shape);
  }, [state.marqueeSettings.shape, canvas]);

  useEffect(() => {
    canvas.setForceSquare(state.marqueeSettings.forceSquare);
  }, [state.marqueeSettings.forceSquare, canvas]);

  useEffect(() => {
    canvas.setMarqueeColor(state.marqueeSettings.color);
  }, [state.marqueeSettings.color, canvas]);

  useEffect(() => {
    canvas.setWandSettings(state.marqueeSettings.tolerance, state.marqueeSettings.contiguous);
  }, [state.marqueeSettings.tolerance, state.marqueeSettings.contiguous, canvas]);

  // Push the active layer id into the canvas hook so new draw strokes get
  // tagged with it (and appear under that layer in the panel).
  useEffect(() => {
    canvas.setActiveLayerId(state.activeLayerId);
  }, [state.activeLayerId, canvas]);

  // Push the React-side edit-target map into the canvas hook. The
  // brush-stroke listener inside Fabric reads from a ref kept in
  // sync this way — without the push, mask painting silently
  // routes to the layer surface every time.
  useEffect(() => {
    canvas.setEditTargets(state.editTargets);
  }, [state.editTargets, canvas]);

  // Tier 1 render-pipeline integration. Phase 2 wires the
  // adjustment-layer slow path through here; mask / non-passthrough
  // group features stay deferred and surface as a dev-only warn.
  // Tier 0-shaped documents (no groups, masks, adjustments) skip
  // the compositor entirely via the `requiresCustomComposite` fast
  // path — zero perf cost for users not on Tier 1 features.
  //
  // rAF-coalesced: rapid `state.document` changes (e.g. dragging
  // a slider in non-destructive edit mode fires UPDATE_ADJUSTMENT
  // at every value change) all collapse into a single composite
  // pass per frame. The compositor walks the whole tree and does
  // ~2x full-canvas getImageData per adjustment, so synchronous
  // per-change would burn frames on any non-trivial canvas size.
  //
  // After the compositor runs, mark every adjustment layer's
  // FabricImage dirty so Fabric reads the freshly-written backing
  // canvas pixels on the next render. Without the dirty flag,
  // Fabric paints the cached version and the user doesn't see
  // the update.
  const slowPathRafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!requiresCustomComposite(state.document)) return;
    if (hasUnimplementedSlowPath(state.document)) {
      if (import.meta.env.DEV) {
        console.warn(
          '[image-editor] document needs the custom compositor; slow-path masks / groups are not yet implemented.',
        );
      }
      return;
    }
    const fabricCanvas = canvas.canvasRef.current;
    if (!fabricCanvas) return;
    // Coalesce. If an rAF is already scheduled, drop this fire —
    // the scheduled tick reads the LATEST document via the
    // documentRef (not via closure of the effect that scheduled
    // it), so rapid state.document changes all collapse into a
    // single composite pass against the freshest state.
    if (slowPathRafRef.current !== null) return;
    slowPathRafRef.current = requestAnimationFrame(() => {
      slowPathRafRef.current = null;
      const fc = canvas.canvasRef.current;
      if (!fc) return;
      const doc = documentRef.current;
      renderDocument({
        document: doc,
        width: state.canvasWidth,
        height: state.canvasHeight,
        paintLeaf: canvas.paintLeafToCanvas,
        getAdjustmentBacking: canvas.getAdjustmentBackingCanvas,
      });
      let anyDirty = false;
      for (const id in doc.layers) {
        const node = doc.layers[id];
        if (node.type !== 'adjustment') continue;
        for (const obj of fc.getObjects()) {
          const objLayerId = (obj as unknown as { layerId?: string }).layerId;
          if (objLayerId !== id) continue;
          obj.dirty = true;
          anyDirty = true;
        }
      }
      if (anyDirty) fc.requestRenderAll();
    });
  }, [state.document, state.canvasWidth, state.canvasHeight, canvas]);

  // Cancel any pending slow-path rAF on unmount. Without this, a
  // scheduled tick can fire against a torn-down canvas after
  // navigation away from the editor.
  useEffect(
    () => () => {
      if (slowPathRafRef.current !== null) {
        cancelAnimationFrame(slowPathRafRef.current);
        slowPathRafRef.current = null;
      }
    },
    [],
  );

  // Mirror mask configs into the canvas-side hook + re-materialize
  // each layer's clipPath effective alpha whenever its mask config
  // changes. Without this, mask Disable / Invert / Density / Feather
  // updates in `state.document` would never reach the Fabric
  // clipPath — Fabric's render loop honors clipPath alpha, but only
  // after `materializeMaskClipPath` has baked the config into the
  // clipPath element. Cheap when nothing changed (a stable map
  // compare per layer); identity materialize is one drawImage.
  //
  // rAF-coalesced for the same reason the slow-path effect is: a
  // future Density / Feather slider drag will fire UPDATE-style
  // dispatches on every pointer-move, and materialization with
  // non-default config does a getImageData/per-pixel/putImageData
  // round-trip (~30-80ms on 4K). Without coalescing, the per-frame
  // work would stack and jank the drag. The rAF callback reads
  // `documentRef.current` so the latest config wins when rapid
  // changes collapse into one tick.
  //
  // Tracked against `lastPushedMaskConfigsRef` so unrelated
  // `state.document` mutations (brush strokes, layer reorders,
  // etc.) don't trigger materialization for masks whose config
  // didn't change. Diff compares every LayerMask field via Object
  // .keys so a future field addition (e.g. linked semantics,
  // colorOverlay) is picked up automatically without editing the
  // effect.
  const lastPushedMaskConfigsRef = useRef<Map<string, LayerMask>>(new Map());
  const maskSyncRafRef = useRef<number | null>(null);
  useEffect(() => {
    if (maskSyncRafRef.current !== null) return;
    maskSyncRafRef.current = requestAnimationFrame(() => {
      maskSyncRafRef.current = null;
      const doc = documentRef.current;
      const liveIds = new Set<string>();
      for (const id in doc.layers) {
        const node = doc.layers[id];
        const mask = node.mask;
        if (!mask) continue;
        liveIds.add(id);
        const prev = lastPushedMaskConfigsRef.current.get(id);
        const unchanged =
          prev !== undefined &&
          (Object.keys(mask) as (keyof LayerMask)[]).every((k) => prev[k] === mask[k]);
        if (unchanged) continue;
        canvas.setMaskConfig(id, mask);
        canvas.materializeMaskClipPath(id, mask);
        lastPushedMaskConfigsRef.current.set(id, { ...mask });
      }
      // Drop entries for layers whose mask was removed since last
      // push. The canvas-side `removeLayerMask` already clears its
      // mirror; this keeps our diff-tracking accurate so a future
      // re-add starts from a clean compare.
      for (const id of Array.from(lastPushedMaskConfigsRef.current.keys())) {
        if (!liveIds.has(id)) {
          lastPushedMaskConfigsRef.current.delete(id);
          canvas.setMaskConfig(id, null);
        }
      }
    });
  }, [state.document, canvas]);

  // Cancel pending mask-sync rAF on unmount so it can't fire against
  // a torn-down canvas. Mirrors the slow-path effect's cleanup.
  useEffect(
    () => () => {
      if (maskSyncRafRef.current !== null) {
        cancelAnimationFrame(maskSyncRafRef.current);
        maskSyncRafRef.current = null;
      }
    },
    [],
  );

  // Text-path binding sync (Tier 2 Group A PR-E). Walks text layers
  // with `textPath` set; rebuilds the `fabric.IText.path` from the
  // referenced vector layer's geometry whenever either the binding
  // OR the underlying vector path changes. rAF-coalesced so a Path
  // Edit drag that fires many SET_VECTOR_PATH dispatches per second
  // collapses to one rebuild per frame.
  //
  // Auto-unbind on missing target: the reducer's REMOVE_LAYER cascade
  // already clears `textPath` when its referenced vector layer is
  // deleted, so this loop just rechecks defensively (the vector may
  // also be deleted via paths the cascade doesn't cover, e.g. a
  // future full-document replace). Missing vector → unbind via
  // `setTextPathBinding(textId, null)`.
  //
  // Track via the path-data string (NOT the VectorPath object) so
  // anchor-handle edits inside Path Edit retrigger the sync — the
  // VectorPath object identity changes on every reducer mutation,
  // but the SVG-data string captures the actual geometry, letting
  // us skip a fabric.Path rebuild when only metadata changed.
  const lastPushedTextPathRef = useRef<Map<string, { pathLayerId: string; svgData: string }>>(
    new Map(),
  );
  const textPathSyncRafRef = useRef<number | null>(null);
  useEffect(() => {
    if (textPathSyncRafRef.current !== null) return;
    textPathSyncRafRef.current = requestAnimationFrame(() => {
      textPathSyncRafRef.current = null;
      const doc = documentRef.current;
      const liveTextIds = new Set<string>();
      for (const id in doc.layers) {
        const node = doc.layers[id];
        if (node.type !== 'text') continue;
        const binding = node.textPath;
        if (!binding) continue;
        liveTextIds.add(id);
        const vectorNode = doc.layers[binding.pathLayerId];
        if (!vectorNode || vectorNode.type !== 'vector' || !vectorNode.path) {
          // Defensive unbind — reducer cascade should have handled
          // the common path, but be safe against partial state.
          canvas.setTextPathBinding(id, null);
          lastPushedTextPathRef.current.delete(id);
          continue;
        }
        const svgData = pathToSvgData(vectorNode.path);
        const prev = lastPushedTextPathRef.current.get(id);
        if (prev && prev.pathLayerId === binding.pathLayerId && prev.svgData === svgData) {
          continue;
        }
        if (canvas.setTextPathBinding(id, svgData)) {
          lastPushedTextPathRef.current.set(id, {
            pathLayerId: binding.pathLayerId,
            svgData,
          });
          if (import.meta.env.DEV) {
            console.debug(
              `[image-editor] text-path-sync: rebound ${id} → ${binding.pathLayerId}` +
                ` (${svgData.length} chars, prev=${prev ? 'set' : 'none'})`,
            );
          }
        }
      }
      // Drop entries for layers that no longer have a binding (text
      // layer removed, or textPath was set to null via Unbind).
      for (const id of Array.from(lastPushedTextPathRef.current.keys())) {
        if (!liveTextIds.has(id)) {
          canvas.setTextPathBinding(id, null);
          lastPushedTextPathRef.current.delete(id);
        }
      }
    });
  }, [state.document, canvas]);

  // Cancel pending text-path-sync rAF on unmount.
  useEffect(
    () => () => {
      if (textPathSyncRafRef.current !== null) {
        cancelAnimationFrame(textPathSyncRafRef.current);
        textPathSyncRafRef.current = null;
      }
    },
    [],
  );

  // Push the latest brush color so the Fill tool can use it on click.
  useEffect(() => {
    canvas.setBrushColor(state.brushSettings.color);
  }, [state.brushSettings.color, canvas]);

  // Live-update the active drawing brush whenever any brush setting changes,
  // so the Draw / Eraser tool's next stroke uses the current values without
  // requiring a tool re-pick.
  useEffect(() => {
    canvas.updateBrush(state.brushSettings);
  }, [state.brushSettings, canvas]);

  // Bidirectional shape-settings sync:
  //   1) When `state.activeLayerId` changes, read the active shape's actual
  //      properties and mirror them into `state.shapeSettings`. The Shape
  //      panel always shows the selected shape's properties.
  //   2) When `state.shapeSettings` changes (e.g., user moved a slider),
  //      apply those settings to the active shape.
  // Both effects use prev-refs to guard against the `canvas` dep changing
  // identity on every render (it's a fresh object literal from
  // `useFabricCanvas`). Without these guards, the sync effect dispatches on
  // every render → infinite loop. The `syncingFromCanvasRef` flag prevents
  // the apply-effect from re-applying settings just read from the canvas.
  const prevShapeSettingsRef = useRef(state.shapeSettings);
  const prevActiveLayerIdForShapeRef = useRef<string | null | undefined>(undefined);
  const syncingShapeFromCanvasRef = useRef(false);

  useEffect(() => {
    if (prevActiveLayerIdForShapeRef.current === state.activeLayerId) return;
    prevActiveLayerIdForShapeRef.current = state.activeLayerId;
    const fromCanvas = canvas.getActiveShapeSettings();
    if (!fromCanvas) return;
    syncingShapeFromCanvasRef.current = true;
    updateShapeSettings(fromCanvas);
  }, [state.activeLayerId, canvas, updateShapeSettings]);

  useEffect(() => {
    if (prevShapeSettingsRef.current === state.shapeSettings) return;
    prevShapeSettingsRef.current = state.shapeSettings;
    if (syncingShapeFromCanvasRef.current) {
      // The latest shapeSettings update came from reading the active shape;
      // skip the round-trip apply.
      syncingShapeFromCanvasRef.current = false;
      return;
    }
    canvas.updateActiveShape(state.shapeSettings);
    // Vector primitives (polygon / star / arrow) regenerate live
    // when their parameters change. The marker `vectorSource` on
    // the layer prevents this from clobbering pen-drawn paths —
    // those have no source kind and are left untouched. We only
    // regenerate when the slider's primitive kind matches the
    // layer's source kind (so switching the chip from polygon to
    // rect doesn't turn a polygon into a degenerate rect; the
    // user deletes + recreates for cross-primitive type changes).
    if (state.activeLayerId) {
      const node = state.document.layers[state.activeLayerId];
      if (node?.type === 'vector' && node.path) {
        if (shouldRegenerateVectorPrimitive(node, state.shapeSettings.type)) {
          // Geometry regenerate (and style — generateVectorShapePath
          // bakes stroke + fill from settings into the new path).
          // Only fires for vector primitives whose marker matches
          // the active chip; pen-drawn paths and custom-edited
          // primitives skip this branch.
          const old = canvas.getActiveVectorBounds(state.activeLayerId);
          if (old) {
            // Use the min half-axis so a non-uniformly-scaled
            // primitive doesn't get stretched further on regen;
            // the visual size matches the layer's current shorter
            // axis. Width/height in `bbox` are already in
            // scene-space coords thanks to `getBoundingRect`.
            const halfExtent = Math.max(1, Math.min(old.bbox.width, old.bbox.height) / 2);
            const path = generateVectorShapePath(state.shapeSettings, old.center, halfExtent);
            setVectorPath(state.activeLayerId, path);
            canvas.replaceVectorObject(state.activeLayerId, path);
            layerSnapshotContinuousRef.current = true;
          }
        } else {
          // Style-only sync — applies to ANY vector layer (pen-
          // drawn paths, custom-edited primitives, and primitives
          // whose chip doesn't match the layer's source kind).
          // Geometry stays put; stroke / fill / width update on
          // both the canvas object and the document path.
          const stroke = {
            color: state.shapeSettings.stroke,
            width: state.shapeSettings.strokeWidth,
          };
          const fill = state.shapeSettings.fill;
          const styleChanged =
            node.path.stroke?.color !== stroke.color ||
            node.path.stroke?.width !== stroke.width ||
            node.path.fill !== fill;
          if (styleChanged) {
            canvas.updateVectorStyle(state.activeLayerId, {
              stroke: stroke.color,
              strokeWidth: stroke.width,
              fill,
            });
            setVectorPath(state.activeLayerId, { ...node.path, stroke, fill });
            layerSnapshotContinuousRef.current = true;
          }
        }
      }
    }
  }, [state.shapeSettings, state.activeLayerId, state.document, canvas, setVectorPath]);

  // Same bidirectional sync for the Text tool's settings.
  const prevTextSettingsRef = useRef(state.textSettings);
  const prevActiveLayerIdForTextRef = useRef<string | null | undefined>(undefined);
  const syncingTextFromCanvasRef = useRef(false);

  useEffect(() => {
    if (prevActiveLayerIdForTextRef.current === state.activeLayerId) return;
    prevActiveLayerIdForTextRef.current = state.activeLayerId;
    const fromCanvas = canvas.getActiveTextSettings();
    if (!fromCanvas) return;
    syncingTextFromCanvasRef.current = true;
    updateTextSettings(fromCanvas);
  }, [state.activeLayerId, canvas, updateTextSettings]);

  useEffect(() => {
    if (prevTextSettingsRef.current === state.textSettings) return;
    prevTextSettingsRef.current = state.textSettings;
    if (syncingTextFromCanvasRef.current) {
      syncingTextFromCanvasRef.current = false;
      return;
    }
    canvas.updateActiveText(state.textSettings);
  }, [state.textSettings, canvas]);

  // Clipboard paste — extracts an image from the OS clipboard and creates a new image layer.
  useEffect(() => {
    const onPaste = async (e: ClipboardEvent) => {
      // Skip if focus is in an input/textarea (the user is pasting text into a form)
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      if (!e.clipboardData) return;
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (!blob) continue;
          e.preventDefault();
          const layer = makeLayer('image');
          addLayer(layer, true);
          await canvas.addImageLayer(blob, { layerId: layer.id });
          layerSnapshotPendingRef.current = true;
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [canvas, addLayer, makeLayer]);

  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    void (async () => {
      const session = await loadSession();
      if (!session) return;
      // Seed the session as the new tab's initial snapshot. The tab-switch
      // effect (below) will pick it up on the next render and call
      // applySnapshot — this single source of truth avoids racing
      // resetEditorToEmpty when the effect fires concurrently with a
      // direct applySnapshot call here.
      tabs.addTab({
        fileName: session.fileName,
        fileId: session.fileId,
        snapshot: session,
      });
    })();
  }, []);

  // Tab switching. When the active tab id changes, save the outgoing tab's
  // editor state and apply the incoming tab's snapshot (or reset the editor
  // for a freshly-created tab with no snapshot yet).
  //
  // We list every closed-over value as a dep, but the body short-circuits
  // on `prevId === newId` so non-id changes (e.g. setTabSnapshot updating
  // tabs.tabs) cost only the dep comparison.
  const prevActiveTabIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prevId = prevActiveTabIdRef.current;
    const newId = tabs.activeTabId;
    if (prevId === newId) return;
    prevActiveTabIdRef.current = newId;

    void (async () => {
      // 1. Capture outgoing tab's state.
      if (prevId !== null && hasImage) {
        const outgoing = buildSnapshot();
        if (outgoing) tabs.setTabSnapshot(prevId, outgoing);
      }

      // 2. Restore incoming tab.
      if (newId === null) {
        // No active tab — last tab was closed. Wipe the persisted
        // session so a refresh starts empty instead of restoring the
        // image the user just closed (and possibly inheriting any
        // half-baked Fabric state from a stale snapshot, which has
        // been observed to break the marquee + move tools on the next
        // open).
        void clearSession();
        resetEditorToEmpty();
        return;
      }
      const incoming = tabs.tabs.find((t) => t.id === newId);
      if (incoming?.snapshot) {
        await applySnapshot(incoming.snapshot);
        // Snapshot is now live in the editor — clear it so the inactive
        // copy doesn't drift.
        tabs.setTabSnapshot(newId, null);
      } else {
        // Freshly-created tab with no content yet. Reset and wait for
        // loadFile / handleFiasImageSelected to populate the editor.
        resetEditorToEmpty();
      }
    })();
  }, [
    tabs.activeTabId,
    tabs.tabs,
    tabs.setTabSnapshot,
    hasImage,
    buildSnapshot,
    applySnapshot,
    resetEditorToEmpty,
  ]);

  // Mirror file-related editor state onto the active tab so the tab label
  // reflects renames + dirty status without the user opening the tab.
  useEffect(() => {
    if (!tabs.activeTabId) return;
    tabs.updateActiveTabMetadata({
      fileName: state.fileName,
      fileId: state.fileId,
      isDirty: state.isDirty,
    });
  }, [state.fileName, state.fileId, state.isDirty, tabs.activeTabId]);

  // Debounced session save. Keyed on canvas mutations (history counts), file
  // metadata, filters, and zoom — so the user's last view (zoom + pan) is
  // preserved when paired with any other change. Persists only the active
  // tab's state — multi-tab persistence is a planned follow-up.
  useEffect(() => {
    if (!hasImage) return;
    const timer = setTimeout(() => {
      const snapshot = buildSnapshot();
      if (snapshot) void saveSession(snapshot);
    }, 800);
    return () => clearTimeout(timer);
  }, [
    buildSnapshot,
    hasImage,
    state.fileName,
    state.fileId,
    state.zoom,
    state.layers,
    state.activeLayerId,
    history.undoCount,
    history.redoCount,
  ]);

  // Refund toast on AI failure. invokeEntity refunds reserved credits
  // server-side whenever the executor errors, so any aiStatus error
  // means the user has been made whole. We surface that explicitly so
  // a failed generation doesn't feel like a lost charge. Fires once
  // per error: each setAiStatus({ kind: 'error', ... }) creates a new
  // object reference, so the effect runs on every fresh failure even
  // if the message string is identical.
  useEffect(() => {
    if (aiStatus.kind === 'error') {
      toast({
        title: 'Generation failed',
        description: `${aiStatus.message} — any credits reserved have been refunded.`,
        variant: 'destructive',
      });
    }
  }, [aiStatus]);

  // Keyboard shortcuts. Stash the dependent handlers in a ref so the
  // window listener can be registered exactly once with `[]` deps —
  // otherwise it would tear down + re-attach on every tool click (which
  // toggles state.activeTool, which used to be a dep), creating a tiny
  // window where keypresses are dropped.
  const keyHandlersRef = useRef({
    undo: () => {
      flushContinuousSnapshot();
      history.undoCanvas();
    },
    redo: () => {
      flushContinuousSnapshot();
      history.redoCanvas();
    },
    deleteSelected: () => canvas.deleteSelected(),
    selectAll: () => canvas.selectAll(),
    invertSelection: handleInvertSelection,
    openSaveAs: () => setExportDialogOpen(true),
    copy: handleCopy,
    cut: handleCut,
    paste: handlePaste,
    applyCrop: handleApplyCrop,
    selectTool: handleToolSelect,
    cycleRetouchVariant: handleCycleRetouchVariant,
    activeTool: state.activeTool,
    // Free Transform — Tier 1 Group E Phase 1. `beginTransform`
    // is a no-op when there's no active layer (the canvas hook
    // bails out cleanly).
    beginTransform: () => {
      if (state.activeLayerId) handleBeginTransform(state.activeLayerId);
    },
    applyTransform: handleApplyTransform,
    cancelTransform: handleCancelTransform,
    transformActive: state.transformActive,
    beginLiquify: () => {
      if (state.activeLayerId) handleBeginLiquify(state.activeLayerId);
    },
    pathEditActive: state.pathEditActive,
    applyPathEdit: handleApplyPathEdit,
    cancelPathEdit: handleCancelPathEdit,
  });
  keyHandlersRef.current = {
    undo: () => {
      flushContinuousSnapshot();
      history.undoCanvas();
    },
    redo: () => {
      flushContinuousSnapshot();
      history.redoCanvas();
    },
    deleteSelected: () => canvas.deleteSelected(),
    selectAll: () => canvas.selectAll(),
    invertSelection: handleInvertSelection,
    openSaveAs: () => setExportDialogOpen(true),
    copy: handleCopy,
    cut: handleCut,
    paste: handlePaste,
    applyCrop: handleApplyCrop,
    selectTool: handleToolSelect,
    cycleRetouchVariant: handleCycleRetouchVariant,
    activeTool: state.activeTool,
    beginTransform: () => {
      if (state.activeLayerId) handleBeginTransform(state.activeLayerId);
    },
    applyTransform: handleApplyTransform,
    cancelTransform: handleCancelTransform,
    transformActive: state.transformActive,
    beginLiquify: () => {
      if (state.activeLayerId) handleBeginLiquify(state.activeLayerId);
    },
    pathEditActive: state.pathEditActive,
    applyPathEdit: handleApplyPathEdit,
    cancelPathEdit: handleCancelPathEdit,
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      const h = keyHandlersRef.current;
      const ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        h.undo();
      } else if (ctrl && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        h.redo();
      } else if (ctrl && e.key === 'y') {
        e.preventDefault();
        h.redo();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        h.deleteSelected();
      } else if (ctrl && e.key === 'a') {
        e.preventDefault();
        h.selectAll();
      } else if (ctrl && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
        h.invertSelection();
      } else if (ctrl && e.key === 's') {
        e.preventDefault();
        h.openSaveAs();
      } else if (ctrl && e.key === 'c') {
        e.preventDefault();
        h.copy();
      } else if (ctrl && e.key === 'x') {
        e.preventDefault();
        h.cut();
      } else if (ctrl && e.key === 'v') {
        e.preventDefault();
        void h.paste();
      } else if (e.key === 'Enter' && h.activeTool === 'crop') {
        e.preventDefault();
        h.applyCrop();
      } else if (ctrl && (e.key === 't' || e.key === 'T') && !e.shiftKey) {
        // Free Transform on the active layer (Tier 1 Group E).
        // Bare 't' is already the Text tool shortcut — Ctrl/Cmd+T
        // for Transform matches Photoshop and avoids the clash.
        e.preventDefault();
        h.beginTransform();
      } else if (h.transformActive && (e.key === 'Enter' || e.key === 'Escape')) {
        // Apply on Enter / Cancel on Escape while in Transform.
        // Mirrors the Crop tool's Enter-applies convention.
        e.preventDefault();
        if (e.key === 'Enter') h.applyTransform();
        else h.cancelTransform();
      } else if (ctrl && e.shiftKey && (e.key === 'x' || e.key === 'X')) {
        // Liquify on the active layer (Tier 1 Group E final piece).
        // Matches Photoshop's Shift+Ctrl+X shortcut. Ctrl+X alone is
        // Cut; the Shift modifier is the distinguisher.
        e.preventDefault();
        h.beginLiquify();
      } else if (h.activeTool === 'pen' && e.key === 'Enter') {
        // Finalize the in-progress pen path as open (Tier 2 Group A).
        // Mirrors the Crop / Transform Enter-applies convention.
        e.preventDefault();
        canvas.finalizePenPath(false);
      } else if (h.activeTool === 'pen' && e.key === 'Escape') {
        // Cancel — discards the in-progress path entirely; no
        // history entry. Switching away from the pen tool also
        // calls cancelPenPath via the activeTool mirror effect.
        e.preventDefault();
        canvas.cancelPenPath();
      } else if (h.pathEditActive && e.key === 'Enter') {
        // Apply Path Edit (Tier 2 Group A PR-B).
        e.preventDefault();
        h.applyPathEdit();
      } else if (h.pathEditActive && e.key === 'Escape') {
        // Cancel Path Edit — restores the snapshot.
        e.preventDefault();
        h.cancelPathEdit();
      }

      // Tool shortcuts
      if (!ctrl && !e.shiftKey) {
        const toolMap: Record<string, EditorTool> = {
          v: 'move',
          m: 'marquee',
          c: 'crop',
          b: 'draw',
          t: 'text',
          u: 'shape',
          e: 'eraser',
          g: 'fill',
          r: 'retouch',
          a: 'ai',
        };
        const tool = toolMap[e.key.toLowerCase()];
        if (tool) h.selectTool(tool);
      }

      // Shift+R cycles Retouch variants — but ONLY when the user
      // is already on the Retouch tool. Without the gating
      // condition, Shift+R would steal the modifier-letter combo
      // from text input (no plain-letter shortcut path is the
      // default for the editor; tools are bare letters above).
      if (!ctrl && e.shiftKey && (e.key === 'r' || e.key === 'R') && h.activeTool === 'retouch') {
        e.preventDefault();
        h.cycleRetouchVariant();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close the close-after-export tab if one was set when Save-As triggered
  // this export. Captured to a local var first so the ref can be cleared
  // immediately, preventing a re-trigger if the dialog is reopened later.
  const consumeCloseAfterExport = useCallback(() => {
    const id = closeAfterExportTabIdRef.current;
    closeAfterExportTabIdRef.current = null;
    if (id) tabs.closeTab(id);
  }, [tabs]);

  const handleExportLocal = useCallback(
    async (
      fileName: string,
      format: 'png' | 'jpeg' | 'webp',
      quality: number,
      target?: { width?: number; height?: number },
    ) => {
      await exportToLocal(fileName, format, quality, target);
      markClean();
      consumeCloseAfterExport();
    },
    [exportToLocal, markClean, consumeCloseAfterExport],
  );

  const handleExportFias = useCallback(
    async (settings: ExportSettings, fileName: string) => {
      const dataUrl = canvas.exportToDataURL(settings.format, settings.quality / 100);
      const result = await exportToFias({
        sourceDataUrl: dataUrl,
        operations: [],
        output: {
          format: settings.format,
          quality: settings.quality,
          width: settings.width,
          height: settings.height,
          maintainAspectRatio: settings.maintainAspectRatio,
        },
        saveAs: { fileName },
      });
      if (result) {
        markClean();
        consumeCloseAfterExport();
      }
    },
    [canvas, exportToFias, markClean, consumeCloseAfterExport],
  );

  // Copy-to-clipboard / native-share export. On mobile (coarse pointer
  // + Web Share API with files), prefer the native share sheet — that's
  // the affordance users expect for "send this image to another app."
  // Desktop falls back to navigator.clipboard.write with a PNG blob.
  // Errors surface as a toast so the user knows nothing happened, since
  // there's otherwise no visible feedback for an in-place clipboard op.
  const handleExportClipboard = useCallback(
    async (
      fileName: string,
      format: 'png' | 'jpeg' | 'webp',
      quality: number,
      target?: { width?: number; height?: number },
    ) => {
      const blob = await canvas.exportToBlob(format, quality, target);
      if (!blob) {
        toast({
          title: 'Export failed',
          description: 'Could not generate image data.',
          variant: 'destructive',
        });
        return;
      }

      const isCoarse =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(pointer: coarse)').matches;
      const canShareFile =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function';

      // Mobile share sheet path.
      if (isCoarse && canShareFile) {
        try {
          const file = new File([blob], `${fileName}.${format}`, { type: blob.type });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: fileName });
            markClean();
            consumeCloseAfterExport();
            return;
          }
        } catch (err) {
          // User cancellation throws AbortError — silent. Other errors
          // fall through to clipboard fallback.
          if (err instanceof Error && err.name === 'AbortError') return;
        }
      }

      // Desktop / fallback: write blob to OS clipboard.
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.clipboard?.write === 'function' &&
        typeof ClipboardItem !== 'undefined'
      ) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
          toast({ title: 'Copied to clipboard' });
          markClean();
          consumeCloseAfterExport();
          return;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Copy failed';
          toast({
            title: 'Copy failed',
            description:
              format !== 'png'
                ? 'Most browsers only support PNG on the clipboard. Try exporting as PNG.'
                : message,
            variant: 'destructive',
          });
          return;
        }
      }

      toast({
        title: 'Not supported',
        description: 'Your browser does not support copying images to the clipboard.',
        variant: 'destructive',
      });
    },
    [canvas, markClean, consumeCloseAfterExport],
  );

  // Prefer the File System Access API so a later Save can overwrite the
  // original file in place. In Firefox / Safari (no FSA), fall back to the
  // hidden `<input type="file">` — that path can't capture a writable
  // handle, so Save will degrade to a download.
  const pickAndOpenLocalFile = useCallback(async () => {
    type ShowOpenFilePicker = (opts?: {
      types?: { description: string; accept: Record<string, string[]> }[];
      multiple?: boolean;
      excludeAcceptAllOption?: boolean;
    }) => Promise<FileSystemFileHandle[]>;
    const picker = (window as unknown as { showOpenFilePicker?: ShowOpenFilePicker })
      .showOpenFilePicker;
    if (!picker) {
      fileInputRef.current?.click();
      return;
    }
    try {
      const [handle] = await picker({
        types: [
          {
            description: 'Image',
            accept: {
              'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'],
            },
          },
        ],
        multiple: false,
      });
      if (!handle) return;
      const file = await handle.getFile();
      loadFile(file, handle);
    } catch (err) {
      // User dismissed the picker (AbortError) — silently abort.
      // Anything else is unexpected; surface to the console for debugging
      // without breaking the UI.
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        console.warn('[image-editor] showOpenFilePicker failed:', err);
      }
    }
  }, [loadFile]);

  const handleOpenFile = useCallback(() => {
    void pickAndOpenLocalFile();
  }, [pickAndOpenLocalFile]);

  const handleOpenFias = useCallback(() => {
    setFiasPickerOpen(true);
  }, []);

  // File menu — `New` creates a blank 1024×1024 white canvas in a new tab,
  // routed through loadFile so it follows the same per-tab plumbing as a
  // user-opened image.
  const handleNewBlankFile = useCallback(() => {
    const W = 1024;
    const H = 1024;
    const offscreen = document.createElement('canvas');
    offscreen.width = W;
    offscreen.height = H;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    offscreen.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'Untitled.png', { type: 'image/png' });
      loadFile(file);
    }, 'image/png');
  }, [loadFile]);

  /** Save the current canvas to either Local or Fias, awaiting completion.
   *  Used by the toolbar Save handler and the close-confirmation Save
   *  action. For local saves, prefers a stored FileSystemFileHandle
   *  (overwrites the original file in place); falls back to a download in
   *  browsers without the File System Access API. Returns false on failure
   *  and surfaces the underlying error via `saveError` for the caller to
   *  display. */
  const quickSaveCurrent = useCallback(
    async (destination: SaveDestination): Promise<boolean> => {
      const fileName = state.fileName || 'Untitled';
      setSaveError(null);
      try {
        if (destination === 'local') {
          const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
          const handle = activeTab?.fileHandle ?? null;
          if (handle) {
            const blob = await canvas.exportToBlob('png', 1);
            if (!blob) {
              setSaveError('Failed to encode the canvas as PNG.');
              return false;
            }
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
          } else {
            await exportToLocal(fileName, 'png', 1);
          }
        } else {
          const dataUrl = canvas.exportToDataURL('png', 1);
          const result = await exportToFias({
            sourceDataUrl: dataUrl,
            operations: [],
            output: { format: 'png' },
            saveAs: { fileName },
          });
          if (!result) {
            setSaveError('Failed to upload to Fias storage.');
            return false;
          }
        }
        markClean();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Save failed.';
        setSaveError(message);
        console.warn('[image-editor] save failed:', err);
        return false;
      }
    },
    [state.fileName, exportToLocal, exportToFias, canvas, markClean, tabs.tabs, tabs.activeTabId],
  );

  // In-place save (toolbar Save / Ctrl+S / file menu Save). Routes through
  // quickSaveCurrent (which uses the FSA handle for local or processImage
  // for Fias). Falls back to Save As when neither destination is known —
  // the very first save of a brand-new doc, for example.
  const handleToolbarSave = useCallback(async () => {
    if (!tabs.activeTabId) return;
    const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
    if (!activeTab) return;
    const canSaveInPlace = activeTab.fileId !== null || activeTab.fileHandle !== null;
    if (!canSaveInPlace) {
      setExportDialogDestination(activeTab.fileId ? 'fias' : 'local');
      setExportDialogOpen(true);
      return;
    }
    const destination: SaveDestination = activeTab.fileId ? 'fias' : 'local';
    await quickSaveCurrent(destination);
  }, [tabs.activeTabId, tabs.tabs, quickSaveCurrent]);

  const handleSaveAs = useCallback(() => {
    const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
    setExportDialogDestination(activeTab?.fileId ? 'fias' : 'local');
    setExportDialogOpen(true);
  }, [tabs.activeTabId, tabs.tabs]);

  // Tab bar handlers
  const handleTabSwitch = useCallback(
    (id: string) => {
      tabs.setActiveTabId(id);
    },
    [tabs],
  );

  const handleTabClose = useCallback(
    (id: string) => {
      // Don't open a second-level dialog if the export dialog or the
      // close-confirm dialog is already mounted — that creates an
      // ambiguous "what does Save-As close?" race window.
      if (exportDialogOpen || confirmCloseTabId !== null) return;
      const tab = tabs.tabs.find((t) => t.id === id);
      if (!tab) return;
      if (!tab.isDirty) {
        tabs.closeTab(id);
        return;
      }
      // Switch to the dirty tab first so the canvas reflects its content
      // when the user is asked. The tab-switch effect will load its
      // snapshot (if it has one).
      if (tabs.activeTabId !== id) tabs.setActiveTabId(id);
      setConfirmCloseTabId(id);
    },
    [tabs, exportDialogOpen, confirmCloseTabId],
  );

  /** File menu — Close the active tab. Handles the dirty-confirm flow via
   *  the same path as clicking the X on the tab. */
  const handleCloseActiveFile = useCallback(() => {
    if (tabs.activeTabId) handleTabClose(tabs.activeTabId);
  }, [tabs.activeTabId, handleTabClose]);

  const handleConfirmCancel = useCallback(() => {
    setConfirmCloseTabId(null);
    setSaveError(null);
  }, []);

  const handleConfirmDiscard = useCallback(() => {
    if (confirmCloseTabId) tabs.closeTab(confirmCloseTabId);
    setConfirmCloseTabId(null);
    setSaveError(null);
  }, [confirmCloseTabId, tabs]);

  const handleConfirmSave = useCallback(async () => {
    if (!confirmCloseTabId) return;
    const tab = tabs.tabs.find((t) => t.id === confirmCloseTabId);
    // Save back to wherever the file came from. Fias if it has a fileId,
    // local otherwise — overrides happen in the Save-As dialog, not here.
    const destination: SaveDestination = tab?.fileId ? 'fias' : 'local';
    setIsQuickSaving(true);
    const ok = await quickSaveCurrent(destination);
    setIsQuickSaving(false);
    if (!ok) return; // Leave the dialog open so the user can retry / Save As.
    tabs.closeTab(confirmCloseTabId);
    setConfirmCloseTabId(null);
  }, [confirmCloseTabId, tabs, quickSaveCurrent]);

  const handleConfirmSaveAs = useCallback(() => {
    if (!confirmCloseTabId) return;
    // Stash the tab id so the export handlers can close the tab after a
    // successful export, then dismiss the confirm dialog and open the full
    // Save As / Export dialog with the tab's original destination prefilled.
    const tab = tabs.tabs.find((t) => t.id === confirmCloseTabId);
    closeAfterExportTabIdRef.current = confirmCloseTabId;
    setExportDialogDestination(tab?.fileId ? 'fias' : 'local');
    setConfirmCloseTabId(null);
    setExportDialogOpen(true);
  }, [confirmCloseTabId, tabs.tabs]);

  const handleExportDialogClose = useCallback(() => {
    setExportDialogOpen(false);
    // Drop any pending close-after-export request — set when Save-As was
    // chosen but cleared whether the user cancelled OR the export already
    // ran (the export handlers fire BEFORE onClose, so by the time we get
    // here a successful save has already closed the tab).
    closeAfterExportTabIdRef.current = null;
  }, []);

  const handleNewTab = useCallback(() => {
    // Same as toolbar Open — funnel through the file picker so the user
    // immediately picks what to open. (A blank tab waiting for content
    // would be confusing; both entry points go through loadFile.)
    void pickAndOpenLocalFile();
  }, [pickAndOpenLocalFile]);

  const handleFiasImageSelected = useCallback(
    async (file: { fileId: string; fileName: string; dataUrl: string }) => {
      const displayName = file.fileName.replace(/\.[^.]+$/, '');
      // Always open in a new tab (matches loadFile behavior).
      tabs.addTab({ fileName: displayName, fileId: file.fileId });
      const fitZoom = await canvas.loadImage(file.dataUrl);
      const bgLayer: Layer = {
        id: nextLayerId(),
        name: 'Background',
        type: 'image',
        visible: true,
        parentId: null,
      };
      canvas.setBackgroundLayerId(bgLayer.id);
      resetLayers([bgLayer], bgLayer.id);
      setZoom(fitZoom);
      setFileInfo(displayName, file.fileId);
      setHasImage(true);
      markClean();
      // See the comment in `loadFile` — direct saveSnapshot here would
      // capture the layers list before React commits resetLayers, so
      // the first undo wipes the Background layer. Use the clean-pending
      // flag so the loaded file isn't marked dirty.
      history.clearHistory();
      cleanSnapshotPendingRef.current = true;
    },
    [canvas, setZoom, setFileInfo, markClean, history, tabs, nextLayerId, resetLayers],
  );

  const handleRemoveBackground = useCallback(async () => {
    setRemoveBackgroundError(null);
    setIsRemovingBackground(true);
    try {
      // imgly path: hook handles the imgly call internally — no override.
      // Paid path: hand the layer's source blob to a processor that hits
      // the backend route, then return the resulting transparent PNG so
      // the hook can swap it onto the active layer (preserving placement).
      if (removeBackgroundEngine === 'imgly') {
        await canvas.removeBackground();
      } else {
        await canvas.removeBackground({
          processBlob: async (sourceBlob) => {
            const sourceDataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
              reader.readAsDataURL(sourceBlob);
            });
            const response = await api.smartRemoveBackgroundImage({
              imageDataUrl: sourceDataUrl,
              engine: removeBackgroundEngine,
            });
            const resultResponse = await fetch(response.imageDataUrl);
            return await resultResponse.blob();
          },
        });
      }
      markDirty();
      history.saveSnapshot();
    } catch (err) {
      setRemoveBackgroundError(err instanceof Error ? err.message : 'Failed to remove background');
    } finally {
      setIsRemovingBackground(false);
    }
  }, [canvas, markDirty, history, removeBackgroundEngine]);

  const handleConvertGrayscale = useCallback(() => {
    // The hook fires onObjectModified internally on success, which routes
    // through our useFabricCanvas options to markDirty + saveSnapshot.
    canvas.convertToGrayscale();
  }, [canvas]);

  // Adjustments modal lifecycle. Picking a kind from the picker
  // captures the active-layer paint target (or background fallback);
  // the modal then drives live preview by re-applying the kind's
  // spec to the cached source on every value change. Apply commits
  // + snapshots; Cancel reverts via a putImageData(source) blit.
  // No history entry is created when the user cancels.
  // Tracks the deferred-clear timer so a quick close→reopen cycle
  // doesn't let the previous close's `setAdjustmentTargets([])` fire
  // AFTER the new open populated targets — which would silently wipe
  // them and leave a blank dialog. Cleared at the top of each open.
  const adjustmentClearTimerRef = useRef<number | null>(null);

  const handleOpenAdjustment = useCallback(
    (kind: AdjustmentKind) => {
      const targets = canvas.getActiveAdjustmentTargets(state.document);
      if (targets.length === 0) return;
      // Cancel any pending clear from a recent close.
      if (adjustmentClearTimerRef.current !== null) {
        window.clearTimeout(adjustmentClearTimerRef.current);
        adjustmentClearTimerRef.current = null;
      }
      setAdjustmentTargets(targets);
      // Sync the fabric-objs ref alongside the state so preview-tick
      // (read through the ref) doesn't lag a render behind. Updating
      // here at the lifecycle boundary avoids a render-phase side
      // effect that would re-run on every parent re-render.
      adjustmentFabricObjsRef.current = targets.map((t) => t.fabricObj);
      // Picker-driven opens are always destructive — clear any
      // stale edit-mode state from a recent close→reopen cycle.
      setEditingAdjustment(null);
      setAdjustmentSessionId((n) => n + 1);
      setActiveAdjustmentKind(kind);
    },
    [canvas, state.document],
  );

  // Derived from React state alone (cheap; doesn't need to inspect
  // Fabric objects). Drives the picker's disabled state — when the
  // active layer is text/shape/adjustment/empty-group, opening the
  // picker would no-op. Better to surface that up front than to let
  // the user click and see nothing happen. Disabled when no layer
  // is selected too (matches the resolver, which requires
  // `activeLayerId`).
  const canAdjustActiveLayer = useMemo(
    () => hasRasterizableTarget(state.document, state.activeLayerId),
    [state.document, state.activeLayerId],
  );

  // Live targets' fabric objects, kept in a ref so the preview-tick
  // callback's identity doesn't change with the state. Updated at
  // open + close lifecycle boundaries — NOT in render phase, which
  // would re-run on every parent re-render and regenerate the array
  // even when the dialog isn't open.
  const adjustmentFabricObjsRef = useRef<fabric.FabricObject[]>([]);

  // Memoized `canvas` array passed to AdjustmentsModal. Without
  // this, the inline `.map` allocates a fresh array on every
  // EditorPage render — and EditorPage has many state hooks that
  // change frequently (cursor position, AI status, viewport, etc.).
  // The body's source-capture effect deps on this prop; a fresh
  // ref re-fires the effect, which re-runs `getImageData` per
  // target plus the full-res→preview-size canvas downscale on
  // every parent render. On a 4K image that's ~30 MB allocated
  // and a 4K→768 stretch per render — the lag the user was seeing.
  const adjustmentTargetCanvases = useMemo(
    () => adjustmentTargets.map((t) => t.canvas),
    [adjustmentTargets],
  );

  // Tell Fabric the backing pixels of every adjustment target
  // changed so the next render reads the fresh image data instead
  // of cached. Called by the modal after every preview re-paint and
  // after the cancel-repaint-source pass.
  /** Synchronous slow-path re-composite. The destructive preview
   *  modal writes new pixels into the source layer's canvas at
   *  every slider tick, but `state.document` doesn't change — so
   *  the rAF-coalesced slow-path effect doesn't re-fire on its
   *  own. Without this, every adjustment layer above the source
   *  would keep showing its pre-edit backing pixels, occluding
   *  the live preview. Running synchronously inside the modal's
   *  preview-tick callback (itself already rAF-throttled to one
   *  pass per frame) keeps total per-frame work bounded; the
   *  per-pixel mask blend was replaced with GPU composite ops so
   *  the slow-path itself completes in a few ms even on 4K. */
  const runSlowPathCompositeSync = useCallback(() => {
    const fc = canvas.canvasRef.current;
    if (!fc) return;
    if (!requiresCustomComposite(state.document)) return;
    if (hasUnimplementedSlowPath(state.document)) return;
    renderDocument({
      document: state.document,
      width: state.canvasWidth,
      height: state.canvasHeight,
      paintLeaf: canvas.paintLeafToCanvas,
      getAdjustmentBacking: canvas.getAdjustmentBackingCanvas,
    });
    for (const id in state.document.layers) {
      const node = state.document.layers[id];
      if (node.type !== 'adjustment') continue;
      for (const obj of fc.getObjects()) {
        const objLayerId = (obj as unknown as { layerId?: string }).layerId;
        if (objLayerId !== id) continue;
        obj.dirty = true;
      }
    }
    fc.requestRenderAll();
  }, [canvas, state.document, state.canvasWidth, state.canvasHeight]);

  const handleAdjustmentPreviewTick = useCallback(() => {
    const fc = canvas.canvasRef.current;
    if (!fc) return;
    for (const obj of adjustmentFabricObjsRef.current) {
      obj.dirty = true;
    }
    fc.requestRenderAll();
    // Schedule the slow-path on the NEXT animation frame instead of
    // running it inline. The modal's own preview rAF is what called
    // us; running the heavy compositor synchronously here would
    // stretch a single frame to (source-apply + slow-path) which on
    // 4K with stacked adjustments visibly stutters the slider. By
    // deferring one frame the source updates immediately and the
    // cumulative composite catches up next frame — imperceptible
    // delay (16ms), no main-thread stall. Coalesces with the
    // state.document-driven scheduling so rapid ticks collapse.
    if (slowPathRafRef.current !== null) return;
    slowPathRafRef.current = requestAnimationFrame(() => {
      slowPathRafRef.current = null;
      runSlowPathCompositeSync();
    });
  }, [canvas, runSlowPathCompositeSync]);

  const handleAdjustmentCommit = useCallback(() => {
    history.saveSnapshot();
    markDirty();
  }, [history, markDirty]);

  // Close: clear `kind` immediately so the open prop flips false and
  // radix plays its close animation. Defer clearing the targets +
  // freeing the source ImageDatas (held in body state) until after
  // the animation finishes — clearing synchronously would render the
  // modal with empty props mid-animation and lose its content.
  // The deferred clear is tracked via a ref so a quick close→reopen
  // can cancel it before it stomps on the next session's targets.
  const handleAdjustmentClose = useCallback(() => {
    setActiveAdjustmentKind(null);
    adjustmentClearTimerRef.current = window.setTimeout(() => {
      adjustmentClearTimerRef.current = null;
      setAdjustmentTargets([]);
      adjustmentFabricObjsRef.current = [];
      setEditingAdjustment(null);
    }, 250);
  }, []);

  /** User tapped an adjustment-layer thumb in the LayersPanel.
   *  Open the modal in non-destructive edit mode pre-filled with
   *  the layer's current config. `editingAdjustment` carries the
   *  original so Cancel can revert via one final
   *  `UPDATE_ADJUSTMENT`. */
  const handleEditAdjustmentLayer = useCallback((layerId: string) => {
    const node = documentRef.current.layers[layerId];
    if (!node || node.type !== 'adjustment' || !node.adjustment) return;
    // Cancel any pending close-side cleanup from a recent close.
    if (adjustmentClearTimerRef.current !== null) {
      window.clearTimeout(adjustmentClearTimerRef.current);
      adjustmentClearTimerRef.current = null;
    }
    setEditingAdjustment({ layerId, original: node.adjustment });
    // Edit mode bypasses the destructive preview pipeline — no
    // target canvases to populate.
    setAdjustmentTargets([]);
    adjustmentFabricObjsRef.current = [];
    setAdjustmentSessionId((n) => n + 1);
    setActiveAdjustmentKind(node.adjustment.kind);
  }, []);

  /** Edit-mode live preview: every value change in the modal
   *  dispatches an `UPDATE_ADJUSTMENT` against the editing layer.
   *  The slow-path effect on `state.document` re-runs the
   *  compositor and the user sees the new config. */
  const handleAdjustmentLiveEdit = useCallback(
    (config: AdjustmentConfig) => {
      if (!editingAdjustment) return;
      updateAdjustment(editingAdjustment.layerId, config);
    },
    [editingAdjustment, updateAdjustment],
  );

  /** User toggled "Apply as adjustment layer" + clicked Apply.
   *  Body already reverted the destructive preview pixels — our
   *  job is to:
   *  1. Add the adjustment node immediately above the active
   *     layer, marked `clipToBelow: true` so its effect is scoped
   *     to JUST that layer's silhouette (matches user intent of
   *     "adjust THIS layer" — sidesteps the Photoshop default of
   *     affecting every layer below, which is confusing on first
   *     encounter and the wrong default for the picker entry-point).
   *  2. Create the Fabric display object that wraps the
   *     adjustment's backing canvas.
   *  3. Keep the source layer active so the user can immediately
   *     re-open Adjust… on it (auto-activating the new adjustment
   *     would disable the picker — adjustment layers themselves
   *     fail `hasRasterizableTarget`).
   *  The slow-path effect on `state.document` fires on the next
   *  tick, runs the compositor (which now honors `clipToBelow`),
   *  and writes the post-adjustment pixels into the new backing
   *  canvas. */
  const handleCreateAdjustmentLayer = useCallback(
    (config: AdjustmentConfig) => {
      const existingAdjustments = Object.values(state.document.layers).filter(
        (l) => l.type === 'adjustment',
      ).length;
      // Soft cap on adjustment-layer count. Each adjustment owns a
      // full-canvas-sized backing Canvas (4K ≈ 64MB), so an
      // unbounded stack exhausts browser memory with no signal to
      // the user. The cap matches a typical pro-photo retouch
      // workflow envelope; users who need more should bake earlier
      // adjustments into the source first (or split into multiple
      // documents). Surfaced as a toast at the creation site rather
      // than silently dropped.
      if (existingAdjustments >= MAX_ADJUSTMENT_LAYERS) {
        toast({
          title: 'Adjustment layer limit reached',
          description: `Documents are limited to ${MAX_ADJUSTMENT_LAYERS} adjustment layers. Delete an existing one before creating another.`,
          variant: 'destructive',
        });
        return;
      }
      const layerId = nextLayerId();
      const adjustmentCount = existingAdjustments + 1;
      const name = `${adjustmentDisplayName(config.kind)} ${adjustmentCount}`;
      // Resolve insertion position. When an active layer exists,
      // insert at its sibling-index so the adjustment lands DIRECTLY
      // above it (front-most-first ordering means lower index =
      // closer to front). When no active layer (rare), fall back to
      // root front.
      const activeId = state.activeLayerId;
      const activeNode = activeId ? state.document.layers[activeId] : null;
      const parentId = activeNode?.parentId ?? null;
      const siblings =
        parentId === null
          ? state.document.rootIds
          : (state.document.layers[parentId]?.childIds ?? []);
      const activeIdx = activeId ? siblings.indexOf(activeId) : -1;
      const insertIndex = activeIdx >= 0 ? activeIdx : 0;
      addAdjustment(layerId, name, config, {
        parentId,
        index: insertIndex,
        clipToBelow: activeNode != null,
        setActive: false,
      });
      canvas.addAdjustmentLayer({ layerId });
      // Pre-compute the post-insert layer order so Fabric reorders
      // immediately rather than waiting for the next state-driven
      // applyLayerOrder effect tick.
      const existingIds = state.layers.map((l) => l.id);
      const activeFlatIdx = activeId ? existingIds.indexOf(activeId) : -1;
      const flatInsertAt = activeFlatIdx >= 0 ? activeFlatIdx : 0;
      const orderedIds = [
        ...existingIds.slice(0, flatInsertAt),
        layerId,
        ...existingIds.slice(flatInsertAt),
      ];
      canvas.applyLayerOrder(orderedIds);
      history.saveSnapshot();
      markDirty();
    },
    [
      addAdjustment,
      canvas,
      history,
      markDirty,
      nextLayerId,
      state.activeLayerId,
      state.document,
      state.layers,
    ],
  );

  // Cancel the deferred clear on unmount so a stale state setter
  // doesn't fire after the editor tears down.
  useEffect(
    () => () => {
      if (adjustmentClearTimerRef.current !== null) {
        window.clearTimeout(adjustmentClearTimerRef.current);
        adjustmentClearTimerRef.current = null;
      }
    },
    [],
  );

  // Cost-protection ref guard for AI handlers.
  //
  // The AI panel button is disabled-while-busy via `aiStatus`, but state
  // commits aren't synchronous — between the user's first click and
  // React painting the disabled button there's a small window where a
  // double-click, a programmatic re-trigger, or a strict-mode double
  // invoke could fire a second handler. Each fired handler calls a paid
  // upstream provider (BFL or OpenAI), so we treat it like money: a ref
  // guard that closes the door before any state mutation, and never
  // opens it again until the handler's `finally` runs. Deliberately
  // shared between Smart Delete and Smart Create because Smart Delete's
  // first run also incurs a (free, but expensive in user time) model
  // download, and we don't want to interleave the two.
  const aiInFlightRef = useRef(false);

  // AI Select Subject — runs `@imgly/background-removal` (the same
  // free in-browser ONNX model that powers the default Remove BG
  // path) on the active layer's source pixels, alpha-thresholds the
  // cutout into a binary mask, and seeds a wand-style marquee. No
  // server round-trip, no credits charged. Image pixels are not
  // modified.
  const handleSelectSubject = useCallback(async () => {
    if (aiInFlightRef.current) return;
    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Selecting subject…' });
    try {
      const maskCanvas = await canvas.extractSubjectMaskCanvas();
      if (!maskCanvas) throw new Error('No image to segment');

      // Switch React's marquee shape to 'wand' so the marquee
      // panel's shape buttons highlight the wand tile (the AI-
      // installed selection IS a wand-style mask). The hook's
      // `setMarqueeShape` effect is idempotent when the canvas-
      // side shape already matches, so this is a UI-sync push,
      // not a defensive workaround.
      if (state.marqueeSettings.shape !== 'wand') {
        updateMarqueeSettings({ shape: 'wand' });
      }

      const installed = canvas.setSelectionFromMask(maskCanvas);
      if (!installed) throw new Error("Couldn't find a subject in the image");

      // Switch to the marquee tool so the new selection feels
      // "active" — the marquee panel surfaces the selection ops
      // immediately. The status-bar chip exposes the same ops
      // tool-independently, but the tool switch matches what the
      // user just asked for.
      if (state.activeTool !== 'marquee') {
        setActiveTool('marquee');
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Select Subject failed';
      setAiStatus({ kind: 'error', message });
    } finally {
      aiInFlightRef.current = false;
    }
  }, [canvas, state.activeTool, state.marqueeSettings.shape, setActiveTool, updateMarqueeSettings]);

  // AI tool handlers. Smart Delete runs the in-browser MI-GAN pipeline
  // on the active layer's marqueed region (no upstream cost). Smart
  // Create either:
  //   • Generates a new image from prompt (BFL FLUX 2 Pro) when no
  //     marquee is active — added as a new layer.
  //   • Inpaints into the marquee with mask-aware blending (OpenAI
  //     gpt-image-1.5 edits) when a marquee is active — composited back
  //     onto the active layer's paint surface.
  const handleSmartDelete = useCallback(async () => {
    if (aiInFlightRef.current) return;
    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Preparing…' });
    try {
      const ok = await canvas.runSmartDeleteOnActiveLayer((source, mask) =>
        runSmartDelete(source, mask, (status) => {
          // Translate the lib's bare progress events to the AIPanel's
          // banner format (running needs a user-facing label).
          if (status.kind === 'loading-model') {
            setAiStatus({ kind: 'loading-model', progressPct: status.progressPct });
          } else {
            setAiStatus({ kind: 'running', label: 'Running Smart Delete…' });
          }
        }),
      );
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Smart Delete needs a marquee selection on a paintable layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Smart Delete failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Smart Delete failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [canvas]);

  // Mask-aware inpaint — replaces the marquee selection with content
  // matching `inpaintPrompt`. Three engines, picked via the dropdown:
  //   - Stability (default): full source + grayscale mask, supports
  //     negative prompt and grow_mask. Min input 64 px.
  //   - Recraft: full source + grayscale mask (server re-encodes to
  //     single-channel). Min input 256 px. Prompt-only.
  //   - OpenAI: cropped to bbox (gpt-image-1 fills the whole frame
  //     otherwise), inverted-alpha mask. Min input 256 px.
  //
  // The composite step is shared: feather the editor mask, alpha-clip
  // the engine result to that feathered mask, draw over the source.
  // The wrapper's destination-in with the un-feathered mask then
  // strips anything outside the original marquee, so off-marquee
  // pixels stay byte-identical regardless of engine.
  const handleInpaint = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const prompt = inpaintPrompt.trim();
    if (prompt.length === 0) return;

    const bbox = canvas.getMarqueeBounds();
    if (!bbox) {
      setAiStatus({
        kind: 'error',
        message: 'Inpaint requires a marquee selection. Draw a selection first.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Inpainting selection…' });
    try {
      const ok = await canvas.runSmartDeleteOnActiveLayer(async (source, mask, maskBbox) => {
        const sourceW = source.width;
        const sourceH = source.height;

        let imageDataUrl: string;
        let maskDataUrl: string;
        let resultPatchRect: { left: number; top: number; width: number; height: number };

        if (inpaintEngine === 'stability') {
          // Stability respects mask boundaries strictly, so surrounding
          // context helps the model produce coherent fills. Send the
          // full source + grayscale mask, scaled to fit Stability's
          // [64, 1536] per-side bounds.
          const fittedSource = fitCanvasToMaxDim(
            source,
            SMART_CREATE_MAX_INPUT_EDGE_PX,
            STABILITY_MIN_INPUT_EDGE_PX,
          );
          const grayscaleMask = toGrayscaleMask(mask);
          const fittedMask = fitCanvasToMaxDim(
            grayscaleMask,
            SMART_CREATE_MAX_INPUT_EDGE_PX,
            STABILITY_MIN_INPUT_EDGE_PX,
          );
          imageDataUrl = fittedSource.toDataURL('image/png');
          maskDataUrl = fittedMask.toDataURL('image/png');
          resultPatchRect = { left: 0, top: 0, width: sourceW, height: sourceH };
        } else {
          // Recraft + OpenAI — crop to bbox. Both engines size their
          // generated content for the whole input frame regardless of
          // the mask, so a full-source send produces output where the
          // generated subject fills the entire image and only a tiny
          // slice of it falls inside the marquee. With crop-to-bbox the
          // marquee bbox *is* the input frame, so the generated subject
          // fills the marquee. Recraft min input is 256 px (matches
          // OpenAI), so the same constant works for both.
          const cropLeft = maskBbox.left;
          const cropTop = maskBbox.top;
          const cropW = maskBbox.width;
          const cropH = maskBbox.height;
          const minDim =
            inpaintEngine === 'recraft' ? RECRAFT_MIN_INPUT_EDGE_PX : OPENAI_MIN_INPUT_EDGE_PX;

          const croppedSource = document.createElement('canvas');
          croppedSource.width = cropW;
          croppedSource.height = cropH;
          croppedSource
            .getContext('2d')
            ?.drawImage(source, cropLeft, cropTop, cropW, cropH, 0, 0, cropW, cropH);

          const croppedMask = document.createElement('canvas');
          croppedMask.width = cropW;
          croppedMask.height = cropH;
          croppedMask
            .getContext('2d')
            ?.drawImage(mask, cropLeft, cropTop, cropW, cropH, 0, 0, cropW, cropH);

          const fittedSource = fitCanvasToMaxDim(
            croppedSource,
            SMART_CREATE_MAX_INPUT_EDGE_PX,
            minDim,
          );
          // Recraft reads the mask as grayscale; the editor's mask
          // works as-is with the server's sharp re-encode. OpenAI gets
          // its alpha inverted server-side. Either way, no extra
          // client-side conversion is needed for the cropped path.
          const fittedMask = fitCanvasToMaxDim(croppedMask, SMART_CREATE_MAX_INPUT_EDGE_PX, minDim);
          imageDataUrl = fittedSource.toDataURL('image/png');
          maskDataUrl = fittedMask.toDataURL('image/png');
          resultPatchRect = { left: cropLeft, top: cropTop, width: cropW, height: cropH };
        }

        const negativePrompt = inpaintNegativePrompt.trim();
        const stylePreset = inpaintStylePreset.trim();
        const response = await api.smartInpaintImage({
          imageDataUrl,
          maskDataUrl,
          prompt,
          engine: inpaintEngine,
          ...(negativePrompt.length > 0 && { negativePrompt }),
          ...(inpaintGrowMaskPx > 0 && { growMask: inpaintGrowMaskPx }),
          ...(stylePreset.length > 0 && { stylePreset }),
        });
        const inpaintResult = await loadImageToCanvas(response.imageDataUrl);

        // Composite path: feather the mask, build an alpha-clipped
        // patch, draw it over the source. Stability/Recraft return a
        // full-source-sized result (resultPatchRect = 0,0,W,H);
        // OpenAI returns a bbox-sized result (drawn back into bbox).
        const featherRadius = computeFeatherRadius(
          maskBbox.width,
          maskBbox.height,
          inpaintFeatherRatio,
        );
        const featheredMask = featherRadius > 0 ? featherMask(mask, featherRadius) : mask;
        const patch = document.createElement('canvas');
        patch.width = sourceW;
        patch.height = sourceH;
        const patchCtx = patch.getContext('2d');
        if (patchCtx) {
          patchCtx.drawImage(
            inpaintResult,
            0,
            0,
            inpaintResult.width,
            inpaintResult.height,
            resultPatchRect.left,
            resultPatchRect.top,
            resultPatchRect.width,
            resultPatchRect.height,
          );
          patchCtx.globalCompositeOperation = 'destination-in';
          patchCtx.drawImage(featheredMask, 0, 0);
        }

        const fullResult = document.createElement('canvas');
        fullResult.width = sourceW;
        fullResult.height = sourceH;
        const ctx = fullResult.getContext('2d');
        if (ctx) {
          ctx.drawImage(source, 0, 0);
          ctx.drawImage(patch, 0, 0);
        }
        return fullResult;
      });
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Inpaint needs a paintable active layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Inpaint failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Inpaint failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [
    inpaintPrompt,
    inpaintNegativePrompt,
    inpaintEngine,
    inpaintGrowMaskPx,
    inpaintFeatherRatio,
    inpaintStylePreset,
    canvas,
  ]);

  // Paid AI cleanup. Mirrors handleInpaint but with no prompt and a
  // dedicated /smart-erase route that dispatches to Stability's
  // /v2beta/stable-image/edit/erase endpoint. Costs ~4 credits per call.
  // The feather post-process matches Inpaint's so the result blends back
  // into the layer cleanly; `eraseGrowMaskPx` is forwarded to Stability
  // server-side via the `grow_mask` form field.
  const handleErase = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const bbox = canvas.getMarqueeBounds();
    if (!bbox) {
      setAiStatus({
        kind: 'error',
        message: 'Erase requires a marquee selection. Draw a selection first.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Erasing selection…' });
    try {
      const ok = await canvas.runSmartDeleteOnActiveLayer(async (source, mask, maskBbox) => {
        const sourceW = source.width;
        const sourceH = source.height;

        // Stability `/edit/erase` — send the FULL source + grayscale
        // mask so the model has surrounding context to fill from.
        // Scaled to fit Stability's per-side bounds (≥64 px, ≤1536 px).
        const fittedSource = fitCanvasToMaxDim(
          source,
          SMART_CREATE_MAX_INPUT_EDGE_PX,
          STABILITY_MIN_INPUT_EDGE_PX,
        );
        const grayscaleMask = toGrayscaleMask(mask);
        const fittedMask = fitCanvasToMaxDim(
          grayscaleMask,
          SMART_CREATE_MAX_INPUT_EDGE_PX,
          STABILITY_MIN_INPUT_EDGE_PX,
        );
        const imageDataUrl = fittedSource.toDataURL('image/png');
        const maskDataUrl = fittedMask.toDataURL('image/png');
        // Result is full-source-sized; draw back over the full canvas.
        const resultPatchRect = { left: 0, top: 0, width: sourceW, height: sourceH };

        const response = await api.smartEraseImage({
          imageDataUrl,
          maskDataUrl,
          ...(eraseGrowMaskPx > 0 && { growMask: eraseGrowMaskPx }),
        });
        const eraseResult = await loadImageToCanvas(response.imageDataUrl);

        // Composite path: feather the mask, build an alpha-clipped
        // "patch" canvas at full source resolution, draw it over the
        // original source. The wrapper's destination-in clip then
        // strips anything outside the original mask so off-marquee
        // pixels stay byte-identical.
        const featherRadius = computeFeatherRadius(
          maskBbox.width,
          maskBbox.height,
          eraseFeatherRatio,
        );
        const featheredMask = featherRadius > 0 ? featherMask(mask, featherRadius) : mask;
        const patch = document.createElement('canvas');
        patch.width = sourceW;
        patch.height = sourceH;
        const patchCtx = patch.getContext('2d');
        if (patchCtx) {
          // Draw the engine result back at its original frame.
          // - Stability path: result is full-source-sized → draw across
          //   the whole canvas (resultPatchRect = 0,0,W,H).
          // - OpenAI path: result is bbox-sized → draw at the bbox.
          patchCtx.drawImage(
            eraseResult,
            0,
            0,
            eraseResult.width,
            eraseResult.height,
            resultPatchRect.left,
            resultPatchRect.top,
            resultPatchRect.width,
            resultPatchRect.height,
          );
          patchCtx.globalCompositeOperation = 'destination-in';
          patchCtx.drawImage(featheredMask, 0, 0);
        }

        const fullResult = document.createElement('canvas');
        fullResult.width = sourceW;
        fullResult.height = sourceH;
        const ctx = fullResult.getContext('2d');
        if (ctx) {
          ctx.drawImage(source, 0, 0);
          ctx.drawImage(patch, 0, 0);
        }
        return fullResult;
      });
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Erase needs a paintable active layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erase failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Erase failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [eraseGrowMaskPx, eraseFeatherRatio, canvas]);

  // Search & Replace. Sends the active layer's full image to Stability
  // alongside two prompts (what to find, what to replace it with);
  // Stability auto-segments and returns the modified full image. No
  // marquee required. Replaces the layer's surface pixels with the
  // result via `runOnActiveLayerFullImage` — no mask composite, no
  // feather (Stability already blends the edges).
  const handleSearchReplace = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const searchPrompt = searchReplaceSearchPrompt.trim();
    const replacePrompt = searchReplacePrompt.trim();
    if (searchPrompt.length === 0 || replacePrompt.length === 0) return;

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Searching and replacing…' });
    try {
      const ok = await canvas.runOnActiveLayerFullImage(async (source) => {
        // Same Stability bounds as the other tools — at least 64 px per
        // side, capped at 1536. The result returns at the input
        // dimensions; the layer-replace helper rescales it back to the
        // surface's native size.
        const fittedSource = fitCanvasToMaxDim(
          source,
          SMART_CREATE_MAX_INPUT_EDGE_PX,
          STABILITY_MIN_INPUT_EDGE_PX,
        );
        const sourceDataUrl = fittedSource.toDataURL('image/png');
        const negativePrompt = searchReplaceNegativePrompt.trim();
        const stylePreset = searchReplaceStylePreset.trim();
        const response = await api.smartSearchReplaceImage({
          imageDataUrl: sourceDataUrl,
          searchPrompt,
          prompt: replacePrompt,
          ...(negativePrompt.length > 0 && { negativePrompt }),
          ...(searchReplaceGrowMaskPx > 0 && { growMask: searchReplaceGrowMaskPx }),
          ...(stylePreset.length > 0 && { stylePreset }),
          ...(searchReplaceSeed !== null && { seed: searchReplaceSeed }),
        });
        return loadImageToCanvas(response.imageDataUrl);
      });
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Search & Replace needs an active paintable layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search & Replace failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Search & Replace failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [
    searchReplaceSearchPrompt,
    searchReplacePrompt,
    searchReplaceNegativePrompt,
    searchReplaceGrowMaskPx,
    searchReplaceStylePreset,
    searchReplaceSeed,
    canvas,
  ]);

  // Search & Recolor — mirrors handleSearchReplace but with selectPrompt
  // (what to recolor) instead of searchPrompt, and prompt describes the
  // new color/effect. Stability returns the full image with the recolor
  // already composited; we swap the active layer's pixels via
  // `runOnActiveLayerFullImage`.
  const handleSearchRecolor = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const selectPrompt = searchRecolorSelectPrompt.trim();
    const colorPrompt = searchRecolorPrompt.trim();
    if (selectPrompt.length === 0 || colorPrompt.length === 0) return;

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Recoloring selection…' });
    try {
      const ok = await canvas.runOnActiveLayerFullImage(async (source) => {
        const fittedSource = fitCanvasToMaxDim(
          source,
          SMART_CREATE_MAX_INPUT_EDGE_PX,
          STABILITY_MIN_INPUT_EDGE_PX,
        );
        const sourceDataUrl = fittedSource.toDataURL('image/png');
        const negativePrompt = searchRecolorNegativePrompt.trim();
        const stylePreset = searchRecolorStylePreset.trim();
        const response = await api.smartSearchRecolorImage({
          imageDataUrl: sourceDataUrl,
          selectPrompt,
          prompt: colorPrompt,
          ...(negativePrompt.length > 0 && { negativePrompt }),
          ...(searchRecolorGrowMaskPx > 0 && { growMask: searchRecolorGrowMaskPx }),
          ...(stylePreset.length > 0 && { stylePreset }),
          ...(searchRecolorSeed !== null && { seed: searchRecolorSeed }),
        });
        return loadImageToCanvas(response.imageDataUrl);
      });
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Search & Recolor needs an active paintable layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search & Recolor failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Search & Recolor failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [
    searchRecolorSelectPrompt,
    searchRecolorPrompt,
    searchRecolorNegativePrompt,
    searchRecolorGrowMaskPx,
    searchRecolorStylePreset,
    searchRecolorSeed,
    canvas,
  ]);

  // Outpaint — extends the background image by leftPx/rightPx/upPx/downPx
  // pixels. Stability returns a single image at the new (extended)
  // dimensions; the canvas helper grows the bg surface and shifts non-bg
  // overlay objects so original content stays visually anchored.
  const handleOutpaint = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const leftPx = Math.max(0, Math.floor(outpaintLeftPx));
    const rightPx = Math.max(0, Math.floor(outpaintRightPx));
    const upPx = Math.max(0, Math.floor(outpaintUpPx));
    const downPx = Math.max(0, Math.floor(outpaintDownPx));
    if (leftPx + rightPx + upPx + downPx === 0) return;

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Outpainting…' });
    // Defense-in-depth: even after the source-downsampling fix, a
    // pathologically slow Stability queue could still leave the user
    // staring at "Outpainting…" indefinitely. A 60 s client-side abort
    // bounds the worst-case wait — past that, surface a friendly
    // timeout and free the in-flight lock so the user can retry.
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 60_000);
    try {
      const ok = await canvas.runOutpaintOnBackground(
        { left: leftPx, right: rightPx, up: upPx, down: downPx },
        async (source, ext) => {
          // Downsample the source before upload, exactly like Search &
          // Replace / Recolor. Without this, a 4032×3024 phone photo
          // produces a ~12 MB PNG, blows past the 30 s fetch timeout,
          // and (worst case) makes Stability queue the job async — at
          // which point `pollForResult` waits up to 3 min before
          // timing out, hence the "stuck for several minutes" report.
          //
          // The extensions are pixel values relative to the un-scaled
          // source, so they must be scaled by the SAME factor for
          // Stability to add proportionally the right strip width.
          // The canvas helper later scales Stability's response back
          // up to full resolution via its drawImage stretch.
          const fittedSource = fitCanvasToMaxDim(
            source,
            SMART_CREATE_MAX_INPUT_EDGE_PX,
            STABILITY_MIN_INPUT_EDGE_PX,
          );
          const scale = fittedSource.width / source.width;
          const scaledLeft = Math.round(ext.left * scale);
          const scaledRight = Math.round(ext.right * scale);
          const scaledUp = Math.round(ext.up * scale);
          const scaledDown = Math.round(ext.down * scale);

          const sourceDataUrl = fittedSource.toDataURL('image/png');
          const promptText = outpaintPrompt.trim();
          const negativePrompt = outpaintNegativePrompt.trim();
          const stylePreset = outpaintStylePreset.trim();
          const response = await api.smartOutpaintImage(
            {
              imageDataUrl: sourceDataUrl,
              ...(scaledLeft > 0 && { left: scaledLeft }),
              ...(scaledRight > 0 && { right: scaledRight }),
              ...(scaledUp > 0 && { up: scaledUp }),
              ...(scaledDown > 0 && { down: scaledDown }),
              ...(promptText.length > 0 && { prompt: promptText }),
              creativity: outpaintCreativity,
              ...(negativePrompt.length > 0 && { negativePrompt }),
              ...(stylePreset.length > 0 && { stylePreset }),
              ...(outpaintSeed !== null && { seed: outpaintSeed }),
            },
            { signal: abortController.signal },
          );
          return loadImageToCanvas(response.imageDataUrl);
        },
      );
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Outpaint needs a background image and at least one edge > 0.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      // An AbortController-driven abort surfaces as a DOMException with
      // name 'AbortError' (or, depending on the path, an ApiError whose
      // cause is an AbortError). Translate either into a friendlier
      // timeout message — the raw "The user aborted a request." string
      // is misleading because the user did NOT cancel; the timer did.
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Outpaint timed out after 60 seconds. Stability may be busy — please try again.'
        : err instanceof Error
          ? err.message
          : 'Outpaint failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Outpaint failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    outpaintLeftPx,
    outpaintRightPx,
    outpaintUpPx,
    outpaintDownPx,
    outpaintPrompt,
    outpaintCreativity,
    outpaintNegativePrompt,
    outpaintStylePreset,
    outpaintSeed,
    canvas,
  ]);

  // Image to Image — sends the active layer as a reference image plus a
  // prompt; Stability redraws guided by both, with `strength` controlling
  // how far to deviate from the source. Same `runOnActiveLayerFullImage`
  // path as Search & Replace — the result lands at the source's aspect
  // ratio, helper scales it back into the surface dims.
  const handleImageToImage = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const promptText = i2iPrompt.trim();
    if (promptText.length === 0) return;

    // Snapshot the active layer before kicking off the request — we use
    // its scene bounds to drop the new layer at the same location, so
    // the user gets a non-destructive overlay they can move/hide rather
    // than a baked-in replacement.
    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Image to Image needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Reimagining…' });
    // Same 60 s defense-in-depth abort as Outpaint — caps the worst-case
    // wait if Stability queues the job.
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 60_000);
    try {
      const fittedSource = fitCanvasToMaxDim(
        snapshot.source,
        SMART_CREATE_MAX_INPUT_EDGE_PX,
        STABILITY_MIN_INPUT_EDGE_PX,
      );
      const sourceDataUrl = fittedSource.toDataURL('image/png');
      const negativePrompt = i2iNegativePrompt.trim();
      const stylePreset = i2iStylePreset.trim();
      const response = await api.smartImageToImageImage(
        {
          imageDataUrl: sourceDataUrl,
          prompt: promptText,
          engine: i2iEngine,
          strength: i2iStrength,
          ...(negativePrompt.length > 0 && { negativePrompt }),
          ...(stylePreset.length > 0 && { stylePreset }),
          ...(i2iSeed !== null && { seed: i2iSeed }),
        },
        { signal: abortController.signal },
      );
      if (i2iMode === 'replace') {
        // Bake the redraw into the active layer's pixels — same path as
        // Search & Replace / Search & Recolor. The helper scales the
        // result back into the surface's native resolution.
        const ok = await canvas.runOnActiveLayerFullImage(async () =>
          loadImageToCanvas(response.imageDataUrl),
        );
        if (!ok) {
          setAiStatus({
            kind: 'error',
            message: 'Image to Image needs an active paintable layer.',
          });
          return;
        }
      } else {
        // Drop the redraw on top as an editable layer aligned to the
        // source's scene rect — same pattern as Smart Create's Insert
        // mode. Non-destructive: the source layer stays untouched.
        const resultResponse = await fetch(response.imageDataUrl);
        const resultBlob = await resultResponse.blob();
        const layer = makeLayer('image', 'Reimagined');
        addLayer(layer, true);
        await canvas.addImageLayer(resultBlob, {
          layerId: layer.id,
          fitTo: snapshot.bounds,
        });
        // Snapshot is captured by the pending-flag effect on the next commit.
        layerSnapshotPendingRef.current = true;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Image to Image timed out after 60 seconds. Stability may be busy — please try again.'
        : err instanceof Error
          ? err.message
          : 'Image to Image failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Image to Image failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    i2iPrompt,
    i2iEngine,
    i2iStrength,
    i2iNegativePrompt,
    i2iStylePreset,
    i2iSeed,
    i2iMode,
    canvas,
    makeLayer,
    addLayer,
  ]);

  // Replace Background — sends the full active layer (engines segment
  // internally) plus a prompt and/or reference image. Stability also
  // accepts light direction / strength / reference for relighting and
  // preserve-subject / preserve-old-bg sliders. Mode toggle decides
  // whether to bake into the active layer or drop on top as a new
  // editable layer (default).
  const handleReplaceBackground = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const promptText = replaceBgPrompt.trim();
    // Stability allows prompt OR reference; Recraft requires prompt.
    if (
      replaceBgEngine === 'recraft'
        ? promptText.length === 0
        : promptText.length === 0 && replaceBgReferenceBlob === null
    ) {
      return;
    }

    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Replace Background needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Replacing background…' });
    // Generous abort — Stability's relight model can run 10-20 s under load.
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 90_000);
    try {
      const fittedSource = fitCanvasToMaxDim(
        snapshot.source,
        SMART_CREATE_MAX_INPUT_EDGE_PX,
        STABILITY_MIN_INPUT_EDGE_PX,
      );
      const sourceDataUrl = fittedSource.toDataURL('image/png');
      // Convert blobs to data URLs so they fit the JSON payload shape.
      const blobToDataUrl = (blob: Blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
      const backgroundReferenceDataUrl = replaceBgReferenceBlob
        ? await blobToDataUrl(replaceBgReferenceBlob)
        : undefined;
      const lightReferenceDataUrl = replaceBgLightReferenceBlob
        ? await blobToDataUrl(replaceBgLightReferenceBlob)
        : undefined;

      const response = await api.smartReplaceBackgroundImage(
        {
          imageDataUrl: sourceDataUrl,
          engine: replaceBgEngine,
          ...(promptText.length > 0 && { backgroundPrompt: promptText }),
          ...(replaceBgEngine === 'stability' && {
            ...(backgroundReferenceDataUrl && { backgroundReferenceDataUrl }),
            ...(lightReferenceDataUrl && { lightReferenceDataUrl }),
            // 'none' is the editor's UI sentinel — drop the field
            // entirely so Stability doesn't bias the relight.
            ...(replaceBgLightDirection !== 'none' && {
              lightDirection: replaceBgLightDirection,
              lightStrength: replaceBgLightStrength,
            }),
            preserveSubject: replaceBgPreserveSubject,
            preserveOriginalBg: replaceBgPreserveOriginalBg,
          }),
          ...(replaceBgEngine === 'recraft' && replaceBgStyle && { style: replaceBgStyle }),
          ...(replaceBgSeed !== null && { seed: replaceBgSeed }),
        },
        { signal: abortController.signal },
      );

      if (replaceBgMode === 'replace') {
        const ok = await canvas.runOnActiveLayerFullImage(async () =>
          loadImageToCanvas(response.imageDataUrl),
        );
        if (!ok) {
          setAiStatus({
            kind: 'error',
            message: 'Replace Background needs an active paintable layer.',
          });
          return;
        }
      } else {
        const resultResponse = await fetch(response.imageDataUrl);
        const resultBlob = await resultResponse.blob();
        const layer = makeLayer('image', 'New Background');
        addLayer(layer, true);
        await canvas.addImageLayer(resultBlob, {
          layerId: layer.id,
          fitTo: snapshot.bounds,
        });
        layerSnapshotPendingRef.current = true;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      // Stability's safety classifier on `replace-background-and-relight`
      // is more aggressive than its other endpoints and refuses requests
      // it deems suggestive — even on subjects that aren't. The provider
      // tags those failures with `CONTENT_FILTERED`; we translate that
      // into a user-friendly message that steers toward Recraft (which
      // doesn't run Stability's filter) instead of a cryptic API error.
      // Credit was refunded automatically by the gateway — surfacing
      // that here so the user doesn't worry about the charge.
      const isContentFiltered = err instanceof Error && /CONTENT_FILTERED/i.test(err.message);
      const message = aborted
        ? 'Replace Background timed out after 90 seconds. Try again — Stability may be busy.'
        : isContentFiltered
          ? 'Stability’s safety filter blocked this request — try the Recraft engine, or adjust the prompt / source image. No credits were charged.'
          : err instanceof Error
            ? err.message
            : 'Replace Background failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Replace Background failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    replaceBgPrompt,
    replaceBgEngine,
    replaceBgReferenceBlob,
    replaceBgLightReferenceBlob,
    replaceBgLightDirection,
    replaceBgLightStrength,
    replaceBgPreserveSubject,
    replaceBgPreserveOriginalBg,
    replaceBgStyle,
    replaceBgSeed,
    replaceBgMode,
    canvas,
    makeLayer,
    addLayer,
  ]);

  // Upscale handler — three modes map to five entities behind the
  // server-side dispatch. For Stability HQ + reimagine the prompt is
  // required; the panel disables the action button until it's filled.
  // 120 s abort budget — heavier than I2I (60 s) and Replace BG (90 s)
  // because Stability's async upscales can sit in the queue for a
  // while under load. The output mode toggle decides whether we bake
  // into the active layer or drop a new layer aligned to the source.
  const handleUpscale = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const promptText = upscalePrompt.trim();
    const acceptsPrompt =
      upscaleEngine === 'stability' &&
      (upscaleMode === 'high-quality' || upscaleMode === 'reimagine');
    // High-quality forces Stability server-side regardless of the
    // local engine state, so we re-derive the effective engine here.
    const effectiveEngine: UpscaleEngine =
      upscaleMode === 'high-quality' ? 'stability' : upscaleEngine;
    const effectiveAcceptsPrompt =
      effectiveEngine === 'stability' &&
      (upscaleMode === 'high-quality' || upscaleMode === 'reimagine');
    if (effectiveAcceptsPrompt && promptText.length === 0) return;
    // Silence the unused warning when the local-state effectiveEngine
    // and promptText branches diverge — we need both for the gating
    // above. The variable usage below makes this explicit.
    void acceptsPrompt;

    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Upscale needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Upscaling…' });
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 120_000);
    try {
      // No fitCanvasToMaxDim here — upscale's whole point is to enlarge
      // a small input. Down-fitting first would defeat the operation.
      // The provider-side endpoints accept inputs up to ~4 MP without
      // complaint; users with sources beyond that probably don't need
      // an upscale anyway.
      //
      // Flatten transparent edges before sending. Stability and Recraft
      // don't preserve alpha — Stability composites on black, Recraft
      // on white — so a transparent-edge source comes back framed by a
      // provider-specific border. Trim + flatten gives the upscaler a
      // pure-content input and the user gets back a clean upscaled
      // image at the actual content's aspect ratio. Identity-equal
      // return when no flatten was needed (opaque source) — no extra
      // allocation in the common case.
      const flattened = flattenForExport(snapshot.source);
      const sourceDataUrl = flattened.toDataURL('image/png');
      const response = await api.smartUpscaleImage(
        {
          imageDataUrl: sourceDataUrl,
          mode: upscaleMode,
          engine: effectiveEngine,
          ...(effectiveAcceptsPrompt && { prompt: promptText }),
          ...(effectiveAcceptsPrompt && { creativity: upscaleCreativity }),
          // Factor only matters for Recraft engines — Stability rejects
          // unknown fields, so we gate the include.
          ...(effectiveEngine === 'recraft' && { factor: upscaleFactor }),
          ...(effectiveAcceptsPrompt && upscaleSeed !== null && { seed: upscaleSeed }),
        },
        { signal: abortController.signal },
      );

      if (upscaleOutputMode === 'replace') {
        // `runUpscaleOnActiveLayer` replaces the layer's FabricImage
        // with one wrapping the upscaled canvas at scale 1, so the
        // larger pixel grid translates to a larger displayed image.
        // For the BG layer it also grows the document dims and
        // re-fits the viewport. Critically NOT `runOnActiveLayerFullImage`,
        // which would scale the result back down into the source dims
        // (the original bug — image stayed visually the same size and
        // the editor's dark background bled around the canvas edges).
        const ok = await canvas.runUpscaleOnActiveLayer(async () =>
          loadImageToCanvas(response.imageDataUrl),
        );
        if (!ok) {
          setAiStatus({
            kind: 'error',
            message: 'Upscale needs an active paintable layer.',
          });
          return;
        }
      } else {
        // Drop the result as a new layer at its NATURAL size — no
        // `fitTo`, since fitTo would scale the upscaled image back
        // down to the source's scene rect (defeating the upscale).
        // `addImageLayer` without fitTo centers the new layer on the
        // photo dims, so visually the upscaled layer overlays the
        // source centered, larger. The viewport re-fit below makes
        // sure the user sees the whole thing.
        const resultResponse = await fetch(response.imageDataUrl);
        const resultBlob = await resultResponse.blob();
        const layer = makeLayer('image', 'Upscaled');
        addLayer(layer, true);
        await canvas.addImageLayer(resultBlob, { layerId: layer.id });
        // Re-fit so the user can actually see the larger result. Without
        // this the new layer extends past the visible viewport and the
        // user thinks "nothing happened" (or "weird black border").
        const fitZoom = canvas.fitToContainer();
        if (fitZoom !== null && fitZoom !== undefined) setZoom(fitZoom);
        layerSnapshotPendingRef.current = true;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Upscale timed out after 120 seconds. Try again — Stability may be busy.'
        : err instanceof Error
          ? err.message
          : 'Upscale failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Upscale failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    upscaleMode,
    upscaleEngine,
    upscalePrompt,
    upscaleCreativity,
    upscaleFactor,
    upscaleSeed,
    upscaleOutputMode,
    canvas,
    makeLayer,
    addLayer,
  ]);

  // Vectorize is treated as an EXPORT op rather than an edit — the
  // editor canvas is left untouched and the result lands either as a
  // local download or a Fias library file via the in-editor
  // VectorizeSaveDialog. Same Local/Fias destination toggle as the
  // toolbar's regular Save As, so the two flows feel consistent.
  //
  // Flow:
  //   1. Click Vectorize → API runs (status banner shows progress)
  //   2. SVG returns → stash in ref, open VectorizeSaveDialog
  //   3. User picks filename + destination + clicks Save
  //   4. Save handler routes to anchor-download (local) or the 3-step
  //      Fias upload (fias).
  //   5. If user cancels the dialog, the SVG stays in the ref so the
  //      panel's "Save again" button can re-open the dialog without
  //      re-running the API.
  const handleVectorize = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Vectorize needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Vectorizing…' });
    setVectorizeStatus('idle');
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 60_000);
    try {
      // Trim transparent edges before sending — same flatten pass
      // upscale uses. The vectorizer doesn't have the per-provider
      // border bug (Recraft's vectorize ignores transparent regions
      // cleanly in its output paths), but trimming saves credits
      // indirectly: fewer paths to compute around empty space.
      const flattened = flattenForExport(snapshot.source);
      const sourceDataUrl = flattened.toDataURL('image/png');
      const response = await api.smartVectorizeImage(
        { imageDataUrl: sourceDataUrl },
        { signal: abortController.signal },
      );
      vectorizeSvgRef.current = response.svg;
      setAiStatus({ kind: 'idle' });
      // Hand off to the in-editor save dialog. The user picks
      // filename + destination there; the dialog's onSave callback
      // (handleVectorizeSave) routes to the right backend.
      setVectorizeSaveDialogOpen(true);
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Vectorize timed out after 60 seconds. Try again — Recraft may be busy.'
        : err instanceof Error
          ? err.message
          : 'Vectorize failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Vectorize failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [canvas]);

  // Save again: re-open the dialog with the SVG that's still in the
  // ref. No new API call → no additional credit charge. Lets the user
  // save the same vector to a different location, switch destinations,
  // or recover from accidentally hitting Cancel on the first dialog.
  const handleSaveVectorizeAgain = useCallback(() => {
    if (!vectorizeSvgRef.current) return;
    setVectorizeSaveDialogOpen(true);
  }, [vectorizeSvgRef]);

  // Dialog → Save click. Routes to the right backend based on the
  // user's destination pick.
  const handleVectorizeSave = useCallback(
    async (fileName: string, destination: VectorizeSaveDestination) => {
      const svg = vectorizeSvgRef.current;
      if (!svg) return;
      // Persist the destination so a "Save again" pre-selects the
      // user's last pick. Matches the ExportDialog's UX.
      setVectorizeSaveDialogDestination(destination);
      const outcome =
        destination === 'fias'
          ? await uploadSvgToFias(svg, fileName)
          : await downloadSvgLocal(svg, fileName);
      if (outcome === 'saved') {
        setVectorizeStatus('saved');
        setVectorizeSavedFileName(/\.svg$/i.test(fileName) ? fileName : `${fileName}.svg`);
      } else {
        setAiStatus({
          kind: 'error',
          message:
            destination === 'fias'
              ? 'Failed to upload SVG to Fias files.'
              : 'Failed to save SVG locally.',
        });
        setVectorizeStatus('cancelled');
      }
    },
    [vectorizeSvgRef],
  );

  const handleVectorizeSaveDialogClose = useCallback(() => {
    setVectorizeSaveDialogOpen(false);
    // If the dialog closes without a save (Cancel), don't clear the
    // SVG — the panel's Save again button needs it. The 'saved'
    // state was already set in handleVectorizeSave on success.
    if (vectorizeStatus === 'idle') setVectorizeStatus('cancelled');
  }, [vectorizeStatus]);

  // Control / Guided — four Stability v2beta endpoints behind a
  // single mode-first tool. Each mode has different required inputs
  // (prompt for first three, style reference image for style-transfer)
  // and different optional knobs. The handler validates locally,
  // calls the route, then either bakes into the active layer or
  // drops a new layer aligned to the source — same Replace / New
  // Layer pattern as I2I / Replace BG / Upscale.
  const handleControl = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const promptText = controlPrompt.trim();
    const acceptsPrompt = controlMode !== 'style-transfer';
    if (acceptsPrompt && promptText.length === 0) return;
    if (controlMode === 'style-transfer' && !controlStyleReferenceBlob) return;

    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Control needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: `${CONTROL_RUNNING_LABEL[controlMode]}…` });
    // 90 s abort matches Replace BG — Stability's control endpoints
    // are similar weight (single API call, not async polling) so
    // they don't need Upscale's 120 s window.
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 90_000);
    try {
      // Same flatten pre-process used by Upscale + Vectorize. None of
      // the control endpoints preserve alpha, so transparent edges
      // would composite onto Stability's default black and produce
      // the same border bug we hit with Replace Background.
      const flattened = flattenForExport(snapshot.source);
      const sourceDataUrl = flattened.toDataURL('image/png');
      // Convert the style reference (style-transfer only) to a data URL.
      const blobToDataUrl = (blob: Blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
      const styleImageDataUrl =
        controlMode === 'style-transfer' && controlStyleReferenceBlob
          ? await blobToDataUrl(controlStyleReferenceBlob)
          : undefined;

      const response = await api.smartControlImage(
        {
          imageDataUrl: sourceDataUrl,
          mode: controlMode,
          ...(acceptsPrompt && { prompt: promptText }),
          ...(controlNegativePrompt.trim().length > 0 && {
            negativePrompt: controlNegativePrompt.trim(),
          }),
          // Per-mode knobs: only forward what the endpoint actually
          // accepts. The route would drop unknowns silently, but
          // sending less is cleaner.
          ...((controlMode === 'from-sketch' || controlMode === 'match-structure') && {
            controlStrength,
          }),
          ...(controlMode === 'match-style' && { fidelity: controlFidelity }),
          ...(controlMode === 'style-transfer' && {
            styleImageDataUrl,
            styleStrength: controlStyleStrength,
            compositionFidelity: controlCompositionFidelity,
            changeStrength: controlChangeStrength,
          }),
          ...(controlSeed !== null && { seed: controlSeed }),
        },
        { signal: abortController.signal },
      );

      if (controlOutputMode === 'replace') {
        const ok = await canvas.runOnActiveLayerFullImage(async () =>
          loadImageToCanvas(response.imageDataUrl),
        );
        if (!ok) {
          setAiStatus({
            kind: 'error',
            message: 'Control needs an active paintable layer.',
          });
          return;
        }
      } else {
        const resultResponse = await fetch(response.imageDataUrl);
        const resultBlob = await resultResponse.blob();
        const layer = makeLayer('image', CONTROL_LAYER_LABEL[controlMode]);
        addLayer(layer, true);
        await canvas.addImageLayer(resultBlob, {
          layerId: layer.id,
          fitTo: snapshot.bounds,
        });
        layerSnapshotPendingRef.current = true;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Control timed out after 90 seconds. Stability may be busy — please try again.'
        : err instanceof Error
          ? err.message
          : 'Control failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Control failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    controlMode,
    controlPrompt,
    controlNegativePrompt,
    controlStrength,
    controlFidelity,
    controlStyleReferenceBlob,
    controlStyleStrength,
    controlCompositionFidelity,
    controlChangeStrength,
    controlSeed,
    controlOutputMode,
    canvas,
    makeLayer,
    addLayer,
  ]);

  const handleSmartCreate = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const prompt = smartCreatePrompt.trim();
    if (prompt.length === 0) return;

    const bbox = canvas.getMarqueeBounds();
    aiInFlightRef.current = true;

    // Smart Create is text-to-image only now (the previous "Blend"
    // mode lives under the Inpaint picker entry).
    // Generate the subject as text-to-image (cheap, fast — BFL FLUX
    // 2 Pro), then run remove-background locally on the result so
    // we can drop just the subject onto a fresh layer with no
    // surrounding photo bleeding through. The user controls
    // composition by moving / scaling the layer afterward, so we
    // don't need any pixel-level blending — clean alpha edges from
    // the cutout do the job.
    //
    // The prompt suffix nudges the model toward a clean isolated
    // subject (otherwise it sometimes generates a scene with the
    // subject in it, which the bg-removal step then mangles).
    // Map the marquee's aspect to the closest universally-supported
    // ratio. The 5-bucket set is the intersection of what every wired
    // image provider's resolver accepts natively (BFL, OpenAI, Google
    // Imagen / Nano Banana, Recraft, Stability). Thresholds are at
    // the geometric mean of adjacent ratios so each marquee aspect
    // lands in its closest bucket.
    const aspect = bbox ? bbox.width / bbox.height : 1;
    const size: '1:1' | '4:3' | '3:4' | '16:9' | '9:16' =
      aspect >= 1.55
        ? '16:9'
        : aspect >= 1.155
          ? '4:3'
          : aspect >= 0.866
            ? '1:1'
            : aspect >= 0.645
              ? '3:4'
              : '9:16';
    const decoratedPrompt = `${prompt}. Plain white background, isolated subject, fully visible, centered, no other objects.`;

    setAiStatus({ kind: 'running', label: 'Generating image…' });
    try {
      // Scope the response data URL to a block so it can be GC'd as
      // soon as we have the Blob — a 1536² PNG is ~2-3 MB and we
      // don't want the base64 string pinned alongside the Blob copy
      // and the eventual FabricImage all the way through bg-removal.
      let generatedBlob: Blob;
      {
        const { imageDataUrl } = await api.smartCreateImage({
          prompt: decoratedPrompt,
          size,
          model: smartCreateInsertModel,
        });
        generatedBlob = await (await fetch(imageDataUrl)).blob();
      }

      // Local background removal (the @imgly/background-removal
      // ONNX model). The lib is already used by the toolbar's
      // Remove Background button; loading it here on demand reuses
      // the cached weights from any prior call in the same session.
      setAiStatus({ kind: 'running', label: 'Removing background…' });
      const { removeBackground: imglyRemoveBackground } = await import('@imgly/background-removal');
      const cutoutBlob = await imglyRemoveBackground(generatedBlob);

      // Add as a new editable layer. The hook handles placement +
      // scale + render in one shot, so there's no chance of the
      // image landing in the wrong place because a follow-up render
      // didn't fire.
      const layer = makeLayer('image', 'AI Image');
      addLayer(layer, true);
      await canvas.addImageLayer(cutoutBlob, {
        layerId: layer.id,
        fitTo: bbox ?? undefined,
      });
      // Snapshot is captured by the pending-flag effect on the next commit.
      layerSnapshotPendingRef.current = true;
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Smart Create failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Smart Create (insert) failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [smartCreatePrompt, smartCreateInsertModel, canvas, makeLayer, addLayer]);

  return (
    <div className="flex flex-col h-screen bg-background">
      <EditorToolbar
        zoom={state.zoom}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
        onUndo={() => {
          flushContinuousSnapshot();
          history.undoCanvas();
        }}
        onRedo={() => {
          flushContinuousSnapshot();
          history.redoCanvas();
        }}
        onZoomIn={() => {
          const newZoom = Math.min(state.zoom * 1.25, 10);
          setZoom(newZoom);
          canvas.setZoom(newZoom);
        }}
        onZoomOut={() => {
          const newZoom = Math.max(state.zoom / 1.25, 0.1);
          setZoom(newZoom);
          canvas.setZoom(newZoom);
        }}
        onFitToScreen={() => {
          const fitZoom = canvas.fitToContainer();
          setZoom(fitZoom ?? 1);
        }}
        onNew={handleNewBlankFile}
        onSaveAs={handleSaveAs}
        onCloseFile={handleCloseActiveFile}
        hasActiveFile={tabs.activeTabId !== null}
        onCut={handleCut}
        onCopy={handleCopy}
        onPaste={() => void handlePaste()}
        canCutCopy={canvas.hasMarqueeSelection}
        canPaste={canvas.hasClipboard || SUPPORTS_OS_CLIPBOARD_READ}
        onSave={() => void handleToolbarSave()}
        onOpenFile={handleOpenFile}
        onOpenFias={handleOpenFias}
        gridVisible={gridVisible}
        onToggleGrid={() => setGridVisible((v) => !v)}
        onShowHelp={() => setHelpDialogOpen(true)}
      />

      {tabs.tabs.length > 0 && (
        <EditorTabBar
          tabs={tabs.tabs}
          activeTabId={tabs.activeTabId}
          onSwitch={handleTabSwitch}
          onClose={handleTabClose}
          onNewTab={handleNewTab}
        />
      )}

      <div className={`flex flex-1 overflow-hidden ${isLandscape ? 'flex-row' : 'flex-col'}`}>
        <div className="flex flex-row flex-1 overflow-hidden min-w-0 min-h-0">
          <ToolPalette activeTool={state.activeTool} onToolSelect={handleToolSelect} />

          <CanvasArea
            canvasElRef={canvasElRef}
            wrapperRef={wrapperRef}
            onFileDropped={loadFile}
            onFileSelected={loadFile}
            onOpenFias={handleOpenFias}
            hasImage={hasImage}
            gridVisible={gridVisible}
            viewport={canvas.viewport}
            brushCursor={
              hasImage && (state.activeTool === 'draw' || state.activeTool === 'eraser')
                ? {
                    // Brush width is in canvas pixels; the screen footprint
                    // scales with zoom so the preview matches what the user
                    // will actually paint.
                    sizePx: Math.max(state.brushSettings.width * state.zoom, 4),
                    color: state.activeTool === 'eraser' ? '#ffffff' : state.brushSettings.color,
                  }
                : hasImage && state.activeTool === 'retouch'
                  ? {
                      // Retouch variants paint via per-cursor stamps sized
                      // by `retouchSettings.size`. Showing the footprint as
                      // a circle with a center `+` makes Clone's
                      // destination cursor visually pair with the source
                      // reticle (also a `+`) — the user sees source ↔
                      // destination as one gesture.
                      sizePx: Math.max(state.retouchSettings.size * state.zoom, 4),
                      color: '#0078d7',
                      withCenterCross: true,
                    }
                  : null
            }
            sourceSetMode={cloneSourceSetMode}
          />
        </div>

        <div
          className={
            isLandscape
              ? 'flex flex-col h-full w-[320px] max-w-[40vw] shrink-0'
              : 'flex flex-col w-full'
          }
        >
          <PropertiesPanel
            isOpen={state.propertiesPanelOpen}
            placement={isLandscape ? 'right' : 'bottom'}
            activeTool={state.activeTool}
            brushSettings={state.brushSettings}
            textSettings={state.textSettings}
            shapeSettings={state.shapeSettings}
            marqueeSettings={state.marqueeSettings}
            retouchSettings={state.retouchSettings}
            penSettings={state.penSettings}
            cloneSourcePoint={cloneSourcePoint}
            cloneArmed={cloneArmed}
            onSetCloneSource={handleSetCloneSourceFromPanel}
            onClearCloneSource={handleClearCloneSource}
            onUpdateBrush={updateBrushSettings}
            onUpdateText={updateTextSettings}
            textPathBoundInfo={textPathBoundInfo}
            textPathBindCandidate={textPathBindCandidate}
            isActiveTextBoundToPath={isActiveTextBoundToPath}
            onBindTextToPath={handleBindTextToPath}
            onUnbindTextFromPath={handleUnbindTextFromPath}
            onUpdateShape={updateShapeSettings}
            onUpdateMarquee={updateMarqueeSettings}
            onUpdateRetouch={updateRetouchSettings}
            onUpdatePen={updatePenSettings}
            penEditPathAvailable={
              state.activeLayerId !== null &&
              state.document.layers[state.activeLayerId]?.type === 'vector'
            }
            onBeginPathEditActive={
              state.activeLayerId
                ? () => handleBeginPathEdit(state.activeLayerId as string)
                : undefined
            }
            pathEditActive={state.pathEditActive}
            pathEditSelectedAnchorIndex={state.pathEditSelectedAnchorIndex}
            pathEditSelectedAnchorType={pathEditSelectedAnchorType}
            onPathEditAnchorTypeChange={handlePathEditAnchorType}
            onApplyPathEdit={handleApplyPathEdit}
            onCancelPathEdit={handleCancelPathEdit}
            eligibleVectorLayerCount={eligibleVectorLayerCount}
            onVectorBooleanOp={handleVectorBooleanOp}
            onConvertGrayscale={handleConvertGrayscale}
            onOpenAdjustment={handleOpenAdjustment}
            canAdjustActiveLayer={canAdjustActiveLayer}
            onRotateLeft={() => canvas.rotateCanvas(-90)}
            onRotateRight={() => canvas.rotateCanvas(90)}
            onFlipHorizontal={() => canvas.flipCanvas('horizontal')}
            onFlipVertical={() => canvas.flipCanvas('vertical')}
            hasMarqueeSelection={canvas.hasMarqueeSelection}
            canGrowMarquee={canvas.canGrowMarquee()}
            onGrowMarqueeSelection={(deltaPx) => canvas.growMarqueeBy(deltaPx)}
            onInvertMarqueeSelection={handleInvertSelection}
            onSelectSubject={handleSelectSubject}
            selectSubjectBusy={
              aiStatus.kind === 'running' && aiStatus.label === 'Selecting subject…'
            }
            quickMaskActive={state.quickMaskActive}
            onToggleQuickMask={handleToggleQuickMask}
            onOpenColorRange={() => {
              setSourceCanvas(canvas.getSourceCanvasForSampling());
              setColorRangeOpen(true);
            }}
            onOpenRefineEdge={() => {
              setSourceCanvas(canvas.getSourceCanvasForSampling());
              setRefineEdgeOpen(true);
            }}
            quickMaskMode={state.quickMaskMode}
            onChangeQuickMaskMode={setQuickMaskMode}
            onApplyQuickMask={handleApplyQuickMask}
            onCancelQuickMask={handleCancelQuickMask}
            canApplyQuickMaskAsLayerMask={state.activeLayerId != null}
            transformActive={state.transformActive}
            transformMode={state.transformMode}
            onChangeTransformMode={handleSetTransformMode}
            getTransformMatrix={canvas.getTransformMatrix}
            onChangeTransformMatrix={canvas.setTransformMatrix}
            onApplyTransform={handleApplyTransform}
            onCancelTransform={handleCancelTransform}
            onApplyWarpPreset={canvas.applyWarpPresetToOverlay}
            onBeginFreeTransform={
              state.activeLayerId
                ? () => handleBeginTransform(state.activeLayerId as string)
                : undefined
            }
            smartCreatePrompt={smartCreatePrompt}
            onSmartCreatePromptChange={setSmartCreatePrompt}
            smartCreateInsertModel={smartCreateInsertModel}
            onSmartCreateInsertModelChange={setSmartCreateInsertModel}
            aiStatus={aiStatus}
            onSmartDelete={() => void handleSmartDelete()}
            onSmartCreate={handleSmartCreate}
            inpaintPrompt={inpaintPrompt}
            onInpaintPromptChange={setInpaintPrompt}
            inpaintNegativePrompt={inpaintNegativePrompt}
            onInpaintNegativePromptChange={setInpaintNegativePrompt}
            inpaintEngine={inpaintEngine}
            onInpaintEngineChange={handleInpaintEngineChange}
            inpaintGrowMaskPx={inpaintGrowMaskPx}
            onInpaintGrowMaskPxChange={setInpaintGrowMaskPx}
            inpaintFeatherRatio={inpaintFeatherRatio}
            onInpaintFeatherRatioChange={setInpaintFeatherRatio}
            inpaintStylePreset={inpaintStylePreset}
            onInpaintStylePresetChange={setInpaintStylePreset}
            onInpaint={() => void handleInpaint()}
            eraseGrowMaskPx={eraseGrowMaskPx}
            onEraseGrowMaskPxChange={setEraseGrowMaskPx}
            eraseFeatherRatio={eraseFeatherRatio}
            onEraseFeatherRatioChange={setEraseFeatherRatio}
            onErase={() => void handleErase()}
            searchReplaceSearchPrompt={searchReplaceSearchPrompt}
            onSearchReplaceSearchPromptChange={setSearchReplaceSearchPrompt}
            searchReplacePrompt={searchReplacePrompt}
            onSearchReplacePromptChange={setSearchReplacePrompt}
            searchReplaceNegativePrompt={searchReplaceNegativePrompt}
            onSearchReplaceNegativePromptChange={setSearchReplaceNegativePrompt}
            searchReplaceGrowMaskPx={searchReplaceGrowMaskPx}
            onSearchReplaceGrowMaskPxChange={setSearchReplaceGrowMaskPx}
            searchReplaceStylePreset={searchReplaceStylePreset}
            onSearchReplaceStylePresetChange={setSearchReplaceStylePreset}
            searchReplaceSeed={searchReplaceSeed}
            onSearchReplaceSeedChange={setSearchReplaceSeed}
            onSearchReplace={() => void handleSearchReplace()}
            searchRecolorSelectPrompt={searchRecolorSelectPrompt}
            onSearchRecolorSelectPromptChange={setSearchRecolorSelectPrompt}
            searchRecolorPrompt={searchRecolorPrompt}
            onSearchRecolorPromptChange={setSearchRecolorPrompt}
            searchRecolorNegativePrompt={searchRecolorNegativePrompt}
            onSearchRecolorNegativePromptChange={setSearchRecolorNegativePrompt}
            searchRecolorGrowMaskPx={searchRecolorGrowMaskPx}
            onSearchRecolorGrowMaskPxChange={setSearchRecolorGrowMaskPx}
            searchRecolorStylePreset={searchRecolorStylePreset}
            onSearchRecolorStylePresetChange={setSearchRecolorStylePreset}
            searchRecolorSeed={searchRecolorSeed}
            onSearchRecolorSeedChange={setSearchRecolorSeed}
            onSearchRecolor={() => void handleSearchRecolor()}
            outpaintLeftPx={outpaintLeftPx}
            onOutpaintLeftPxChange={setOutpaintLeftPx}
            outpaintRightPx={outpaintRightPx}
            onOutpaintRightPxChange={setOutpaintRightPx}
            outpaintUpPx={outpaintUpPx}
            onOutpaintUpPxChange={setOutpaintUpPx}
            outpaintDownPx={outpaintDownPx}
            onOutpaintDownPxChange={setOutpaintDownPx}
            outpaintPrompt={outpaintPrompt}
            onOutpaintPromptChange={setOutpaintPrompt}
            outpaintCreativity={outpaintCreativity}
            onOutpaintCreativityChange={setOutpaintCreativity}
            outpaintNegativePrompt={outpaintNegativePrompt}
            onOutpaintNegativePromptChange={setOutpaintNegativePrompt}
            outpaintStylePreset={outpaintStylePreset}
            onOutpaintStylePresetChange={setOutpaintStylePreset}
            outpaintSeed={outpaintSeed}
            onOutpaintSeedChange={setOutpaintSeed}
            onOutpaint={() => void handleOutpaint()}
            i2iPrompt={i2iPrompt}
            onI2iPromptChange={setI2iPrompt}
            i2iEngine={i2iEngine}
            onI2iEngineChange={setI2iEngine}
            i2iStrength={i2iStrength}
            onI2iStrengthChange={setI2iStrength}
            i2iNegativePrompt={i2iNegativePrompt}
            onI2iNegativePromptChange={setI2iNegativePrompt}
            i2iStylePreset={i2iStylePreset}
            onI2iStylePresetChange={setI2iStylePreset}
            i2iSeed={i2iSeed}
            onI2iSeedChange={setI2iSeed}
            i2iMode={i2iMode}
            onI2iModeChange={setI2iMode}
            onImageToImage={() => void handleImageToImage()}
            selectedAITool={state.selectedAITool}
            onOpenAIPicker={() => setAIPickerOpen(true)}
            onRemoveBackground={handleRemoveBackground}
            isRemovingBackground={isRemovingBackground}
            canRemoveBackground={hasImage}
            removeBackgroundEngine={removeBackgroundEngine}
            onRemoveBackgroundEngineChange={setRemoveBackgroundEngine}
            replaceBgEngine={replaceBgEngine}
            onReplaceBgEngineChange={setReplaceBgEngine}
            replaceBgPrompt={replaceBgPrompt}
            onReplaceBgPromptChange={setReplaceBgPrompt}
            replaceBgReferenceBlob={replaceBgReferenceBlob}
            onReplaceBgReferenceBlobChange={setReplaceBgReferenceBlob}
            replaceBgLightReferenceBlob={replaceBgLightReferenceBlob}
            onReplaceBgLightReferenceBlobChange={setReplaceBgLightReferenceBlob}
            replaceBgLightDirection={replaceBgLightDirection}
            onReplaceBgLightDirectionChange={setReplaceBgLightDirection}
            replaceBgLightStrength={replaceBgLightStrength}
            onReplaceBgLightStrengthChange={setReplaceBgLightStrength}
            replaceBgPreserveSubject={replaceBgPreserveSubject}
            onReplaceBgPreserveSubjectChange={setReplaceBgPreserveSubject}
            replaceBgPreserveOriginalBg={replaceBgPreserveOriginalBg}
            onReplaceBgPreserveOriginalBgChange={setReplaceBgPreserveOriginalBg}
            replaceBgStyle={replaceBgStyle}
            onReplaceBgStyleChange={setReplaceBgStyle}
            replaceBgSeed={replaceBgSeed}
            onReplaceBgSeedChange={setReplaceBgSeed}
            replaceBgMode={replaceBgMode}
            onReplaceBgModeChange={setReplaceBgMode}
            onReplaceBackground={() => void handleReplaceBackground()}
            upscaleMode={upscaleMode}
            onUpscaleModeChange={(m) => {
              setUpscaleMode(m);
              // HQ's conservative endpoint rejects creativity < 0.2.
              // Bump the live value when entering HQ from a mode that
              // allowed lower so the slider, default, and server-side
              // gate all agree.
              if (m === 'high-quality' && upscaleCreativity < 0.2) {
                setUpscaleCreativity(0.2);
              }
            }}
            upscaleEngine={upscaleEngine}
            onUpscaleEngineChange={setUpscaleEngine}
            upscalePrompt={upscalePrompt}
            onUpscalePromptChange={setUpscalePrompt}
            upscaleCreativity={upscaleCreativity}
            onUpscaleCreativityChange={setUpscaleCreativity}
            upscaleFactor={upscaleFactor}
            onUpscaleFactorChange={setUpscaleFactor}
            upscaleSeed={upscaleSeed}
            onUpscaleSeedChange={setUpscaleSeed}
            upscaleOutputMode={upscaleOutputMode}
            onUpscaleOutputModeChange={setUpscaleOutputMode}
            upscaleSourceWidth={state.canvasWidth}
            upscaleSourceHeight={state.canvasHeight}
            onUpscale={() => void handleUpscale()}
            vectorizeStatus={vectorizeStatus}
            vectorizeSavedFileName={vectorizeSavedFileName}
            onVectorize={() => void handleVectorize()}
            onSaveVectorizeAgain={() => void handleSaveVectorizeAgain()}
            controlMode={controlMode}
            onControlModeChange={setControlMode}
            controlPrompt={controlPrompt}
            onControlPromptChange={setControlPrompt}
            controlNegativePrompt={controlNegativePrompt}
            onControlNegativePromptChange={setControlNegativePrompt}
            controlStrength={controlStrength}
            onControlStrengthChange={setControlStrength}
            controlFidelity={controlFidelity}
            onControlFidelityChange={setControlFidelity}
            controlStyleReferenceBlob={controlStyleReferenceBlob}
            onControlStyleReferenceBlobChange={setControlStyleReferenceBlob}
            controlStyleStrength={controlStyleStrength}
            onControlStyleStrengthChange={setControlStyleStrength}
            controlCompositionFidelity={controlCompositionFidelity}
            onControlCompositionFidelityChange={setControlCompositionFidelity}
            controlChangeStrength={controlChangeStrength}
            onControlChangeStrengthChange={setControlChangeStrength}
            controlSeed={controlSeed}
            onControlSeedChange={setControlSeed}
            controlOutputMode={controlOutputMode}
            onControlOutputModeChange={setControlOutputMode}
            onControl={() => void handleControl()}
            onApplyCrop={handleApplyCrop}
            onToggle={togglePropertiesPanel}
          />
          <LayersPanel
            isOpen={state.layersPanelOpen}
            placement={isLandscape ? 'right' : 'bottom'}
            layers={state.layers}
            activeLayerId={state.activeLayerId}
            hasSelection={canvas.hasMarqueeSelection}
            onAddLayer={handleAddLayer}
            onSelectLayer={handleSelectLayer}
            onToggleVisibility={handleToggleLayerVisibility}
            onRenameLayer={handleRenameLayer}
            onDeleteLayer={handleDeleteLayer}
            onReorderLayer={handleReorderLayer}
            onSetLayerOpacity={handleSetLayerOpacity}
            onSetLayerBlendMode={handleSetLayerBlendMode}
            onSetLayerEffects={handleSetLayerEffects}
            onAddMaskToLayer={handleAddMaskToLayer}
            onDeleteMaskFromLayer={handleDeleteMaskFromLayer}
            onToggleMaskEnabled={handleToggleMaskEnabled}
            onToggleMaskInverted={handleToggleMaskInverted}
            onEditAdjustment={handleEditAdjustmentLayer}
            editTargets={state.editTargets}
            onSetEditTarget={setEditTarget}
            selectedLayerIds={selectedLayerIds}
            onShiftSelectLayer={handleShiftSelectLayer}
            multiSelectMode={multiSelectMode}
            onToggleMultiSelectMode={toggleMultiSelectMode}
            onToggleSelectLayer={handleToggleSelectLayer}
            layerDepths={layerDepths}
            collapsedGroupIds={collapsedGroupIds}
            onToggleCollapse={handleToggleCollapse}
            onGroupActiveLayer={handleGroupActiveLayer}
            onUngroup={handleUngroup}
            onTransformLayer={handleBeginTransform}
            onLiquifyLayer={handleBeginLiquify}
            onEditPathLayer={handleBeginPathEdit}
            onMoveNodeInTree={handleMoveNodeInTree}
            onToggle={toggleLayersPanel}
          />
        </div>
      </div>

      <StatusBar
        zoom={state.zoom}
        canvasWidth={state.canvasWidth}
        canvasHeight={state.canvasHeight}
        subscribeToCursorMove={canvas.subscribeToCursorMove}
        currentColor={state.brushSettings.color}
        selection={
          // Status-bar chip is rendered whenever an image is loaded
          // (canvas dimensions known) so Quick Mask + Color Range
          // remain reachable even with no selection. Modify-rows
          // hide themselves inside the popover when there's nothing
          // to modify.
          state.canvasWidth > 0
            ? (() => {
                const b = canvas.hasMarqueeSelection ? canvas.getMarqueeBounds() : null;
                return {
                  hasSelection: canvas.hasMarqueeSelection,
                  width: b?.width ?? 0,
                  height: b?.height ?? 0,
                  canGrow: canvas.canGrowMarquee(),
                  onGrow: (deltaPx) => canvas.growMarqueeBy(deltaPx),
                  onInvert: handleInvertSelection,
                  onSelectSubject: handleSelectSubject,
                  selectSubjectBusy: aiStatus.kind === 'running',
                  quickMaskActive: state.quickMaskActive,
                  onToggleQuickMask: handleToggleQuickMask,
                  onOpenColorRange: () => {
                    // Snapshot the sampling source on open so the
                    // preview doesn't drift if the canvas changes
                    // mid-pick.
                    setSourceCanvas(canvas.getSourceCanvasForSampling());
                    setColorRangeOpen(true);
                  },
                  onOpenRefineEdge: () => {
                    setSourceCanvas(canvas.getSourceCanvasForSampling());
                    setRefineEdgeOpen(true);
                  },
                };
              })()
            : null
        }
      />

      {exportDialogOpen && (
        <ExportDialog
          onClose={handleExportDialogClose}
          onExportLocal={handleExportLocal}
          onExportFias={handleExportFias}
          onExportClipboard={handleExportClipboard}
          defaultFileName={state.fileName}
          defaultDestination={exportDialogDestination}
          canvasWidth={state.canvasWidth}
          canvasHeight={state.canvasHeight}
        />
      )}

      <AdjustmentsModal
        kind={activeAdjustmentKind}
        sessionId={adjustmentSessionId}
        targetCanvases={adjustmentTargetCanvases}
        onPreviewTick={handleAdjustmentPreviewTick}
        onCommit={handleAdjustmentCommit}
        onCreateAdjustmentLayer={handleCreateAdjustmentLayer}
        editingAdjustment={editingAdjustment ?? undefined}
        onLiveEditValue={handleAdjustmentLiveEdit}
        onClose={handleAdjustmentClose}
      />

      <ColorRangeDialog
        open={colorRangeOpen}
        source={sourceCanvas}
        onClose={() => setColorRangeOpen(false)}
        onApply={(maskCanvas) => {
          // Match the AI Select Subject UX — install via
          // `setSelectionFromMask`, ensure the marquee panel
          // reflects the new wand-style shape, and drop the user
          // into the marquee tool so the modify-selection rows
          // become reachable.
          const installed = canvas.setSelectionFromMask(maskCanvas);
          if (installed) {
            if (state.marqueeSettings.shape !== 'wand') {
              updateMarqueeSettings({ shape: 'wand' });
            }
            if (state.activeTool !== 'marquee') {
              setActiveTool('marquee');
            }
          }
        }}
      />

      {refineEdgeOpen && sourceCanvas && (
        <RefineEdgeWorkspace
          source={sourceCanvas}
          initialMaskCanvas={canvas.getSelectionAsMaskCanvas()}
          onClose={() => setRefineEdgeOpen(false)}
          onApply={(maskCanvas, outputTo) => {
            if (outputTo === 'selection') {
              canvas.setSelectionFromMask(maskCanvas);
              if (state.marqueeSettings.shape !== 'wand') {
                updateMarqueeSettings({ shape: 'wand' });
              }
            }
            // Other output modes (Mask / New Layer / New Layer With
            // Mask) are listed in the roadmap but ship in a follow-
            // up — the layer-mask wiring needs richer state plumbing
            // than this PR adds, and a single-output v1 is honest
            // about scope. The dropdown is rendered disabled for
            // those options.
            setRefineEdgeOpen(false);
          }}
        />
      )}

      {liquifyOpen && liquifySource && (
        <LiquifyWorkspace
          source={liquifySource}
          onClose={handleCloseLiquify}
          onApply={(deformedCanvas) => {
            void handleApplyLiquify(deformedCanvas);
          }}
        />
      )}

      {vectorizeSaveDialogOpen && (
        <VectorizeSaveDialog
          defaultFileName={(state.fileName || 'image').replace(/\.[^.]+$/, '')}
          defaultDestination={vectorizeSaveDialogDestination}
          onClose={handleVectorizeSaveDialogClose}
          onSave={handleVectorizeSave}
        />
      )}

      {confirmCloseTabId !== null && (
        <ConfirmCloseDialog
          fileName={tabs.tabs.find((t) => t.id === confirmCloseTabId)?.fileName ?? ''}
          isSaving={isQuickSaving}
          saveError={saveError}
          onCancel={handleConfirmCancel}
          onDiscard={handleConfirmDiscard}
          onSave={handleConfirmSave}
          onSaveAs={handleConfirmSaveAs}
        />
      )}

      {pendingWarpMode !== null &&
        (() => {
          const layerId = state.transformLayerId ?? state.activeLayerId;
          const node = layerId ? state.document.layers[layerId] : null;
          if (!node || (node.type !== 'vector' && node.type !== 'shape' && node.type !== 'text')) {
            // Defensive: should not happen — handleSetTransformMode
            // only opens the dialog when the active layer matches
            // one of these. If it does (e.g. the layer was deleted
            // between chip click and render), don't render.
            return null;
          }
          return (
            <RasterizeBeforeWarpDialog
              layerName={node.name}
              sourceType={node.type}
              mode={pendingWarpMode}
              onCancel={handleCancelRasterizeBeforeWarp}
              onConfirm={handleConfirmRasterizeBeforeWarp}
            />
          );
        })()}

      {helpDialogOpen && <IconLegendDialog onClose={() => setHelpDialogOpen(false)} />}

      <FiasImagePickerDialog
        open={fiasPickerOpen}
        onOpenChange={setFiasPickerOpen}
        onImageSelected={handleFiasImageSelected}
      />

      <AIToolPickerDialog
        open={state.aiPickerOpen}
        onOpenChange={setAIPickerOpen}
        onSelect={setSelectedAITool}
      />

      {/* Hidden file input for toolbar open */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) loadFile(file);
          e.target.value = '';
        }}
      />

      {/* Loading overlay — full-screen, blocking, shown while a long-
          running async op (export, AI inference, AI background removal)
          is in flight so the user always knows something is happening. */}
      {(isExporting ||
        isRemovingBackground ||
        aiStatus.kind === 'loading-model' ||
        aiStatus.kind === 'running') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-background rounded-lg p-6 shadow-xl">
            <p className="text-sm">
              {isRemovingBackground
                ? 'Removing background…'
                : aiStatus.kind === 'loading-model'
                  ? `Loading AI model${
                      typeof aiStatus.progressPct === 'number' ? ` ${aiStatus.progressPct}%` : '…'
                    }`
                  : aiStatus.kind === 'running'
                    ? aiStatus.label
                    : 'Exporting...'}
            </p>
          </div>
        </div>
      )}

      {removeBackgroundError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-destructive text-destructive-foreground rounded-md px-4 py-2 shadow-lg flex items-center gap-3">
          <span className="text-sm">{removeBackgroundError}</span>
          <button
            type="button"
            className="text-xs underline"
            onClick={() => setRemoveBackgroundError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
